import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, EXPLORER_API_BASE_URL, EXPLORER_CHAT_BASE_URL, WS_URL } from '../config/api';
import { chatGameApi } from '../components/ChatGame/chatGameApi';
import FloatingSvgBackground from '../components/theme/FloatingSvgBackground';
import SakuraPetals from '../components/theme/SakuraPetals';
import ShareButton from '../components/ShareButton';
import ClaudeExplorerRailCard from '../components/ClaudeExplorerRailCard';
import SpaceSwipeDeck from '../components/SpaceSwipeDeck';
import LivePreviewStrip from '../components/LivePreviewStrip';
import WhatsNewPill from '../components/WhatsNewPill';
import WorldTicker from '../components/WorldTicker';
import ExploreChat from '../components/explore/ExploreChat';
import { decideLook, discoverDeal } from '../utils/exploreStart';
import ExplorerTamagotchi from '../components/ExplorerTamagotchi';
import TillJar from '../components/ChatGame/TillJar';
import isLowResourceDevice from '../utils/lowResourceDevice';
import soundManager from '../utils/SoundManager';
import { placeOn } from '../utils/portedPlace';
import { porchFor, porchDaysAlive } from '../utils/portedHomes';
import { prefersDark, chooseTheme } from '../utils/themeChoice';
// NOTE: ClaudeThoughtsModal exists in src/components/ but is intentionally not
// wired up — clicking the 💭 button on the explorer tile opens the raw thoughts
// log in a new tab instead. Bring it back if you want a parsed in-page modal.

// Sentinel id used to identify the pinned Claude the Explorer tile in the
// Live and Trending rail. See LiveNowRail render branch below.
const EXPLORER_SENTINEL_ID = '__claude_explorer__';


// ─── REVERT: Featured Competition Banner toggle ───────────────────────────────
// Set to `true` to bring back the weekly competition banner on /explore.
// When false, the banner JSX is skipped AND both supporting effects (the
// /getFeaturedCompetition fetch and the 1s countdown interval) short-circuit,
// so there's zero runtime cost. Search this file for SHOW_FEATURED_BANNER to
// see all three gated sites.
// ──────────────────────────────────────────────────────────────────────────────
const SHOW_FEATURED_BANNER = false;
// Featured game rides the Live rail as its own pinned card instead (user call
// 07-10) — this flag re-enables the /getFeaturedCompetition fetch the banner
// used to own, without resurrecting the banner or its countdown interval.
const SHOW_FEATURED_RAIL_CARD = true;
// 🛫 Expedition strip: MOVED OFF /explore PERMANENTLY (Nina 08-15, quoted: "we should
// disable it permanently by moving it to one of the chibi passport page" + "so that it
// reappears when we fix that up"). It now lives in AgentImportPage.js gated on
// `source === 'passport'`, which SHOW_CHIBI_PASSPORT_DOOR holds shut — so it returns by
// itself when the passport funnel does, with no flag of its own to remember.
//
// ⛔ Do NOT re-add it here. Re-adding needs a NEW quoted request from Nina.
// There is deliberately no EXPLORE_EXPEDITION_STRIP const any more: as a one-word
// boolean it was flipped FOUR times in ~40 hours (6996140e3 off → b0fbd511c on →
// 7d71b4cad off → da6b4fcb1 on), because every "off" comment also documented its own
// undo. A flag that exists is a flag that gets flipped. `npm test` asserts this file
// greps to ZERO for the strip — see ExploreSpacesPage.expeditionStrip.test.js.

// ─── 🕵️ Whodunnit rail treatment ──────────────────────────────────────────────
// The card sells the CRIME, not the rules (user call 07-13): a rotating pulp
// headline in a noir face replaces the objective copy on Whodunnit cards.
// Headlines echo the template's case_facts (rain, blackout, the dog) so the
// card and the game speak the same night. Rotates hourly so the rail stays
// fresh without any per-render flicker.
// ──────────────────────────────────────────────────────────────────────────────
const WHODUNNIT_HEADLINES = [
  'MURDER AT THE MIDNIGHT HOUR!',
  'A BODY IN THE STAIRWELL!',
  'BLACKOUT AT 3AM. ONE OF THEM DID IT.',
  'EIGHT ALIBIS. ONE LIAR.',
  'THE DOG KNEW FIRST!',
  'RAIN, A LOCKED ROOM, AND A KILLER LOOSE!',
];
const isWhodunnitCard = (name) => /whodunnit/i.test(name || '');
const whodunnitHeadline = () =>
  WHODUNNIT_HEADLINES[Math.floor(Date.now() / 3600000) % WHODUNNIT_HEADLINES.length];
const NOIR_HEADLINE_STYLE = {
  fontFamily: "Georgia, 'Palatino Linotype', 'Times New Roman', serif",
  fontWeight: 900,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  lineHeight: 1.25,
};
// Pre-generated noir crime-scene art (07-14): empty interrogation room, swinging
// bulb, rain through blinds — style-locked to the Whodunnit template's
// recap_style pulp noir so rail + in-game panels read as one comic. Generated
// once via NanoBananaService (scratchpad gen-noir-rail-art.js), hosted on the
// public content bucket. Completes the round-4 noir rail card: headline + art.
const NOIR_RAIL_ART = 'https://spaceexplorer-content-files.s3.us-east-1.amazonaws.com/profile-images/agent-avatar-1784087414599-k7s4du.png';

// ─── REVERT: standalone Explorer Tamagotchi egg toggle ────────────────────────
// The pet's Tamagotchi UI now lives INSIDE the explorer rail tile (see
// ClaudeExplorerRailCard.js — hearts, DAY chip, care buttons on the offline
// strip). This standalone egg widget (ExplorerTamagotchi.js) was its first
// home; kept behind this gate in case the egg is wanted elsewhere later
// (it talks to the same /explorer/pet endpoints).
// ──────────────────────────────────────────────────────────────────────────────
const SHOW_EXPLORER_TAMAGOTCHI = false;

// ─── Sakura season toggle ─────────────────────────────────────────────────────
// Set to `true` during cherry-blossom season to render falling petals on
// /explore. The component (SakuraPetals.js) is self-gating on
// prefers-reduced-motion and adapts count/size for mobile.
//
// Petals are intentionally rendered as a SIBLING of the dark-mode wrapper
// (not a child) so the wrapper's CSS filter doesn't re-rasterize them every
// animation frame in dark mode. They use z-index 9999 to layer above page
// content (pointer-events: none, so clicks pass through).
//
// ─── HOW TO REVERT (search this file for "SAKURA" to find all three sites) ──
// 1) Set SHOW_SAKURA_PETALS = false below — that disables the feature.
// 2) Remove the `false &&` test override on the FloatingSvgBackground line
//    (search for "SAKURA PREVIEW") to restore the original floating emojis.
// 3) Optional full removal: delete the import at the top of this file, this
//    flag block, the <SakuraPetals /> render call (search "SAKURA RENDER"),
//    and the file src/components/theme/SakuraPetals.js.
// ──────────────────────────────────────────────────────────────────────────────
const SHOW_SAKURA_PETALS = false;

// Browse-section dividers. Each space is matched against every category, so a
// space can appear in multiple groups (intentional — helps discovery), but the
// three FEATURED cards per rail dedupe against earlier rails (08-03, Nina:
// identical top-3s rail after rail read as a bug, not discovery).
// "Recently Added" is a date filter; the final "More to explore" group is a
// safety-net for spaces that don't match any of the above.
// Editorial rail order (Nina 08-02 v3): Storytelling FIRST, then Just Chat &
// Vibes, then Entertainment > QT3.14 > Games > Learning > Self; Recently
// Added stays last (duplicates the new batch). Sparse rails (<3 spaces)
// still auto-demote below well-stocked ones.
const SPACE_CATEGORIES = [
  {
    key: 'storytelling',
    label: 'Storytelling & Roleplay',
    match: (s) => (s.interests || []).some(i => i === 'Storytelling/D&D' || i === 'Fantasy/Fiction'),
  },
  {
    key: 'chat',
    label: 'Just Chat & Vibes',
    match: (s) => (s.interests || []).some(i => i === 'Conversational Nook' || i === 'ISSAVIBE' || i === 'Silly/Random' || i === 'qt3.14s'),
  },
  {
    key: 'art',
    label: 'Entertainment & Media',
    // 'Entertainment/Media' is a legacy tag — kept here as a backwards-compat
    // match so spaces tagged before the Digital Content/Film/TV Shows split
    // still appear in this section. New spaces use the more specific tags.
    match: (s) => (s.interests || []).some(i => i === 'Music' || i === 'Art' || i === 'Digital Content' || i === 'Film' || i === 'TV Shows' || i === 'Gaming' || i === 'Entertainment/Media'),
  },
  {
    key: 'qt314',
    label: 'Certified QT3.14',
    match: (s) => (s.interests || []).some(i => i === 'qt3.14s'),
  },
  {
    key: 'games',
    label: 'Games & Competition',
    match: (s) => !!(s.competitive_mode || s.imposter_mode),
  },
  {
    key: 'learning',
    label: 'Learning & Productivity',
    // 08-02 (Nina): Science/Technology + News/Politics were feeding this rail
    // the whole comedy catalog (KitKat Heist, Roomba Union, Fridge
    // Interrogation... all news/tech-tagged bits). Those tags are premise
    // flavor, not category. Learning now = Language + Self Improvement, which
    // is what actually reads as learning/productivity (MOTIVATION 24/7 et al).
    match: (s) => (s.interests || []).some(i => i === 'Language' || i === 'Self Improvement'),
  },
  {
    key: 'self',
    label: 'Self',
    // 08-02 (Nina): Holiday/Culture dropped — it pulled themed-fun spaces
    // (Escape to Disney et al) into a rail meant for self-improvement.
    match: (s) => (s.interests || []).some(i => i === 'Self Improvement' || i === 'Relationships' || i === 'qt3.14s'),
  },
  {
    key: 'recent',
    label: 'Recently Added',
    match: (s) => {
      const ts = s.shared_at || s.created_at;
      if (!ts) return false;
      const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;
      return new Date(ts).getTime() > fourteenDaysAgo;
    },
  },
];

// Curated quick-suggestion chips that appear under the search bar on focus.
// Kept short (≤8) so they don't wrap to multiple rows on desktop.
const SEARCH_SUGGESTION_CHIPS = [
  'Storytelling',
  'Relationships',
  'Language',
  'Music',
  'Vibes',
  'Fantasy',
  'Science',
  'Art',
];

// Mood chips — filter spaces by their CURRENT vibe. space_mood is computed by
// the round-analysis pass each chapter (see AnalysisService), so it tracks
// what's actually happening in the room; absent until a space completes a round.
const MOOD_META = [
  { key: 'dramatic', emoji: '🔥', label: 'Dramatic' },
  { key: 'unhinged', emoji: '😂', label: 'Unhinged' },
  { key: 'cozy', emoji: '🧘', label: 'Cozy' },
  { key: 'mysterious', emoji: '🕵️', label: 'Mysterious' },
  { key: 'romantic', emoji: '💞', label: 'Romantic' },
  { key: 'melancholy', emoji: '🌧️', label: 'Melancholy' },
  { key: 'hype', emoji: '🎉', label: 'Hype' },
  { key: 'wholesome', emoji: '🌻', label: 'Wholesome' },
];
const MOOD_BY_KEY = Object.fromEntries(MOOD_META.map(m => [m.key, m]));

// Helper functions for localStorage vote tracking
const VOTES_STORAGE_KEY = 'space_votes';

const getStoredVotes = () => {
  try {
    const stored = localStorage.getItem(VOTES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const storeVote = (spaceId, voteType) => {
  try {
    const votes = getStoredVotes();
    votes[spaceId] = voteType; // 'like' or 'dislike'
    localStorage.setItem(VOTES_STORAGE_KEY, JSON.stringify(votes));
  } catch (err) {
    console.error('Failed to store vote:', err);
  }
};

const getUserVote = (spaceId) => {
  const votes = getStoredVotes();
  return votes[spaceId] || null; // Returns 'like', 'dislike', or null
};

// 🃏 Fresh-visitor space deck (Nina 08-14): a phone landing on /explore for the
// first time meets THREE spaces, one per category, as swipe cards. Attention
// grab first, data source second — but the log starts on day one so the future
// recommender has history.
// 09-29 (Nina's /explore batch, spec §6 #6): it no longer opens by itself.
// "Discover" in look C's phone sticky bar opens it, as often as asked, dealt
// from C's own rooms (utils/exploreStart discoverDeal). The seen key is still
// stamped when it shows, so a return of the first-visit deck knows who met it.
const SPACE_DECK_SEEN_KEY = 'exploreSpaceDeckSeen';
const SPACE_DECK_LOG_KEY = 'exploreSpaceDeckLog';

const ExploreSpacesPage = () => {
  const navigate = useNavigate();
  // 🖱️ "Desktop + narrow desktop, but not phones" — the gate the WORLD TICKER
  // wants (Nina 08-14: desktop chrome she does not want on a phone). Pointer
  // type, NOT width: a width check would also hide it on a narrow desktop
  // window, which is exactly the case she asked to keep. Read once — a
  // pointing device does not change under a window resize, so no listener.
  // Fails OPEN (a browser without matchMedia is far more likely to be a
  // desktop than a phone).
  // ⚠️ This used to be shared with the expedition strip, which moved off this
  // page on 08-15 (see the tombstone at the top of the file). It is NOT dead
  // code — `<WorldTicker />` below is still gated on it. Do not remove it while
  // clearing up after the strip.
  const finePointerDevice = useMemo(() => {
    try { return window.matchMedia('(hover: hover) and (pointer: fine)').matches; }
    catch (e) { return true; }
  }, []);
  // 🃏 Space deck: opened on demand (09-29: Discover), never at mount any more
  // (it used to open for a phone that had never seen it). `deckDeal` counts
  // the opens, so each Discover deals afresh.
  const [spaceDeckOpen, setSpaceDeckOpen] = useState(false);
  const [deckDeal, setDeckDeal] = useState(0);
  const [deckToast, setDeckToast] = useState(null); // { text, at }: "Saved to Loved" over the deck
  const openDiscover = () => {
    setDeckDeal(n => n + 1);
    setSpaceDeckOpen(true);
  };
  const closeSpaceDeck = () => {
    setSpaceDeckOpen(false);
    try { localStorage.setItem(SPACE_DECK_SEEN_KEY, '1'); } catch (e) { /* private mode */ }
  };
  const [spaces, setSpaces] = useState([]);
  const [filteredSpaces, setFilteredSpaces] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState('most-watched');
  const [selectedMood, setSelectedMood] = useState(null); // current-vibe filter (space_mood)
  const [spaceOfWeek, setSpaceOfWeek] = useState(null); // weekly featured space {game_instance_id, reason, ...}
  // Cycle 7: in bot mode (?bot=1 or sessionStorage-pinned directTileNav),
  // default ALL categories expanded so every space card is in the DOM from
  // the start. Without this, only the top 3 cards per category render
  // (collapsed sections are React-state-gated, not just CSS-hidden), so
  // navigate_to_space anchor enumeration and find_by_text both miss the
  // bulk of the catalog. Humans see the page exactly as before. Parallels
  // the existing directTileNav pattern.
  const [expandedSections, setExpandedSections] = useState(() => {
    if (typeof window === 'undefined') return {};
    const isBot = new URLSearchParams(window.location.search).get('bot') === '1'
      || window.sessionStorage.getItem('directTileNav') === '1';
    return isBot ? { __BOT_EXPAND_ALL__: true } : {};
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userVotes, setUserVotes] = useState({}); // Track user's votes locally
  const [viewerCounts, setViewerCounts] = useState({}); // Live viewer counts per game
  const [totalViews, setTotalViews] = useState({}); // All-time visit counts per game
  const [liveGames, setLiveGames] = useState({}); // Which games are currently live (playing)
  const [liveStreams, setLiveStreams] = useState({}); // gameId -> [{id,name}] humans publishing webcam/screen (game_livestream WS; HTTP livestream field is the load-time seed)
  const [watchParties, setWatchParties] = useState({}); // gameId -> watch_party|null (game_watch_party lobby WS; space.watch_party is the load-time seed — null is a real "party ended" signal, distinct from no-update)
  const [goLiveToast, setGoLiveToast] = useState(null); // {gameId, streamer, ts} — fresh go-live seen on the lobby WS; click jumps into the space
  const knownLiveIdsRef = useRef({}); // gameId -> Set(streamer ids); the FIRST message per game seeds silently (already-live on page load is not news, and 45s heartbeats repeat the same ids)
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [featuredData, setFeaturedData] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [joiningCompetition, setJoiningCompetition] = useState(false);

  // ─── directTileNav (bot mode) — sticky via sessionStorage ─────────────────
  // Once ?bot=1 is detected, persist in sessionStorage so the bot's self-tile
  // stays hidden even after a Back navigation strips the URL param. Without
  // this, the bot could land on /explore mid-session without ?bot=1 and see
  // its own iframe → recursive hall-of-mirrors trap.
  const directTileNav = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const urlBot = new URLSearchParams(window.location.search).get('bot') === '1';
    const storedBot = window.sessionStorage.getItem('directTileNav') === '1';
    return urlBot || storedBot;
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || !directTileNav) return;
    try { window.sessionStorage.setItem('directTileNav', '1'); } catch (e) { /* private mode */ }
  }, [directTileNav]);

  // 🧭💬 "Start here" = look C · Group chat (Nina 09-26; A + B saved), desktop
  // and (09-27) phones, each behind its own switch. Decided ONCE at mount,
  // like the phone deck: a resize never swaps the page under someone (C lays
  // itself out for the screen). ?bot=1 always gets this page's grid. The
  // switches and the ?browse=chat preview live in utils/exploreStart.js.
  // "See every space" = ?view=all, which renders today's page unchanged.
  const location = useLocation();
  const [startLook] = useState(() => decideLook({
    search: typeof window === 'undefined' ? '' : window.location.search,
    storage: (() => { try { return window.sessionStorage; } catch (e) { return null; } })(),
    matchMedia: (q) => window.matchMedia(q),
    isBot: directTileNav,
  }));
  const viewAll = new URLSearchParams(location.search).get('view') === 'all';
  const chatLook = startLook === 'chat' && !viewAll;
  const openAll = ({ key = null, query = null } = {}) => {
    if (query != null) setSearchQuery(query);
    if (key) setExpandedSections(prev => ({ ...prev, [key]: true }));
    const p = new URLSearchParams(location.search);
    p.set('view', 'all');
    navigate({ pathname: location.pathname, search: `?${p.toString()}`, hash: key ? `#browse-${key}` : '#browse' });
  };
  const closeAll = () => {
    const p = new URLSearchParams(location.search);
    p.delete('view');
    const s = p.toString();
    navigate({ pathname: location.pathname, search: s ? `?${s}` : '' });
  };
  useEffect(() => {
    if (startLook === 'grid' || !viewAll) return undefined;
    const id = (location.hash || '#browse').slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: 'start' });
    }, 60);
    return () => clearTimeout(t);
  }, [startLook, viewAll, location.hash, isLoading]);

  // Go-live toast lingers 10s, then clears itself
  useEffect(() => {
    if (!goLiveToast) return;
    const t = setTimeout(() => setGoLiveToast(null), 10000);
    return () => clearTimeout(t);
  }, [goLiveToast]);

  // 🔬 ?layoutprobe=1 — on-device layout forensics (Nina's iPhone, 07-21):
  // "browse cards load too far right, fix as you scroll" reproduces ONLY on
  // real iOS Safari (headless Chrome + Playwright WebKit both render clean),
  // so this overlay reads the truth off the failing device itself: layout
  // vs visual viewport, media-query match, first card box, and — when the
  // page ever gets wider than the viewport — WHICH elements own the
  // overflow. Worst-seen values stick so a transient blip survives long
  // enough to screenshot. Param-gated, zero cost without it.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!new URLSearchParams(window.location.search).has('layoutprobe')) return undefined;
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;left:4px;bottom:4px;z-index:99999;background:rgba(0,0,0,0.85);color:#7CFC00;font:10px/1.45 monospace;padding:6px 8px;border-radius:6px;max-width:92vw;pointer-events:none;white-space:pre-wrap;';
    document.body.appendChild(el);
    const worst = { docW: 0, offenders: [], cardL: 0, badCards: 0, maxCardW: 0 };
    const tick = () => {
      const vw = window.innerWidth;
      const vv = window.visualViewport;
      const docW = Math.max(document.documentElement.scrollWidth, document.body ? document.body.scrollWidth : 0);
      // Only scan when the PAGE truly overflows — elements inside their own
      // horizontal scrollers (chip rows) stick out past vw legitimately and
      // would be pure noise here.
      if (docW > worst.docW && docW > vw + 8) {
        worst.docW = docW;
        const off = [];
        const all = document.querySelectorAll('body *');
        for (let i = 0; i < all.length && off.length < 5; i++) {
          const n = all[i];
          const r = n.getBoundingClientRect();
          if (r.right - vw > 8 && r.width > 0) {
            off.push(`${n.tagName}.${(typeof n.className === 'string' ? n.className : '').slice(0, 20)} R${Math.round(r.right)}`);
          }
        }
        worst.offenders = off;
      } else if (docW > worst.docW) {
        worst.docW = docW;
      }
      const card = document.querySelector('.space-card-outer');
      const cr = card ? card.getBoundingClientRect() : null;
      if (cr && cr.left > worst.cardL) worst.cardL = Math.round(cr.left);
      // v2 (08-02): scan EVERY card, not just the first. The 08-02 capture
      // showed the bug lives on whichever cards are currently skip-sized
      // (card1 read W=359 while the pictured card was fine) — and the
      // offender scan above can never fire because the container's
      // overflowX:clip stops the DOCUMENT from ever overflowing. "bad" =
      // a card box poking past either viewport edge; worst-seen sticks.
      const cards = document.querySelectorAll('.space-card-outer');
      let bad = 0; let maxW = 0;
      for (let i = 0; i < cards.length; i++) {
        const r = cards[i].getBoundingClientRect();
        if (r.width > maxW) maxW = Math.round(r.width);
        if (r.width > 0 && (r.right > vw + 2 || r.left < -2)) bad++;
      }
      if (bad > worst.badCards) worst.badCards = bad;
      if (maxW > worst.maxCardW) worst.maxCardW = maxW;
      el.textContent =
        `iw=${vw} dw=${docW} sx=${Math.round(window.scrollX)} mq699=${window.matchMedia('(max-width: 699px)').matches}\n` +
        `vv=${vv ? `${Math.round(vv.width)} ox=${Math.round(vv.offsetLeft)} s=${vv.scale.toFixed(2)}` : 'n/a'}\n` +
        `card1 L=${cr ? Math.round(cr.left) : '-'} W=${cr ? Math.round(cr.width) : '-'} worstL=${worst.cardL}\n` +
        `cards=${cards.length} bad=${bad}/${worst.badCards} maxW=${maxW}/${worst.maxCardW}\n` +
        `worstDW=${worst.docW}${worst.offenders.length ? '\n' + worst.offenders.join('\n') : ''}`;
    };
    const iv = setInterval(tick, 500);
    tick();
    return () => { clearInterval(iv); el.remove(); };
  }, []);

  // ─── Claude the Explorer status ────────────────────────────────────────────
  // Polls the public status endpoint every 30s. Drives the pinned tile in the
  // Live and Trending rail (live stream / countdown / loading states).
  const [explorerStatus, setExplorerStatus] = useState(null);
  // 3 straight failed polls with no status ever received → the hero card
  // stops saying "Loading…" forever and shows a designed idle line instead
  // (the explorer stack is its own service; it being down must not read as
  // the whole site being broken).
  const [explorerUnreachable, setExplorerUnreachable] = useState(false);
  const explorerFailsRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const fetchStatus = async () => {
      let json = null;
      try {
        // Explorer lives in its own Serverless service (separate stack from main backend).
        // See EXPLORER_API_BASE_URL in config/api.js.
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/status`);
        if (res.ok) {
          json = await res.json();
          if (!cancelled) setExplorerStatus(json);
        }
      } catch (err) {
        // Swallow — tile falls back to "Loading…" / next-live placeholder.
      }
      if (cancelled) return;
      if (json) {
        explorerFailsRef.current = 0;
        setExplorerUnreachable(false);
      } else {
        explorerFailsRef.current += 1;
        if (explorerFailsRef.current >= 3) setExplorerUnreachable(true);
      }
      // Adaptive cadence (user 07-12: the WATCH LIVE button appeared too
      // late). Flat 30s meant up to half a minute between a session starting
      // and the rail knowing. 10s base; 3s while a session is spinning up
      // (provisioning/starting) or a scheduled go-live is <2 min away — the
      // exact windows where seconds matter for catching the curtain show.
      const status = json && json.status;
      const spinningUp = status === 'provisioning' || status === 'starting';
      const nextAt = json && Number(json.next_live_at);
      const imminent = Number.isFinite(nextAt) && nextAt - Date.now() > 0 && nextAt - Date.now() < 2 * 60 * 1000;
      timer = setTimeout(fetchStatus, (spinningUp || imminent) ? 3000 : 10000);
    };
    fetchStatus();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // ─── Dark mode toggle (opt-in, persisted) ──────────────────────────────────
  // Purely additive: when off, the page behaves exactly as before.
  // When on, an outer wrapper applies CSS filter:invert + hue-rotate so the
  // entire page renders in dark mode without touching any existing styles.
  // Images, videos, and iframes are counter-inverted so they stay correct.
  // DEFAULT DARK everywhere, desktop and mobile (Nina 08-14 launch ruling;
  // supersedes both the 07-19 light-phone guard and the earlier OS-preference
  // honor). ⚠️ The invert wrapper forces full-page offscreen GPU compositing —
  // iOS tab-memory crash territory alongside the LiveKit rail + explorer
  // iframe — so watch for silent tab reloads on iOS. The manual toggle is the
  // escape hatch. 🌙 09-30 (Nina: "by default users are in dark mode"): only
  // a press of the switch saves a choice (utils/themeChoice); the old key was
  // saved on every visit, so devices from the OS-preference days sat in light
  // without ever choosing it.
  const [darkMode, setDarkMode] = useState(() => prefersDark());
  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    chooseTheme(next);
  };

  const wsRef = useRef(null);
  const wsBackoffRef = useRef(3000);
  const wsReconnectTimeoutRef = useRef(null);
  // Load stored votes and cached SVGs on mount
  useEffect(() => {
    setUserVotes(getStoredVotes());
    // Seed floating emojis from localStorage (newest entries last in object, so reverse)
    try {
      const stored = JSON.parse(localStorage.getItem('space_emojis') || '{}');
      const seen = new Set();
      const ordered = [];
      // Reverse so newest-added entries come first
      for (const emojis of Object.values(stored).reverse()) {
        for (const e of (Array.isArray(emojis) ? emojis : [])) {
          if (typeof e === 'string' && !seen.has(e)) { seen.add(e); ordered.push(e); }
        }
      }
      if (ordered.length > 0) setFloatingEmojis(ordered);
    } catch (e) { /* ignore */ }
  }, []);

  // Fetch featured competition data on mount (banner OR rail card needs it)
  useEffect(() => {
    if (!SHOW_FEATURED_BANNER && !SHOW_FEATURED_RAIL_CARD) return;
    const fetchFeatured = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api?getFeaturedCompetition=true`);
        const data = await response.json();
        let result = data;
        if (data.body) {
          result = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        }
        setFeaturedData(result);
      } catch (err) {
        console.error('Failed to fetch featured competition:', err);
      }
    };
    fetchFeatured();
  }, []);

  // Countdown timer for weekly competition
  useEffect(() => {
    if (!SHOW_FEATURED_BANNER) return; // REVERT: gated by SHOW_FEATURED_BANNER
    const getWeekEnd = () => {
      const now = new Date();
      const dayOfWeek = now.getUTCDay(); // 0=Sun, 1=Mon
      const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
      return new Date(Date.UTC(
        now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysUntilMonday,
        0, 0, 0, 0
      ));
    };

    const updateCountdown = () => {
      const diff = getWeekEnd() - new Date();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch shared spaces on mount
  useEffect(() => {
    fetchSharedSpaces();
  }, []);

  // WebSocket connection for live viewer counts
  useEffect(() => {
    let isCancelled = false;

    const connect = () => {
      if (isCancelled) return;

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      let heartbeatInterval = null;

      ws.onopen = () => {
        console.log('[WS] Connected to lobby');
        wsBackoffRef.current = 3000;
        ws.send(JSON.stringify({ action: 'joinLobby' }));
        heartbeatInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'heartbeat' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'lobby_subscribed') {
            if (msg.viewer_counts) setViewerCounts(msg.viewer_counts);
            if (msg.total_views) setTotalViews(prev => ({ ...prev, ...msg.total_views }));
            if (msg.live_games) {
              const liveMap = {};
              msg.live_games.forEach(id => { liveMap[id] = true; });
              setLiveGames(liveMap);
            }
          }
          if (msg.type === 'viewer_count') {
            setViewerCounts(prev => ({
              ...prev,
              [msg.gameInstanceId]: msg.count
            }));
            if (msg.total_views !== undefined) {
              setTotalViews(prev => ({
                ...prev,
                [msg.gameInstanceId]: msg.total_views
              }));
            }
          }
          if (msg.type === 'game_live') {
            setLiveGames(prev => ({
              ...prev,
              [msg.gameInstanceId]: msg.is_live
            }));
          }
          // Human webcam/screen streams (announceLivestream lobby broadcast)
          if (msg.type === 'game_livestream') {
            const streamers = msg.streamers || [];
            const known = knownLiveIdsRef.current[msg.gameInstanceId];
            const fresh = known ? streamers.filter(s => s?.id && !known.has(s.id)) : [];
            knownLiveIdsRef.current[msg.gameInstanceId] = new Set(streamers.map(s => s?.id).filter(Boolean));
            if (known && fresh.length > 0) {
              setGoLiveToast({ gameId: msg.gameInstanceId, streamer: fresh[0].name || 'Someone', ts: Date.now() });
            }
            setLiveStreams(prev => ({
              ...prev,
              [msg.gameInstanceId]: streamers
            }));
          }
          // 🍿 Watch party started/advanced/ended (chatGameApi lobby broadcast) —
          // flips the rail's "Watching:" pill live; load only seeds it
          if (msg.type === 'game_watch_party') {
            setWatchParties(prev => ({
              ...prev,
              [msg.gameInstanceId]: msg.watch_party || null
            }));
          }
          if (msg.type === 'new_space' && msg.space && !msg.space.is_featured_competition) {
            setSpaces(prev => {
              // Avoid duplicates
              if (prev.some(s => s.id === msg.space.id)) return prev;
              return [msg.space, ...prev];
            });
          }
        } catch (err) {
          // Ignore parse errors
        }
      };

      ws.onclose = () => {
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        if (isCancelled) return;
        console.log('[WS] Lobby disconnected, reconnecting in', wsBackoffRef.current / 1000, 's');
        wsReconnectTimeoutRef.current = setTimeout(() => {
          wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
          connect();
        }, wsBackoffRef.current);
      };

      ws.onerror = (err) => {
        console.error('[WS] Lobby error:', err);
        ws.close();
      };
    };

    connect();

    return () => {
      isCancelled = true;
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
        wsReconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // Filter and sort spaces when search/sort changes
  useEffect(() => {
    let result = [...spaces];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(space =>
        space.name?.toLowerCase().includes(query) ||
        space.description?.toLowerCase().includes(query) ||
        space.interests?.some(i => i.toLowerCase().includes(query)) ||
        space.ai_roles?.some(r => r.toLowerCase().includes(query))
      );
    }

    // Filter by current vibe (space_mood from the latest round analysis)
    if (selectedMood) {
      result = result.filter(space => space.space_mood === selectedMood);
    }

    // Sort
    switch (sortBy) {
      case 'a-z':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'z-a':
        result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'most-liked':
        result.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.shared_at || b.created_at) - new Date(a.shared_at || a.created_at));
        break;
      case 'most-watched':
        result.sort((a, b) => (viewerCounts[b.id] || 0) - (viewerCounts[a.id] || 0));
        break;
      case 'most-visited':
        result.sort((a, b) => (totalViews[b.id] || b.total_views || 0) - (totalViews[a.id] || a.total_views || 0));
        break;
      case 'games':
        result.sort((a, b) => {
          const aIsGame = (a.competitive_mode || a.imposter_mode) ? 1 : 0;
          const bIsGame = (b.competitive_mode || b.imposter_mode) ? 1 : 0;
          if (bIsGame !== aIsGame) return bIsGame - aIsGame;
          return (totalViews[b.id] || b.total_views || 0) - (totalViews[a.id] || a.total_views || 0);
        });
        break;
      default:
        break;
    }

    setFilteredSpaces(result);
  }, [spaces, searchQuery, selectedMood, sortBy, viewerCounts, totalViews]);

  // Group filtered spaces into category sections. A space can appear in
  // multiple categories. Empty categories are dropped, and anything that
  // didn't match a category falls into "More to explore" so nothing is
  // hidden from a browsing user. We bypass the grouping when a search
  // query is active — slicing search results by category isn't useful.
  const groupedSections = useMemo(() => {
    // Soft popularity sort: keep editorial order, but auto-demote sparse
    // sections (< 3 spaces) to the bottom so the lead of the page always
    // shows well-stocked rails. 3 mirrors the "top 3 then Show all" pattern.
    const SPARSE_THRESHOLD = 3;
    // Editorial pins lead their rail. 08-04 (Nina): Dorm + House moved to the
    // Live rail's bookend slots, so Just Chat & Vibes surfaces the evergreen
    // comedy batch instead (order = Nina's list; only the first 3 show before
    // "Show all"). Name-keyed on purpose — survives regeneration; a renamed/
    // unshared space just drops out of the pin.
    const PINNED_BY_CATEGORY = {
      // 🐉 AITA: Dragon Edition leads STORYTELLING (Nina 08-08) — MOVED here
      // from the chat rail below, where it was card 1. Storytelling is the
      // first rail on the page, so this makes it the first card on /explore.
      // ⭐ Pinning works even though the space is not tagged
      // Storytelling/D&D: the pin lookup below resolves names against
      // `filteredSpaces` (every space), not against `cat.match`, so a pin can
      // seat a space in a rail it would not otherwise qualify for.
      storytelling: [
        'AITA: Dragon Edition',
      ],
      chat: [
        '2016 Forever',
        'NPC Break Room',
        'Roomba Union Local 404',
        'Time Loop DMV',
        'Medieval IT Help Desk',
        'Pigeon Influencer House',
        'KitKat Heist HQ',
      ],
    };
    // Category-scoped removals (Nina 08-04): these read as off-brand for the
    // rail they matched into via tags. They stay members of every OTHER rail
    // they match (this is not an unshare).
    const EXCLUDED_BY_CATEGORY = {
      learning: ['Protecting our Peace', 'HeartBreakHotel'],
    };
    const populated = SPACE_CATEGORIES
      .map(cat => {
        let catSpaces = filteredSpaces.filter(cat.match);
        // Recently Added sorts by actual recency (08-03): it used to inherit
        // the page sort, which only LOOKED newest-first while every viewer
        // count was zero (stable-sort accident).
        if (cat.key === 'recent') {
          catSpaces = [...catSpaces].sort((a, b) =>
            new Date(b.shared_at || b.created_at).getTime() - new Date(a.shared_at || a.created_at).getTime());
        }
        const excludedNames = EXCLUDED_BY_CATEGORY[cat.key];
        if (excludedNames) {
          catSpaces = catSpaces.filter(s => !excludedNames.includes(s.name));
        }
        const pinnedNames = PINNED_BY_CATEGORY[cat.key];
        if (pinnedNames) {
          const pinned = pinnedNames
            .map(n => filteredSpaces.find(s => s.name === n))
            .filter(Boolean);
          catSpaces = [...pinned, ...catSpaces.filter(s => !pinnedNames.includes(s.name))];
        }
        return { ...cat, spaces: catSpaces, pinnedNames: pinnedNames || null };
      })
      .filter(cat => cat.spaces.length > 0);
    const lead = populated.filter(cat => cat.spaces.length >= SPARSE_THRESHOLD);
    const sparse = populated.filter(cat => cat.spaces.length < SPARSE_THRESHOLD);
    const sections = [...lead, ...sparse];

    // 🃏 Featured-slot variety (Nina 08-03): every rail filtered the SAME
    // newest-first list, so rails with overlapping tags opened with identical
    // top-3s (Storytelling ≡ Just Chat, Learning ≡ Self, Recently Added ≡ the
    // page head). A space KEEPS full membership everywhere it matches — "Show
    // all" is untouched — but the three featured slots prefer spaces no
    // earlier rail has already featured; repeats only fill a rail that has
    // nothing fresh left.
    const featuredIds = new Set();
    sections.forEach(cat => {
      // Editorial pins outrank the variety shuffle (08-04): a pinned card
      // that already got featured in an earlier rail must NOT get demoted
      // behind "fresh" ones — the pin order IS the curation.
      const pinnedSet = new Set(cat.pinnedNames || []);
      const front = cat.spaces.filter(s => pinnedSet.has(s.name));
      const rest = cat.spaces.filter(s => !pinnedSet.has(s.name));
      const fresh = rest.filter(s => !featuredIds.has(s.id));
      const repeats = rest.filter(s => featuredIds.has(s.id));
      cat.spaces = [...front, ...fresh, ...repeats];
      cat.spaces.slice(0, SPARSE_THRESHOLD).forEach(s => featuredIds.add(s.id));
    });

    const seen = new Set();
    sections.forEach(cat => cat.spaces.forEach(s => seen.add(s.id)));
    const leftovers = filteredSpaces.filter(s => !seen.has(s.id));
    if (leftovers.length > 0) {
      sections.push({ key: 'more', label: 'More to explore', spaces: leftovers });
    }
    return sections;
  }, [filteredSpaces]);

  const fetchSharedSpaces = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api?listSharedSpaces=true`);
      const data = await response.json();

      // Handle Lambda response format
      let spacesData = data;
      if (data.body) {
        spacesData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      const spacesList = spacesData.spaces || [];
      setSpaces(spacesList);
      setSpaceOfWeek(spacesData.space_of_week || null);

      // Seed viewer counts, total views, and live status from HTTP response
      const counts = {};
      const views = {};
      const live = {};
      for (const s of spacesList) {
        if (s.viewer_count) counts[s.id] = s.viewer_count;
        if (s.total_views) views[s.id] = s.total_views;
        if (s.is_live) live[s.id] = true;
      }
      setViewerCounts(prev => ({ ...counts, ...prev }));
      setTotalViews(prev => ({ ...views, ...prev }));
      setLiveGames(prev => ({ ...live, ...prev }));

      // Merge API emojis with localStorage cache for floating background
      try {
        const stored = JSON.parse(localStorage.getItem('space_emojis') || '{}');
        for (const s of spacesList) {
          if (s.background_emojis && !stored[s.id]) {
            stored[s.id] = s.background_emojis;
          }
        }
        // Cap at 100 entries
        const keys = Object.keys(stored);
        if (keys.length > 100) {
          for (const k of keys.slice(0, keys.length - 100)) delete stored[k];
        }
        localStorage.setItem('space_emojis', JSON.stringify(stored));
        // Build newest-first deduplicated emoji list
        const seen = new Set();
        const ordered = [];
        for (const emojis of Object.values(stored).reverse()) {
          for (const e of (Array.isArray(emojis) ? emojis : [])) {
            if (typeof e === 'string' && !seen.has(e)) { seen.add(e); ordered.push(e); }
          }
        }
        setFloatingEmojis(ordered);
      } catch (e) { /* ignore */ }
    } catch (err) {
      console.error('Failed to fetch shared spaces:', err);
      setError('Failed to load shared spaces');
    } finally {
      setIsLoading(false);
    }
  };

  // 🎵 Instant walk-in soundtrack (Nina 08-01): seed the space's music into
  // GameStepPage's walk-in cache AT the launch click, and navigate IN-APP so
  // the click's audio permission survives — builder music starts on the first
  // frame of the loading screen, even on a first-ever visit. The old
  // window.location.href reload threw the gesture away (fresh page = muted or
  // blocked autoplay, and no cache until a second visit) — same failure class
  // as the whodunnit SPA-join rain fix (0b924f8b2).
  // `replace` (look C's phone thread): the room takes the thread's history
  // entry, so Back from the room lands on the list, not the preview.
  const launchSpace = (space, { replace = false } = {}) => {
    try {
      if (space?.music_url) localStorage.setItem(`spaceMusicUrl:${space.id}`, space.music_url);
    } catch (e) { /* private mode */ }
    navigate(`/game-step?gameId=${encodeURIComponent(space.id)}`, replace ? { replace: true } : undefined);
  };

  const handleRandomSpace = () => {
    if (filteredSpaces.length > 0) {
      const randomIndex = Math.floor(Math.random() * filteredSpaces.length);
      launchSpace(filteredSpaces[randomIndex]);
    }
  };

  const handleJoinSpace = (space) => {
    // Join at current state — SPA nav + music seed via launchSpace
    launchSpace(space);
  };

  const handleJoinCompetition = async () => {
    if (joiningCompetition || !featuredData?.featured) return;
    setJoiningCompetition(true);
    try {
      const template = featuredData.featured;

      // 🌧️ Whodunnit: rain from the Play-now click itself (Nina 07-25). The
      // click is a gesture, so the bed is autoplay-legal and it covers the
      // "Setting up..." wait. The join must SPA-navigate (see below) so this
      // bed survives into the game page unbroken — a full reload kills the
      // AudioContext and leaves silence until the next gesture. The scenario
      // pack rolls server-side at creation, so this teaser is always the
      // classic rain.
      if (template.guilty_role) soundManager.startRain();

      // Generate or retrieve a persistent player identifier for leaderboard tracking
      let playerId = localStorage.getItem('competition_player_id');
      if (!playerId) {
        playerId = 'player_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
        localStorage.setItem('competition_player_id', playerId);
      }

      // Build player configs from template AI models
      const MODEL_MAP = {
        'Gemini 3 Flash': 'google/gemini-3-flash-preview',
        'Gemini 2.5 Pro': 'google/gemini-2.5-pro-preview',
        'ChatGPT (GPT-5.1)': 'openai/gpt-5.1-chat',
        // 07-19: was MISSING here (present in backend modelRoster) — four
        // templates list this seat and it silently dropped at creation,
        // shrinking whodunnit tables by one suspect.
        'ChatGPT (GPT-5 Mini)': 'openai/gpt-5-mini',
        'Claude Haiku 4.5': 'anthropic/claude-haiku-4.5',
        'Llama 3.3 Nemotron': 'nvidia/llama-3.3-nemotron-super-49b-v1.5',
        'DeepSeek v3.2': 'deepseek/deepseek-v3.2',
        'Qwen3 Max': 'qwen/qwen3-max-thinking',
        'Kimi K2': 'moonshotai/kimi-k2-0905'
      };

      const players = [];
      for (const [modelName, count] of Object.entries(template.ai_models || {})) {
        const modelId = MODEL_MAP[modelName];
        if (!modelId) continue;
        for (let i = 0; i < count; i++) {
          players.push({
            name: count > 1 ? `${modelName} ${i + 1}` : modelName,
            character_description: `An AI competitor in ${template.name}`,
            personality_traits: [],
            objective: template.objective,
            secret_info: '',
            is_human: false,
            model: modelId
          });
        }
      }

      // Add human player — use a persistent display name so leaderboard shows a real name
      const humanName = (() => {
        const stored = localStorage.getItem('competition_player_name');
        if (stored) return stored;
        const adjectives = ['Swift', 'Bold', 'Clever', 'Lucky', 'Witty', 'Sharp', 'Brave', 'Keen', 'Sly', 'Chill'];
        const nouns = ['Fox', 'Hawk', 'Wolf', 'Lynx', 'Raven', 'Tiger', 'Panda', 'Otter', 'Falcon', 'Bear'];
        const name = `${adjectives[Math.floor(Math.random() * adjectives.length)]}${nouns[Math.floor(Math.random() * nouns.length)]}`;
        localStorage.setItem('competition_player_name', name);
        return name;
      })();
      players.push({
        name: humanName,
        character_description: 'A human competitor',
        personality_traits: [],
        objective: template.objective,
        secret_info: '',
        is_human: true,
        model: null
      });

      const isImposter = !!template.imposter_mode;

      const config = {
        name: `${template.name} - Competition`,
        objective: template.objective,
        scene_description: template.scene_description || template.description,
        competitive_mode: true,
        imposter_mode: isImposter,
        ...(isImposter
          ? { imposter_word_rounds: template.imposter_word_rounds || 3 }
          : { total_rounds: template.total_rounds || 3, max_rounds: (template.total_rounds || 3) * 2 }),
        messages_per_round: template.messages_per_round || 4,
        is_shared: true,
        is_featured_competition: true,
        featured_week_key: template.weekKey,
        featured_template_index: template.templateIndex,
        human_player_identifier: playerId,
        interests: [isImposter ? 'imposter' : 'competitive', 'featured'],
        players,
        profileImagesEnabled: true,
        allowAiNameSelection: true,
        ...(template.music_url ? { music_url: template.music_url } : {}),
        ...(template.ai_roast_ammo ? { ai_roast_ammo: template.ai_roast_ammo } : {})
      };

      const result = await chatGameApi.createGameInstant(config);
      const gameId = result.game_id;

      // Store the human player ID so GameStepPage's ensureHumanPlayer doesn't create a duplicate
      if (result.human_player_id) {
        localStorage.setItem(`humanPlayerId_${gameId}`, result.human_player_id);
      }

      // 🌧️ SPA navigation, NOT window.location (Nina 07-30 "rain stops 1-2s
      // after play, then comes back"): a full reload tears down the
      // AudioContext, so the click-started rain bed above died at navigation
      // and stayed dead until the game page's next gesture (storm door /
      // Investigate) — a gap NO GameStepPage-side fix could close. Router
      // navigation keeps the context (and the bed) alive through mount;
      // AgentImportPage already enters /game-step this way with loading=true.
      navigate(`/game-step?gameId=${encodeURIComponent(gameId)}&loading=true`);
    } catch (err) {
      console.error('Failed to join competition:', err);
      alert('Failed to join competition. Please try again.');
    } finally {
      setJoiningCompetition(false);
    }
  };

  // 🃏 The cards (09-29: Discover): C's own rooms, never a ported room, junk,
  // a game or a room this device already voted on, the ones the deck showed
  // lately last; three, tiered strong / mid / under-exposed by visits and then
  // shuffled (utils/exploreStart discoverDeal holds the whole rule).
  // Built ONCE per open (deps are the deck flag, the deal count and a length,
  // not the array identity): the spaces array re-renders on every viewer-count
  // poll, and a rebuild would reshuffle the deck under the user's thumb.
  const spaceDeckCards = useMemo(() => {
    if (!spaceDeckOpen || !spaces.length) return [];
    let seen = null;
    try {
      const log = JSON.parse(localStorage.getItem(SPACE_DECK_LOG_KEY) || '[]');
      seen = new Set(log.filter(e => e && e.action === 'shown').map(e => e.id));
    } catch (e) { seen = null; }
    return discoverDeal(spaces, { categories: SPACE_CATEGORIES, votes: { ...getStoredVotes(), ...userVotes }, seen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceDeckOpen, spaces.length, deckDeal]);

  // Nothing left to deal (every room with art already loved): say so, never an
  // empty screen.
  useEffect(() => {
    if (!spaceDeckOpen || isLoading || spaceDeckCards.length) return;
    setSpaceDeckOpen(false);
    setDeckToast({ text: 'No new rooms to deal right now.', at: Date.now() });
  }, [spaceDeckOpen, isLoading, spaceDeckCards.length]);

  useEffect(() => {
    if (!deckToast) return undefined;
    const t = setTimeout(() => setDeckToast(null), 2200);
    return () => clearTimeout(t);
  }, [deckToast]);

  // Stamp "seen" the moment the deck actually shows a card, not when it is
  // dismissed: a reload or a back-swipe mid-deck would otherwise re-summon it
  // on the next visit, which is how a one-time delight becomes a recurring
  // toll gate.
  useEffect(() => {
    if (!spaceDeckOpen || spaceDeckCards.length === 0) return;
    try { localStorage.setItem(SPACE_DECK_SEEN_KEY, '1'); } catch (e) { /* private mode */ }
  }, [spaceDeckOpen, spaceDeckCards.length]);

  // Append-only local log. Nothing reads it yet — it exists so the recommender
  // Nina wants later has history from day one, and because position/tier
  // cannot be reconstructed after the fact. Capped so it can never bloat
  // localStorage.
  const logSpaceDeck = (entry) => {
    try {
      const prev = JSON.parse(localStorage.getItem(SPACE_DECK_LOG_KEY) || '[]');
      prev.push({ ...entry, at: Date.now() });
      localStorage.setItem(SPACE_DECK_LOG_KEY, JSON.stringify(prev.slice(-60)));
    } catch (e) { /* private mode — the deck still works */ }
  };

  const handleLike = async (spaceId, isLike) => {
    // Check if user already voted on this space
    const existingVote = userVotes[spaceId];
    if (existingVote) {
      // User already voted - don't allow another vote
      return;
    }

    const voteType = isLike ? 'like' : 'dislike';

    // Optimistically update UI
    setUserVotes(prev => ({ ...prev, [spaceId]: voteType }));
    storeVote(spaceId, voteType);

    // Update local count state
    setSpaces(prev => prev.map(space => {
      if (space.id === spaceId) {
        return {
          ...space,
          likes_count: isLike ? (space.likes_count || 0) + 1 : space.likes_count,
          dislikes_count: !isLike ? (space.dislikes_count || 0) + 1 : space.dislikes_count
        };
      }
      return space;
    }));

    try {
      await fetch(`${API_BASE_URL}/api?rateSpace=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spaceId,
          rating: voteType
        })
      });
    } catch (err) {
      console.error('Failed to rate space:', err);
      // Note: We don't revert since the vote is stored locally anyway
    }
  };

  return (
    <>
      {/* ─── Go-live toast: fresh lobby go-live → one-tap jump into the space.
          Auto-dismisses; hidden for bot nav (directTileNav). ─────────────── */}
      {goLiveToast && !directTileNav && (() => {
        const toastSpace = spaces.find(s => s.id === goLiveToast.gameId);
        return (
          <div
            key={goLiveToast.ts}
            onClick={() => { navigate(`/game-step?gameId=${encodeURIComponent(goLiveToast.gameId)}`); }}
            style={{
              position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10000, display: 'flex', alignItems: 'center', gap: 10,
              maxWidth: 'calc(100vw - 32px)', padding: '10px 16px', borderRadius: 999,
              background: 'rgba(17,17,24,0.95)', border: '1px solid rgba(239,68,68,0.6)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.4), 0 0 16px rgba(239,68,68,0.25)',
              cursor: 'pointer', color: '#fff',
              animation: 'goLiveToastIn 0.35s ease',
            }}
          >
            <style>{`
              @keyframes goLiveToastIn { from { opacity: 0; transform: translate(-50%, 12px); } to { opacity: 1; transform: translate(-50%, 0); } }
              @keyframes goLiveToastDot { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
            `}</style>
            <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0, animation: 'goLiveToastDot 1.4s ease-in-out infinite' }} />
            <span style={{ fontSize: '0.88em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {goLiveToast.streamer} is LIVE{toastSpace?.name ? ` in ${toastSpace.name}` : ''}! Tap to watch!
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setGoLiveToast(null); }}
              aria-label="Dismiss"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.9em', padding: '0 2px', flexShrink: 0, lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
        );
      })()}
      {/* ─── Dark mode toggle (additive, /explore only) ─────────────────────── */}
      {/* Sibling of the dark-mode wrapper so the button itself isn't inverted.
          Hidden while the swipe deck owns the screen (Nina 08-18): this sits
          top-left at z9999, ABOVE the deck overlay (z5200), and painted over
          the deck's ✕ on phones — same fix as the marketplace toggle's
          !deckOpen gate. The deck styles itself dark either way. */}
      {/* 🧭💬 look C owns the whole desktop screen: its own header (mode,
          marketplace, create), its own palettes, the ticker inside it. */}
      {chatLook && (
        <ExploreChat
          spaces={spaces}
          isLoading={isLoading}
          error={error}
          onRetry={fetchSharedSpaces}
          categories={SPACE_CATEGORIES}
          totalViews={totalViews}
          viewerCounts={viewerCounts}
          liveStreams={liveStreams}
          watchParties={watchParties}
          explorerStatus={explorerStatus}
          darkMode={darkMode}
          onToggleDark={toggleDark}
          onEnter={launchSpace}
          onSeeAll={openAll}
          onNavigate={navigate}
          ticker={finePointerDevice ? <WorldTicker /> : null}
          whatsNew={<WhatsNewPill variant="pill" triggerClassName="xc-new-pill" openOnHover />}
          votes={userVotes}
          onDiscover={openDiscover}
        />
      )}
      {!spaceDeckOpen && !chatLook && <button
        className="explore-dark-toggle"
        onClick={toggleDark}
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 9999,
          width: 30,
          height: 30,
          borderRadius: '50%',
          border: '1px solid rgba(0,0,0,0.15)',
          background: darkMode ? '#1a1a1a' : '#ffffff',
          color: darkMode ? '#ffffff' : '#1a1a1a',
          cursor: 'pointer',
          fontSize: 13,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          padding: 0
        }}
      >
        {darkMode ? '☀' : '☾'}
      </button>}
      {/* AI Marketplace + Build pinned links — kept OUTSIDE the dark-mode
          wrapper so the wrapper's CSS `filter` doesn't create a containing
          block that breaks position: fixed for these descendants. */}
      {!chatLook && <>
      <a
        href="/marketplace"
        target="_blank"
        rel="noopener noreferrer"
        title="Invite your AI companions/agents into spaces you enjoy!"
        className={`marketplace-link marketplace-pinned${darkMode ? ' dark-mode-cta' : ''}`}
        style={{
          ...styles.marketplaceLink,
          color: darkMode ? '#e8e8e8' : '#333',
        }}
      >
        <span>AI MARKETPLACE</span>
        <span style={{ ...styles.betaInline, color: darkMode ? '#999' : '#999' }}>(beta)</span>
        <span className="marketplace-new-tag" style={styles.newTag}>NEW!</span>
        <span className="explore-cta-tip" aria-hidden="true">
          Save AI agents!
        </span>
      </a>
      {/* Real <a> anchor so automated explorers can enumerate /custom-space
          navigation from the page; humans still get SPA nav via the onClick
          that preventDefaults the full-page load. Cycle 2: anchor-wrap. */}
      <a
        href="/custom-space"
        onClick={(e) => { e.preventDefault(); navigate('/custom-space'); }}
        className={`build-space-pinned${darkMode ? ' dark-mode-cta' : ''}`}
        style={{
          ...styles.buildSpacePinned,
          color: darkMode ? '#e8e8e8' : '#333',
          textDecoration: 'none',
        }}
      >
        + BUILD
        <span className="explore-cta-tip" aria-hidden="true">
          Build your own AI space in under a minute!
        </span>
      </a>
      </>}
      {darkMode && !chatLook && (
        <style>{`
          /* Whole page inverted + hue-rotated → "dark mode" */
          .explore-dark-mode {
            filter: invert(1) hue-rotate(180deg);
            background: #ffffff;
            min-height: 100vh;
          }
          /* Match the rubber-band overscroll color on iOS so scrolling past
             the top/bottom edge doesn't reveal a white seam outside the
             filtered wrapper. body sits outside .explore-dark-mode, so it
             isn't inverted — set it directly to black. */
          html, body {
            background-color: #000 !important;
          }
          /* Counter-invert media so photos/videos stay correct */
          .explore-dark-mode img,
          .explore-dark-mode video,
          .explore-dark-mode iframe,
          .explore-dark-mode picture,
          .explore-dark-mode canvas,
          .explore-dark-mode svg,
          .explore-dark-mode [style*="background-image"] {
            filter: invert(1) hue-rotate(180deg);
          }
          /* Slightly dim media so they don't blast against the dark background */
          .explore-dark-mode img,
          .explore-dark-mode video,
          .explore-dark-mode picture {
            filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(1.02);
          }
          /* Escape hatch: any element marked data-no-invert is left alone */
          .explore-dark-mode [data-no-invert] {
            filter: invert(1) hue-rotate(180deg);
          }
          /* Soften the card border so edges are visible on the dark page.
             The filter inverts pixel values, so #b8b8b8 here renders as ~#474747
             (a soft gray) on the user's screen — visible but not harsh. */
          .explore-dark-mode .space-card-front,
          .explore-dark-mode .space-card-back {
            border: 1px solid #b8b8b8 !important;
          }
          /* Same softening for the search input + sort select on /explore. */
          .explore-dark-mode input[type="text"],
          .explore-dark-mode select {
            border: 1px solid #b8b8b8 !important;
          }
          /* Bump low-contrast inline #999 text (likes count, etc.) so it stays
             readable on the dark page. After inversion #555 renders as ~#aaa. */
          .explore-dark-mode [style*="rgb(153, 153, 153)"] {
            color: #555 !important;
          }
        `}</style>
      )}
      {/* SAKURA RENDER — sibling of the dark-mode wrapper so the parent filter
          (invert + hue-rotate) doesn't re-rasterize them every animation frame.
          To revert: see SHOW_SAKURA_PETALS comment block at top of this file. */}
      {SHOW_SAKURA_PETALS && <SakuraPetals />}
      {/* 🌍 World ticker — VERY TOP of the page, matching /marketplace exactly
          (Nina 08-14 eve). It used to sit mid-page, below the Live & Trending
          rail, in the slot the expedition strip vacated.
          ⚠️ Mounted as a SIBLING of the dark-mode wrapper, not inside it —
          same reason as SakuraPetals above and the swipe deck below. The
          wrapper is `filter: invert(1) hue-rotate(180deg)`, so the ticker's
          own dark #1a1a2e strip was being inverted into a LIGHT bar on
          /explore while the identical component rendered dark on
          /marketplace. Moving it out fixes the position and that mismatch in
          one go: the component styles itself dark and needs no invert.
          Self-fetches (no `activity` prop) — /explore doesn't otherwise call
          the marketplace endpoint, and it renders nothing until that lands,
          so a slow or failed fetch costs no layout at the top of the page.
          📵 NOT on phones (Nina 08-14 eve). Gated on `finePointerDevice`, the
          same pointer test the expedition strip uses, so a NARROW DESKTOP
          window still gets it — a max-width check would have taken that case
          out too. /marketplace is unaffected: its ticker is deliberately
          sticky on mobile (see .mp-sec-ticker there), and this gate lives on
          the /explore mount only, not inside the shared component. */}
      {!directTileNav && !chatLook && finePointerDevice && <WorldTicker />}

      {/* 🃏 Fresh-phone space deck — mounted at TRUE page level, exactly the
          way /marketplace mounts <AgentSwipeDeck/> (Nina 08-14 eve: "why can't
          we just mimic how the cards are in /marketplace").
          ⚠️ It USED to render mid-page, and the comment there claimed it was
          "outside the dark-mode invert wrapper" — it was not. Verified by
          walking the JSX AST: the old mount sat inside BOTH
          `.explore-dark-mode` AND `styles.container`. Two separate ways that
          breaks a `position: fixed; inset: 0` overlay:
            · the wrapper's `filter: invert(1)` makes it a containing block, so
              the overlay sizes to the whole PAGE instead of the viewport, and
              the deck's self-dark styling gets inverted on top;
            · styles.container sets `overflowX: 'clip'`, and a clip context
              clips fixed descendants in WebKit — which is how a full-screen
              overlay ends up rendering as a blank half-painted panel on a real
              iPhone while headless Chrome looks fine.
          At page level it has neither ancestor, matching the marketplace deck
          that has always worked. Waits for cards: the picker returns [] until
          spaces have loaded, so no empty flash. */}
      {/* 🃏 Deck bridge (Nina 08-18): a first-visit phone keeps the black
          D-lockup brand screen (same composition as the index.html shell)
          while spaces load, instead of flashing the masthead + skeleton for
          a beat before the deck covers them. Fixed at PAGE level for the
          same WebKit fixed-ancestor reason as the deck below, and gated on
          isLoading (not card count) so a spaces list too short to build a
          deck can never trap it — load ends, bridge drops, normal page. */}
      {spaceDeckOpen && isLoading && (
        <div role="status" aria-label="Loading spaces" style={{
          position: 'fixed', inset: 0, zIndex: 5150, background: '#000',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center', padding: 24,
          color: '#fff',
          fontFamily: "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',Roboto,sans-serif",
        }}>
          <svg viewBox="0 0 64 64" aria-hidden="true" style={{ width: 'clamp(54px,12vw,68px)', height: 'auto', marginBottom: 18 }}>
            <defs><mask id="deck-bridge-eyes"><rect width="64" height="64" fill="white" /><circle cx="26.6" cy="29.3" r="2.4" fill="black" /><circle cx="37.4" cy="29.3" r="2.4" fill="black" /><path d="M11 21 L7.5 12.5 L14.5 12.5 Z" fill="black" /><path d="M53 21 L49.5 12.5 L56.5 12.5 Z" fill="black" /></mask></defs>
            <g mask="url(#deck-bridge-eyes)"><circle cx="32" cy="30" r="16" fill="none" stroke="#fff" strokeWidth="3" /><circle cx="32" cy="33" r="10" fill="#fff" /><path d="M17 38 C10 36 7.5 29 9.5 24.5" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" /><path d="M47 38 C54 36 56.5 29 54.5 24.5" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" /><circle cx="11" cy="19.5" r="5.5" fill="#fff" /><circle cx="53" cy="19.5" r="5.5" fill="#fff" /></g>
          </svg>
          <div style={{ fontSize: 'clamp(2.5rem,10vw,3.25rem)', fontWeight: 300, lineHeight: 1.05 }}>
            myspa<span style={{ color: '#43a047', fontWeight: 600 }}>i</span>ce
          </div>
          <div style={{ margin: '14px 0 0', fontSize: 'clamp(1rem,4vw,1.2rem)', fontWeight: 600, letterSpacing: 1.5, fontFamily: "Georgia,'Times New Roman',serif" }}>
            together, al<span style={{ color: '#43a047' }}>i</span>ve.
          </div>
          <div style={{ margin: '40px 0 0', fontSize: '0.78rem', opacity: 0.38 }}>
            dealing you in…
          </div>
        </div>
      )}
      {spaceDeckOpen && spaceDeckCards.length > 0 && (
        <SpaceSwipeDeck
          cards={spaceDeckCards}
          onLike={(space) => {
            // The real ❤️ mechanic, same as a card tap on the page. A LEFT
            // swipe deliberately writes nothing public.
            handleLike(space.id, true);
            logSpaceDeck({ id: space.id, name: space.name, action: 'like' });
            setDeckToast({ text: 'Saved to Loved', at: Date.now() });
          }}
          // onEnter retired 08-28 (Nina): the deck is like/pass only — no
          // Peek button, no end-card Enter. deck-log 'enter' rows stop here.
          onBrowse={closeSpaceDeck}
          onImpression={({ space, position, category, tier }) => {
            logSpaceDeck({ id: space.id, name: space.name, action: 'shown', position, category, tier });
          }}
        />
      )}
      {/* ❤️ 09-29: a right swipe says where it went (look C's Loved chip).
          Above the deck (z 5200), at the top: the card's name and the
          like/pass buttons own the bottom. */}
      {deckToast && <style>{'@keyframes deckToastIn { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }'}</style>}
      {deckToast && (
        <div key={deckToast.at} role="status" style={{
          position: 'fixed', top: 'calc(58px + env(safe-area-inset-top, 0px))', left: '50%', transform: 'translateX(-50%)',
          zIndex: 5300, display: 'flex', alignItems: 'center', gap: 8, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box',
          padding: '9px 16px 9px 13px', borderRadius: 999, background: '#0D0F13', color: '#ECEEF2',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16), 0 8px 24px rgba(0,0,0,0.4)',
          font: '600 14px/1.2 Geist, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
          whiteSpace: 'nowrap', pointerEvents: 'none', animation: 'deckToastIn 0.25s ease',
        }}>
          {/^Saved/.test(deckToast.text) && (
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style={{ color: '#4CAF50', flexShrink: 0 }}><path d="M12 20.5s-7.5-4.6-7.5-10.3A4.2 4.2 0 0 1 12 7.6a4.2 4.2 0 0 1 7.5 2.6c0 5.7-7.5 10.3-7.5 10.3z" fill="currentColor" /></svg>
          )}
          {deckToast.text}
        </div>
      )}
      {!chatLook && <div className={darkMode ? 'explore-dark-mode' : ''}>
    <div style={styles.container}>
      <style>{`
        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 6px rgba(76, 175, 80, 0.6); }
          50% { box-shadow: 0 0 12px rgba(76, 175, 80, 0.9), 0 0 4px rgba(76, 175, 80, 1); }
        }
        @keyframes liveRedDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.18); }
        }
        @keyframes liveRecDotPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(235,4,0,0.55), 0 0 6px rgba(235,4,0,0.5);
          }
          50% {
            transform: scale(1.15);
            box-shadow: 0 0 0 7px rgba(235,4,0,0), 0 0 10px rgba(235,4,0,0.7);
          }
        }
        @keyframes liveRailCardGlow {
          0%, 100% { box-shadow: 0 0 0 2px rgba(235,4,0,0.75), 0 0 20px rgba(235,4,0,0.35), 0 16px 44px rgba(0,0,0,0.4); }
          50% { box-shadow: 0 0 0 2px rgba(235,4,0,0.45), 0 0 34px rgba(235,4,0,0.6), 0 16px 44px rgba(0,0,0,0.4); }
        }
        /* Phone live glow: ring + a glow small enough to decay inside the
           stage's ~14px clip headroom — the desktop glow/drop layers clip
           into a hard-edged rectangle there (same artifact as the center
           card's drop shadow, see LiveRailCard). */
        @keyframes liveRailCardGlowPhone {
          0%, 100% { box-shadow: 0 0 0 2px rgba(235,4,0,0.75), 0 0 8px 2px rgba(235,4,0,0.35); }
          50% { box-shadow: 0 0 0 3px rgba(235,4,0,0.5), 0 0 10px 2px rgba(235,4,0,0.55); }
        }
        .live-rail-card {
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        /* hover:hover guard — iOS sticky hover would re-apply this big
           shadow after a tap and it clips square on the phone stage */
        @media (hover: hover) {
          .live-rail-card.is-center:hover {
            box-shadow: 0 22px 44px rgba(0,0,0,0.55), 0 6px 14px rgba(0,0,0,0.35);
            transform: translateY(-4px);
          }
        }
        .live-rail-arrow:hover {
          background-color: rgba(169,112,255,0.95) !important;
          color: #fff !important;
          transform: translateY(-50%) scale(1.08) !important;
        }
        .space-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
        }
        @media (hover: none) and (pointer: coarse) {
          .space-card-inner {
            -webkit-transform-style: preserve-3d;
            will-change: transform;
          }
          .space-card-outer {
            -webkit-tap-highlight-color: transparent;
          }
          /* iOS Safari: absolutely-positioned descendants with their own
             stacking context (e.g., the "New"/likes/menu top row over a banner,
             which has zIndex: 3) don't inherit backface-visibility from the
             face element, so they leak through to the flipped side reading
             mirrored. Applying it directly to all face descendants fixes it. */
          .space-card-front *,
          .space-card-back * {
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
          }
        }
        .space-card-inner.flipped {
          transform: rotateY(180deg);
        }
        .space-card-front, .space-card-back {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .space-card-back {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          transform: rotateY(180deg);
          border-radius: 12px;
          overflow: hidden;
        }
        @keyframes aiWiggle {
          0%, 8%, 100% { transform: translateY(0) rotate(0deg); }
          1.5% { transform: translateY(-5px) rotate(-8deg); }
          3% { transform: translateY(-8px) rotate(6deg); }
          4.5% { transform: translateY(-4px) rotate(-4deg); }
          6% { transform: translateY(-2px) rotate(2deg); }
        }
        @keyframes aiShimmer {
          0%, 100% { text-shadow: 0 0 4px rgba(67, 160, 71, 0.3); }
          50% { text-shadow: 0 0 8px rgba(67, 160, 71, 0.6), 0 0 16px rgba(67, 160, 71, 0.3); }
        }
        @keyframes ghostChatFadeIn {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes newTagPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(67, 160, 71, 0.5);
          }
          50% {
            transform: scale(1.08);
            box-shadow: 0 0 0 6px rgba(67, 160, 71, 0);
          }
        }
        .marketplace-new-tag {
          animation: newTagPulse 2s ease-in-out infinite;
        }
        /* Mobile-only AI Marketplace card under the "together, alive." subtitle.
           Hidden on desktop (the fixed top-right CTA covers that breakpoint). */
        .marketplace-link-mobile {
          display: none;
        }
        @media (max-width: 699px) {
          .marketplace-link-mobile {
            display: inline-flex;
            align-items: center;
            gap: 12px;
            margin-top: 14px;
            padding: 10px 14px;
            background: rgba(123,31,162,0.06);
            border: 1px solid rgba(123,31,162,0.20);
            border-radius: 12px;
            text-decoration: none;
            color: #2a1146;
            width: fit-content;
            max-width: 100%;
            box-sizing: border-box;
            transition: background-color 160ms ease, transform 160ms ease;
          }
          .marketplace-link-mobile:active {
            transform: scale(0.98);
            background: rgba(123,31,162,0.10);
          }
        }
        .marketplace-link,
        .build-space-pinned {
          transition: color 0.2s ease;
        }
        /* Light-mode hover — slight darken. */
        .marketplace-link:hover,
        .build-space-pinned:hover {
          color: #111 !important;
        }
        /* Dark-mode hover — brighten instead. The CTA buttons sit OUTSIDE the
           .explore-dark-mode wrapper (so the parent filter doesn't break their
           fixed positioning), so we can't rely on an ancestor selector. The
           dark-mode-cta class is added to the button itself when darkMode is on. */
        .marketplace-link.dark-mode-cta:hover,
        .build-space-pinned.dark-mode-cta:hover {
          color: #ffffff !important;
        }
        /* Hover tip — small popover-style chip that pops out to the LEFT of the
           CTA on hover/focus. Hidden on mobile (no hover state, and we don't
           want it competing with limited screen real estate). */
        .marketplace-link,
        .build-space-pinned {
          /* position: fixed already establishes a containing block, so the
             absolutely-positioned .explore-cta-tip inside is anchored to it. */
        }
        .explore-cta-tip {
          position: absolute;
          right: calc(100% + 12px);
          top: 50%;
          transform: translateY(-50%);
          padding: 6px 12px;
          background: rgba(255,255,255,0.97);
          color: #555;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 8px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.10);
          font-size: 0.7rem;
          font-weight: 500;
          font-style: italic;
          letter-spacing: 0;
          text-transform: none;
          white-space: nowrap;
          opacity: 0;
          visibility: hidden;
          transition: opacity 180ms ease, visibility 180ms ease, transform 180ms ease;
          pointer-events: none;
        }
        .dark-mode-cta .explore-cta-tip {
          background: rgba(28,28,28,0.97);
          color: #e2e2e2;
          border-color: rgba(255,255,255,0.14);
          box-shadow: 0 4px 14px rgba(0,0,0,0.45);
        }
        .marketplace-link:hover .explore-cta-tip,
        .marketplace-link:focus-visible .explore-cta-tip,
        .build-space-pinned:hover .explore-cta-tip,
        .build-space-pinned:focus-visible .explore-cta-tip {
          opacity: 1;
          visibility: visible;
          transform: translateY(-50%) translateX(-2px);
        }
        /* 08-01 finder unit: invisible on desktop (display:contents = no box,
           zero layout change), a carded panel on phones (rules in the 699px
           block below). The mobile combined rail + sort pill only ever render
           there too. */
        .browse-finder { display: contents; }
        .browse-rail-mobile { display: none !important; }
        .rail-divider {
          width: 1px;
          height: 16px;
          background: rgba(0,0,0,0.18);
          flex-shrink: 0;
        }
        .sort-pill {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          /* Nina 08-02: the solid #7b1fa2 pill rendered PINK through the
             dark-mode invert(1) hue-rotate(180deg) — and phones default
             dark now, so that was its debut look. Round 2 same day: her
             pick = CLASSIC DROPDOWN chrome, not a chip — rectangular
             field, white bg + gray border (inverts to a dark field in
             dark mode, exactly what a select should do there). The native
             select still rides invisibly on top for the stock picker. */
          background: #fff;
          color: inherit;
          border: 1px solid rgba(0,0,0,0.22);
          font-size: 0.82rem;
          font-weight: 600;
          border-radius: 8px;
          padding: 8px 12px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .sort-pill select {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          /* 16px floor: a smaller select zooms the page on iOS focus */
          font-size: 16px;
        }
        @media (max-width: 699px) {
          .explore-grid {
            grid-template-columns: 1fr !important;
          }
          /* 📱 07-19 crash fix: let WebKit skip rendering offscreen cards
             entirely (layers, ghost-chat animations, decoded banner paint).
             Older Safari ignores content-visibility — harmless. The intrinsic
             size keeps the scrollbar from jumping as cards enter view. */
          .space-card-outer {
            content-visibility: auto;
            /* "Cards load far right, fix as you scroll" (Nina's iPhone,
               layoutprobe 07-22: card1 W=420 while wrong, W=335 once
               scrolled into view): her iOS WebKit applies the two-axis
               contain-intrinsic-size shorthand's intrinsic size to WIDTH
               and sizes SKIPPED cards 420px wide in the 335px column —
               content shifts right, self-heals at first real layout.
               The 07-22 width:100% guard shipped and did NOT cure her
               device (07-26 re-report) — so the width
               intrinsic is now GONE ENTIRELY: height-only longhand keeps
               the scrollbar-stability placeholder (the 07-19 crash-fix
               benefit) and leaves the engine no width value to misapply.
               ROUND 3 (07-31, Nina re-report — still shifting, live bundle
               VERIFIED to contain round 2): intrinsic-size machinery is now
               GONE ENTIRELY — no lengths, no 'auto' remembered-size keyword,
               nothing any buggy parser/cache can misapply to width. A real
               min-height does the skipped-card placeholder job through plain
               layout instead (min-height applies normally under size
               containment). Cost: scrollbar springs a bit as tall cards
               enter view. If THIS fails, the mechanism isn't intrinsic
               sizing at all — capture ?layoutprobe=1 on-device before round
               4 (dropping content-visibility on iOS — mind the 07-19
               GPU-crash reason it exists). */
            contain-intrinsic-size: none;
            min-height: 420px !important;
            width: 100%;
            box-sizing: border-box;
          }
          /* ROUND 4 (08-02): round 3 FAILED on device — Nina's layoutprobe
             capture caught card1 at W=359 (24px past its 335px column, right
             edge off-screen) with the intrinsic-size machinery entirely gone,
             page/visualViewport/document all clean. So the mechanism is
             content-visibility's skip-sizing ITSELF on her iOS WebKit; cards
             heal when real layout runs = "fix as you scroll". iOS therefore
             gets NO content-visibility (this @supports matches iOS WebKit
             only — -webkit-touch-callout is an iOS-only property). The
             07-19 GPU-crash risk this reintroduces is much smaller than it
             was: that crash predates the 07-28 image-weight fix (/explore
             images 35MB of 1024px PNGs → 2.3MB of 512px WebP), which
             removed most of the decode/layer pressure. min-height above
             stays (harmless without CV). Other engines keep the skip. */
          @supports (-webkit-touch-callout: none) {
            .space-card-outer {
              content-visibility: visible;
            }
          }
          .marketplace-pinned {
            display: none !important;
          }
          .build-space-pinned {
            top: 76px !important;
          }
          .explore-dark-toggle {
            top: 8px !important;
            left: 8px !important;
          }
          /* Mobile keeps Show-all in the section header (compact). */
          .category-show-all-below-wrap {
            display: none !important;
          }
          /* No hover state on touch devices, and screen real estate is tight —
             so we never show the desktop hover-tip on mobile. */
          .explore-cta-tip {
            display: none !important;
          }
          /* 📱 Chip de-crowding (Nina's 13-mini screenshot, 07-19): trending +
             vibe + search-suggestion pills wrapped into ~7 stacked rows and the
             filter stack ate the whole first viewport. Phones get ONE swipeable
             row per chip group (labels ride along as the first item), and
             search + sort share a row instead of wrapping. Desktop untouched. */
          .browse-chip-row {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            padding-bottom: 2px;
          }
          .browse-chip-row::-webkit-scrollbar {
            display: none;
          }
          .browse-chip-row > * {
            flex-shrink: 0;
          }
          .browse-controls {
            flex-wrap: nowrap !important;
            gap: 8px !important;
            margin-bottom: 28px !important;
          }
          .browse-search {
            min-width: 0 !important;
          }
          /* Space-of-the-Week (08-01): three FIXED rows via grid — the old
             flex-wrap tumbled the five spans into 2-3 arbitrary lines.
             Row 1 label + visits · row 2 name + Visit pill · row 3 blurb,
             title and blurb hard-clamped to one line each. */
          .sow-banner {
            display: grid !important;
            grid-template-columns: auto 1fr auto;
            grid-template-areas:
              'trophy label visits'
              'name   name  pill'
              'meta   meta  meta';
            column-gap: 8px;
            row-gap: 6px;
            align-items: center;
            padding: 11px 14px !important;
            margin-bottom: 16px !important;
            border-radius: 14px !important;
          }
          .sow-trophy { grid-area: trophy; font-size: 1rem !important; }
          .sow-label {
            grid-area: label;
            font-size: 0.66rem !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .sow-visits {
            grid-area: visits;
            font-size: 0.7rem !important;
            font-weight: 700;
            white-space: nowrap;
          }
          .sow-name {
            grid-area: name;
            font-size: 0.98rem !important;
            min-width: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .sow-visit {
            grid-area: pill;
            justify-self: end;
            margin-left: 0 !important;
            font-size: 0.72rem !important;
            padding: 4px 12px !important;
          }
          .sow-meta {
            grid-area: meta;
            font-size: 0.74rem !important;
            min-width: 0;
            /* 08-02 (Nina): one nowrap line gave the pick no room to say
               anything — allow a small two-line blurb instead. */
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.35;
          }
          /* 08-01 finder panel: one quiet surface for search + sort + chips.
             Children's own margins die here — the panel's gap is the rhythm
             (the old negative margins were tuned against desktop spacing). */
          .browse-finder {
            display: flex !important;
            flex-direction: column;
            gap: 8px;
            background: rgba(0,0,0,0.03);
            border: 1px solid rgba(0,0,0,0.10);
            border-radius: 14px;
            padding: 10px;
            margin-bottom: 28px;
          }
          .browse-finder .browse-controls { margin-bottom: 0 !important; }
          .browse-finder .browse-chip-row { margin: 0 !important; padding: 0 !important; }
          .browse-sort-desktop { display: none !important; }
          .browse-rail-desktop { display: none !important; }
          .browse-rail-mobile { display: flex !important; }
          /* 📱 Card copy diet (Nina 08-02): descriptions clamp to TWO lines
             (Show more still expands), and collapsed chip rows are hard
             single-line — no wrapping until +N is tapped. */
          .card-desc-clamp {
            -webkit-line-clamp: 2 !important;
          }
          .card-chips-collapsed {
            flex-wrap: nowrap !important;
            overflow: hidden !important;
          }
        }
        @media (min-width: 700px) {
          /* Desktop moves Show-all below the grid (load-more footer pattern). */
          .category-show-all-header {
            display: none !important;
          }
          /* SOW one-line banner joiners (desktop only): the spans are flat
             siblings now, so the old "— reason · N visits" join is CSS. */
          .sow-meta::before { content: '· '; }
          .sow-visits::before { content: '· '; }
        }
        .category-show-all:hover {
          background-color: rgba(123,31,162,0.08);
          color: #5e1583;
        }
        .category-show-all:focus-visible {
          outline: 2px solid #7b1fa2;
          outline-offset: 2px;
        }
        .search-suggestion-chip:hover {
          background-color: rgba(0,0,0,0.08) !important;
          border-color: rgba(0,0,0,0.28) !important;
        }
        .search-suggestion-chip:focus-visible {
          outline: 2px solid #7b1fa2;
          outline-offset: 2px;
        }
        .search-suggestion-chip:active {
          transform: scale(0.97);
        }
        @keyframes exploreSkeletonShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .explore-skeleton-shimmer {
          background: linear-gradient(90deg, #eef0f3 0%, #f6f7f9 50%, #eef0f3 100%);
          background-size: 200% 100%;
          animation: exploreSkeletonShimmer 1.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .explore-skeleton-shimmer {
            animation: none;
            background: #eef0f3;
          }
        }
        .explore-empty-primary:hover {
          background-color: #5e1583;
        }
        .explore-empty-secondary:hover {
          background-color: rgba(123,31,162,0.06);
          border-color: rgba(123,31,162,0.6);
        }
        .explore-empty-primary:focus-visible,
        .explore-empty-secondary:focus-visible {
          outline: 2px solid #7b1fa2;
          outline-offset: 2px;
        }
      `}</style>
      {/* Floating-emoji background OFF (Nina 08-14: briefly restored during
          the launch pass, reverted same day — no ambient bg is the intended
          look). TO RESTORE: delete `false &&`. */}
      {false && floatingEmojis.length > 0 && <FloatingSvgBackground emojis={floatingEmojis} count={60} opacityMin={0.65} opacityMax={0.95} sizeMin={8} sizeMax={16} />}
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitleBlock}>
          <h1 style={styles.title}>myspa<span style={styles.titleAi}>i</span>ce</h1>
          <p style={styles.subtitle}>
            together, al<span style={styles.titleAi}>i</span>ve.
          </p>
          {/* Mobile-only inline AI Marketplace entry. Lives in the empty area
              under the subtitle — desktop hides it via the
              .marketplace-link-mobile media query and uses the fixed top-right
              CTA instead. Sits inside the dark-mode wrapper, so colors auto-
              invert in dark mode (no manual handling needed). */}
          <a
            href="/marketplace"
            target="_blank"
            rel="noopener noreferrer"
            className="marketplace-link-mobile"
          >
            <span style={styles.marketplaceMobileIcon} aria-hidden="true">&#129302;</span>
            <span style={styles.marketplaceMobileText}>
              <span style={styles.marketplaceMobileTitle}>AI Marketplace</span>
              <span style={styles.marketplaceMobileSubtitle}>Save AI agents!</span>
            </span>
            <span className="marketplace-new-tag" style={styles.newTag}>NEW!</span>
          </a>
          {/* ✨ What's new pill + sheet (09-15): content = data/whatsNew.js,
              dot logic = utils/whatsNewBadge.js. Inside the dark-mode invert
              wrapper on purpose — light colors auto-invert like the
              marketplace card above. */}
          <WhatsNewPill />
        </div>
      </div>
      {/* Featured Competition Banner — REVERT: gated by SHOW_FEATURED_BANNER (top of file) */}
      {SHOW_FEATURED_BANNER && featuredData?.featured && (
        <FeaturedCompetitionBanner
          featured={featuredData.featured}
          leaderboard={featuredData.leaderboard}
          totalGames={featuredData.totalGamesThisWeek}
          countdown={countdown}
          onJoin={handleJoinCompetition}
          isJoining={joiningCompetition}
        />
      )}
      {/* Live Now Rail */}
      <LiveNowRail
        spaces={spaces}
        viewerCounts={viewerCounts}
        liveGames={liveGames}
        liveStreams={liveStreams}
        watchParties={watchParties}
        onJoin={handleJoinSpace}
        explorerStatus={explorerStatus}
        explorerUnreachable={explorerUnreachable}
        // True when ?bot=1 was seen on the URL at any point in this tab's session
        // (sticky via sessionStorage — see resolver above). Enables direct-tile-click
        // navigation for the explorer bot AND hides his own tile to prevent the
        // recursive-iframe trap.
        directTileNav={directTileNav}
        // Per-card weekly-game pill RETIRED (Nina 08-01): with a named game
        // pinned ("AI Roast Battle") the pill on every card read as each
        // space's own label. The FeaturedGameRailCard advertises the weekly
        // game; restore by passing featuredData?.featured?.name again.
        featuredGame={null}
        featuredCompetition={SHOW_FEATURED_RAIL_CARD ? (featuredData?.featured || null) : null}
        onJoinFeatured={handleJoinCompetition}
        joiningFeatured={joiningCompetition}
      />


      {/* 🌍 World ticker MOVED (Nina 08-14 eve) to the very top of the page,
          mounted as a sibling of the dark-mode wrapper — see the comment there.
          It briefly owned this mid-page slot (the one the expedition strip
          vacated); nothing renders here now. */}

      {/* 🃏 Space deck MOVED to page level (Nina 08-14 eve) — see the mount
          beside <WorldTicker/> above. It used to render here, which was inside
          BOTH the invert wrapper and styles.container; nothing renders here
          now. */}

      {/* Explorer Tamagotchi — hidden in bot mode (directTileNav) so the bot
          never sees/pokes its own pet form (same recursion guard as its
          self-tile above) and its browser skips the extra polling. */}
      {SHOW_EXPLORER_TAMAGOTCHI && !directTileNav && <ExplorerTamagotchi />}

      {/* Browse section header — matches the "LIVE & TRENDING" eyebrow above the rail */}
      <div id="browse" style={{ ...styles.browseHeader, scrollMarginTop: 16 }}>
        <h2 style={styles.liveRailTitle}>Browse</h2>
        {/* 🧭 the way back to look C's room list (only when C is on for this visitor) */}
        {startLook !== 'grid' && (
          <a
            href="/explore"
            onClick={(e) => { e.preventDefault(); closeAll(); }}
            style={{ display: 'inline-block', marginTop: 6, fontSize: '0.85rem', fontWeight: 600, color: '#555', textDecoration: 'none' }}
          >
            ← Back to the room list
          </a>
        )}
      </div>

      {/* 🏆 Space of the Week banner */}
      {spaceOfWeek && (() => {
        const sowSpace = spaces.find(s => s.id === spaceOfWeek.game_instance_id);
        if (!sowSpace) return null;
        // reason is a snapshot from Sunday's pick — its "N visits" flavor
        // went stale the moment someone visited (banner said 51 while the
        // row was at 95+). Show the LIVE count (WS viewer_count keeps
        // totalViews fresh) and keep only non-visit reasons as flavor.
        // 08-01 (Nina): visits gated to >10 so a small count never undersells
        // the pick; count is its own span (mobile grid places it top-right).
        // Desktop joiners ("— " before reason, "· " before visits) are CSS
        // ::before rules in the min-width:700 block — mobile omits them.
        const liveViews = totalViews[sowSpace.id] || sowSpace.total_views || 0;
        const reasonIsVisits = /^\d+\s+visits?$/.test(spaceOfWeek.reason || '');
        const reasonText = (spaceOfWeek.reason && !reasonIsVisits) ? spaceOfWeek.reason : null;
        // 08-02 (Nina): the banner often carried no blurb at all (visit-count
        // reasons are suppressed). Fall back to the space's own tagline, then
        // a trimmed description, so the pick always says WHY it's worth a tap.
        const sowBlurb = reasonText
          || (sowSpace.tagline ? `“${sowSpace.tagline}”` : null)
          || (sowSpace.description ? `${sowSpace.description.slice(0, 110)}${sowSpace.description.length > 110 ? '…' : ''}` : null);
        return (
          <a
            href={`/game-step?gameId=${encodeURIComponent(sowSpace.id)}`}
            onClick={(e) => { e.preventDefault(); launchSpace(sowSpace); }}
            className="sow-banner"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '10px 16px', marginBottom: 20, borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(255,193,7,0.12), rgba(255,152,0,0.08))',
              border: '1px solid rgba(255,193,7,0.35)',
              textDecoration: 'none', cursor: 'pointer',
            }}
          >
            <span className="sow-trophy" style={{ fontSize: '1.2rem' }}>🏆</span>
            <span className="sow-label" style={{ fontSize: '0.78rem', fontWeight: 800, color: '#b8860b', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Space of the Week
            </span>
            <span className="sow-name" style={{ fontSize: '0.95rem', fontWeight: 700, color: '#5e4a00' }}>{sowSpace.name}</span>
            {sowBlurb && (
              <span className="sow-meta" style={{ fontSize: '0.78rem', color: '#8a7a30' }}>{sowBlurb}</span>
            )}
            {liveViews > 10 && (
              <span className="sow-visits" style={{ fontSize: '0.78rem', color: '#8a7a30', fontVariantNumeric: 'tabular-nums' }}>{liveViews} visits</span>
            )}
            <span className="sow-visit" style={{ marginLeft: 'auto', background: '#b8860b', color: '#fff', fontSize: '0.78rem', fontWeight: 700, padding: '5px 14px', borderRadius: 999 }}>Visit</span>
          </a>
        );
      })()}

      {/* Search + sort + chip rails live inside one .browse-finder unit:
          display:contents on desktop (zero layout change there), a single
          carded panel on phones so the finder reads as ONE thing (08-01). */}
      <div className="browse-finder">
      {/* Search and Controls */}
      <div className="browse-controls" style={styles.controls}>
        <div className="browse-search" style={styles.searchContainer}>
          <span style={styles.searchIcon}>&#128269;</span>
          <input
            type="text"
            placeholder="Search spaces..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={(e) => {
              setIsSearchFocused(true);
              // Mobile fix queue #2 (07-20): at mini heights the software
              // keyboard covers the search box (kbd-input-visible FALSE
              // @375×812). Same pin pattern the composers got in wave 1
              // (HumanInputForm textarea onFocus).
              if (window.innerWidth >= 700) return;
              const el = e.target;
              const pin = () => { try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { el.scrollIntoView(); } };
              setTimeout(pin, 300);
              if (window.visualViewport) {
                const vv = window.visualViewport;
                const openedAt = Date.now();
                const onResize = () => { if (Date.now() - openedAt < 1500) pin(); };
                vv.addEventListener('resize', onResize);
                const cleanup = () => { vv.removeEventListener('resize', onResize); el.removeEventListener('blur', cleanup); };
                el.addEventListener('blur', cleanup);
              }
            }}
            // Delay so a chip click registers before chips collapse on blur.
            onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
            style={styles.searchInput}
          />
        </div>

        <div className="browse-sort-desktop" style={styles.sortContainer}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.sortSelect}
          >
            <option value="most-watched">Live Now</option>
            <option value="most-visited">Popular</option>
            <option value="newest">Recent</option>
            <option value="most-liked">Loved</option>
            <option value="games">Games</option>
            <option value="a-z">A-Z</option>
            <option value="z-a">Z-A</option>
          </select>
        </div>

      </div>

      {/* Suggestion chips — appear on search focus or while typing.
          Filter to chips that match the current query (substring, case-insensitive). */}
      {(isSearchFocused || searchQuery.trim()) && (() => {
        const q = searchQuery.trim().toLowerCase();
        const visible = q
          ? SEARCH_SUGGESTION_CHIPS.filter(c => c.toLowerCase().includes(q) && c.toLowerCase() !== q)
          : SEARCH_SUGGESTION_CHIPS;
        if (visible.length === 0) return null;
        return (
          <div className="browse-chip-row" style={styles.searchChipsRow} aria-label="Search suggestions">
            <span style={styles.searchChipsLabel}>Try:</span>
            {visible.map(chip => (
              <button
                key={chip}
                type="button"
                className="search-suggestion-chip"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSearchQuery(chip);
                  setIsSearchFocused(false);
                }}
                style={styles.searchChip}
              >
                {chip}
              </button>
            ))}
          </div>
        );
      })()}

      {/* 🔥 Trending tags (07-15): interests weighted by live viewers across
          loaded spaces; a chip tap searches that tag (the search filter
          already matches interests — zero new plumbing).
          08-01 restructure (Nina): trending is computed ONCE here and the chip
          renderers are shared between the desktop rows (unchanged look) and a
          NEW mobile-only combined rail — sort pill · Trending · Vibe in one
          swipe lane. Labels stay as words (Nina call, 08-01). */}
      {(() => {
        const score = {};
        (spaces || []).forEach(s => {
          const w = 1 + (viewerCounts[s.id] || 0) * 2;
          (s.interests || []).forEach(i => {
            if (i && i !== 'featured') score[i] = (score[i] || 0) + w;
          });
        });
        const trending = Object.entries(score).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([t]) => t);
        const showTrending = trending.length >= 2;
        const activeTag = searchQuery.trim().toLowerCase();
        // Label + chips share the Vibe row's system (searchChip pills) —
        // a one-off style here next to the mood chips read as two
        // competing designs (Nina 07-31). Pills went back to neutral
        // gray/black 08-01; purple = active/selected only.
        const trendingChip = (tag) => {
          const active = activeTag === tag.toLowerCase();
          return (
            <button
              key={tag}
              type="button"
              className="search-suggestion-chip"
              onClick={() => setSearchQuery(active ? '' : tag)}
              style={{
                ...styles.searchChip,
                ...(active ? {
                  background: '#7b1fa2',
                  color: '#fff',
                  border: '1px solid #7b1fa2',
                } : {}),
              }}
            >
              {tag}{active ? ' ✕' : ''}
            </button>
          );
        };
        const moodChip = (m) => {
          const active = selectedMood === m.key;
          return (
            <button
              key={m.key}
              type="button"
              className="search-suggestion-chip"
              onClick={() => setSelectedMood(active ? null : m.key)}
              style={{
                ...styles.searchChip,
                ...(active ? {
                  background: '#7b1fa2',
                  color: '#fff',
                  border: '1px solid #7b1fa2',
                } : {}),
              }}
            >
              {m.emoji} {m.label}
            </button>
          );
        };
        const SORT_LABELS = { 'most-watched': 'Live Now', 'most-visited': 'Popular', newest: 'Recent', 'most-liked': 'Loved', games: 'Games', 'a-z': 'A-Z', 'z-a': 'Z-A' };
        return (
          <>
            {showTrending && (
              <div className="browse-chip-row browse-rail-desktop" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '-20px 0 10px', padding: '0 4px' }}>
                <span style={styles.searchChipsLabel}>
                  Trending:
                </span>
                {trending.map(trendingChip)}
              </div>
            )}

            {/* Mood chips — filter by each space's current vibe */}
            <div className="browse-chip-row browse-rail-desktop" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 0, marginBottom: 24 }} aria-label="Filter by mood">
              <span style={styles.searchChipsLabel}>Vibe:</span>
              {MOOD_META.map(moodChip)}
              {selectedMood && (
                <button
                  type="button"
                  onClick={() => setSelectedMood(null)}
                  style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}
                >
                  clear
                </button>
              )}
            </div>

            {/* 📱 Mobile rails, one per line (Nina 08-02 round 2): sort pill ·
                Vibe · Trending, vibes ABOVE trending. The native select still
                rides invisibly on the pill, so the picker UX is stock. The 🔴
                emoji prefix is gone (her call, same pass). No "clear" here —
                an active mood chip toggles off on tap and a trailing button
                would hide off-screen in a scroll lane. */}
            <div className="browse-chip-row browse-rail-mobile" style={{ display: 'flex', alignItems: 'center', gap: 8 }} aria-label="Sort spaces">
              <span className="sort-pill">
                {SORT_LABELS[sortBy] || 'Sort'} ▾
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort spaces">
                  <option value="most-watched">Live Now</option>
                  <option value="most-visited">Popular</option>
                  <option value="newest">Recent</option>
                  <option value="most-liked">Loved</option>
                  <option value="games">Games</option>
                  <option value="a-z">A-Z</option>
                  <option value="z-a">Z-A</option>
                </select>
              </span>
            </div>
            <div className="browse-chip-row browse-rail-mobile" style={{ display: 'flex', alignItems: 'center', gap: 8 }} aria-label="Filter by mood">
              <span style={styles.searchChipsLabel}>Vibe:</span>
              {MOOD_META.map(moodChip)}
            </div>
            {showTrending && (
              <div className="browse-chip-row browse-rail-mobile" style={{ display: 'flex', alignItems: 'center', gap: 8 }} aria-label="Trending tags">
                <span style={styles.searchChipsLabel}>Trending:</span>
                {trending.map(trendingChip)}
              </div>
            )}
          </>
        );
      })()}
      </div>{/* /.browse-finder */}

      {/* Loading/Error States */}
      {isLoading && (
        <div className="explore-grid" style={styles.grid} aria-busy="true" aria-label="Loading spaces">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {error && (
        <div style={styles.errorContainer}>
          <p>{error}</p>
          <button onClick={fetchSharedSpaces} style={styles.retryButton}>
            Retry
          </button>
        </div>
      )}

      {/* Space Cards — flat grid when searching, grouped sections otherwise */}
      {!isLoading && !error && filteredSpaces.length === 0 && (
        <div style={styles.emptyState}>
          {/* Vibe-filter miss FIRST: without this branch a single chip tap on a
              feed where no space carries that mood claimed the whole site was
              empty ("No shared spaces yet") with no way to clear on mobile. */}
          {selectedMood ? (
            <>
              <h3 style={styles.emptyStateTitle}>
                Nothing&rsquo;s {MOOD_BY_KEY[selectedMood]?.emoji} {MOOD_BY_KEY[selectedMood]?.label || selectedMood} right now
              </h3>
              <p style={styles.emptyStateBody}>Vibes shift as the rooms talk. Try another one, or browse everything.</p>
              <div style={styles.emptyStateActions}>
                <button
                  type="button"
                  className="explore-empty-primary"
                  style={styles.emptyStatePrimaryBtn}
                  onClick={() => setSelectedMood(null)}
                >
                  Show all vibes
                </button>
              </div>
            </>
          ) : searchQuery.trim() ? (
            <>
              <h3 style={styles.emptyStateTitle}>No spaces match &ldquo;{searchQuery.trim()}&rdquo;</h3>
              <p style={styles.emptyStateBody}>Try a different search, browse everything, or build your own.</p>
              <div style={styles.emptyStateActions}>
                <button
                  type="button"
                  className="explore-empty-secondary"
                  style={styles.emptyStateSecondaryBtn}
                  onClick={() => setSearchQuery('')}
                >
                  Browse all
                </button>
                <button
                  type="button"
                  className="explore-empty-primary"
                  style={styles.emptyStatePrimaryBtn}
                  onClick={() => navigate('/custom-space')}
                >
                  Create your own &rarr;
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={styles.emptyStateTitle}>No shared spaces yet</h3>
              <p style={styles.emptyStateBody}>Be the first to put one up.</p>
              <div style={styles.emptyStateActions}>
                <button
                  type="button"
                  className="explore-empty-primary"
                  style={styles.emptyStatePrimaryBtn}
                  onClick={() => navigate('/custom-space')}
                >
                  Create your own &rarr;
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {!isLoading && !error && filteredSpaces.length > 0 && (searchQuery.trim() || sortBy !== 'most-watched') && (
        <div className="explore-grid" style={styles.grid}>
          {filteredSpaces.map((space, idx) => (
            <SpaceCard
              key={space.id}
              space={space}
              viewerCount={viewerCounts[space.id] || 0}
              totalViews={totalViews[space.id] || space.total_views || 0}
              isLive={liveGames[space.id] || false}
              onJoin={() => handleJoinSpace(space)}
              flipDelay={3000 + (idx % 5) * 1200}
            />
          ))}
        </div>
      )}

      {/* Category rails only for the default browse — an explicit sort means the user wants one flat ordered list */}
      {!isLoading && !error && filteredSpaces.length > 0 && !searchQuery.trim() && sortBy === 'most-watched' && (
        groupedSections.map(section => {
          // Cycle 7: bot-mode flag forces every section expanded by default.
          // Per-section overrides (set by the toggle below) take precedence
          // when present, so a human visiting ?bot=1 manually can still
          // collapse individual sections.
          const isExpanded = (expandedSections[section.key] ?? !!expandedSections.__BOT_EXPAND_ALL__);
          const visibleSpaces = isExpanded ? section.spaces : section.spaces.slice(0, 3);
          const hasMore = section.spaces.length > 3;
          const toggleExpanded = () =>
            setExpandedSections(prev => ({
              ...prev,
              [section.key]: !(prev[section.key] ?? !!prev.__BOT_EXPAND_ALL__),
            }));
          const showAllLabel = isExpanded
            ? <>Show less <span aria-hidden="true" style={styles.categoryShowAllArrow}>↑</span></>
            : <>Show all ({section.spaces.length}) <span aria-hidden="true" style={styles.categoryShowAllArrow}>→</span></>;
          return (
            <div key={section.key} id={`browse-${section.key}`} style={{ ...styles.categorySection, scrollMarginTop: 16 }}>
              <div style={styles.categoryHeader}>
                <h3 style={styles.categoryTitle}>{section.label}</h3>
                {/* Mobile: show toggle inline with the section title (compact). */}
                {hasMore && (
                  <button
                    type="button"
                    className="category-show-all category-show-all-header"
                    style={styles.categoryShowAll}
                    onClick={toggleExpanded}
                    aria-expanded={isExpanded}
                  >
                    {showAllLabel}
                  </button>
                )}
              </div>
              <div className="explore-grid" style={styles.grid}>
                {visibleSpaces.map((space, idx) => (
                  <SpaceCard
                    key={space.id}
                    space={space}
                    viewerCount={viewerCounts[space.id] || 0}
                    totalViews={totalViews[space.id] || space.total_views || 0}
                    isLive={liveGames[space.id] || false}
                    onJoin={() => handleJoinSpace(space)}
                    flipDelay={3000 + (idx % 5) * 1200}
                  />
                ))}
              </div>
              {/* Desktop: show toggle below the grid (more like a "load more" footer). */}
              {hasMore && (
                <div className="category-show-all-below-wrap" style={styles.categoryShowAllBelowWrap}>
                  <button
                    type="button"
                    className="category-show-all category-show-all-below"
                    style={styles.categoryShowAll}
                    onClick={toggleExpanded}
                    aria-expanded={isExpanded}
                  >
                    {showAllLabel}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

    </div>
      </div>}
    </>
  );
};

// Featured Competition Banner Component
const FeaturedCompetitionBanner = ({ featured, leaderboard, totalGames, countdown, onJoin, isJoining }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!featured) return null;

  return (
    <div style={{
      ...styles.featuredBanner,
      background: featured.themeGradient || styles.featuredBanner.background,
      boxShadow: featured.themeColor
        ? `0 4px 16px rgba(0,0,0,0.2), 0 0 25px ${featured.themeColor}38, 0 0 50px ${featured.themeColor}1F, 0 0 80px ${featured.themeColor}0F`
        : styles.featuredBanner.boxShadow,
      ...(featured.bannerStyle || {}),
    }}>
      {/* Decorative emoji background */}
      <div style={styles.featuredEmojiBg}>
        {(featured.emojis || []).map((emoji, i) => (
          <span key={i} style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: `${10 + i * 30}%`,
            fontSize: `${2 + i * 0.5}rem`,
            opacity: 0.1,
            pointerEvents: 'none',
          }}>{emoji}</span>
        ))}
      </div>

      {/* Details toggle (top right) */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        style={styles.featuredMoreButton}
        title="Competition details"
      >
        &#9432;
      </button>

      {/* Compact bar */}
      <div style={styles.featuredContent}>
        {/* Left: info */}
        <div style={styles.featuredLeft}>
          <div style={{ ...styles.featuredLabel, ...(featured.labelStyle || {}) }}>{featured.labelText || 'WEEKLY ARENA'}</div>
          <h2 style={{ ...styles.featuredTitle, ...(featured.titleStyle || {}) }}>
            {(featured.emojis || [])[0]}{' '}
            {featured.highlightWord
              ? (() => {
                  const parts = featured.name.split(new RegExp(`(${featured.highlightWord}[!?.]*)`  , 'i'));
                  return parts.map((part, i) =>
                    part.toLowerCase().startsWith(featured.highlightWord.toLowerCase())
                      ? <span key={i} style={{ color: '#fff', textShadow: '0 0 20px rgba(255,255,255,0.5)' }}>{part}</span>
                      : part
                  );
                })()
              : featured.name}
          </h2>
        </div>

        {/* Center: countdown */}
        <div style={styles.featuredCenter}>
          <div style={styles.countdownRow}>
            <span style={styles.countdownLabel}>Ends in</span>
            <div style={styles.countdownDigits}>
              {countdown.days > 0 && <span style={styles.countdownUnit}>{countdown.days}d</span>}
              <span style={styles.countdownUnit}>{String(countdown.hours).padStart(2, '0')}h</span>
              <span style={styles.countdownUnit}>{String(countdown.minutes).padStart(2, '0')}m</span>
              <span style={styles.countdownUnit}>{String(countdown.seconds).padStart(2, '0')}s</span>
            </div>
          </div>
        </div>

        {/* Right: leaderboard + join */}
        <div style={styles.featuredRight}>
          {leaderboard && leaderboard.length > 0 && (
            <div style={styles.leaderboardList}>
              {leaderboard.slice(0, 3).map((entry, i) => (
                <div key={i} style={styles.leaderboardRow}>
                  <span style={styles.leaderboardRank}>
                    {i === 0 ? '\u{1F947}' : i === 1 ? '\u{1F948}' : '\u{1F949}'}
                  </span>
                  <span style={styles.leaderboardName}>{entry.playerName}</span>
                  <span style={styles.leaderboardScore}>{entry.score}pts</span>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={onJoin}
            disabled={isJoining}
            style={{
              ...styles.joinCompetitionButton,
              backgroundColor: featured.themeColor || '#7b1fa2',
              opacity: isJoining ? 0.7 : 1,
              cursor: isJoining ? 'not-allowed' : 'pointer',
            }}
          >
            {isJoining ? 'Joining...' : 'Join Competition'}
          </button>
        </div>
      </div>

      {/* Expandable details panel */}
      {showDetails && (
        <div style={styles.featuredDetails}>
          <div style={styles.featuredDetailsGrid}>
            <div style={styles.featuredDetailsSection}>
              <div style={{ ...styles.featuredDetailsSectionTitle, ...(featured.detailsTextColor ? { color: featured.detailsTextColor } : {}) }}>
                About
              </div>
              <p style={{ ...styles.featuredDetailsText, ...(featured.detailsTextColor ? { color: featured.detailsTextColor } : {}) }}>
                {featured.tagline ? `${featured.tagline}. ` : ''}
                {featured.highlightWord && featured.description?.includes(featured.highlightWord)
                  ? featured.description.split(new RegExp(`(${featured.highlightWord}[!?.]*)`, 'i')).map((part, i) =>
                      part.toLowerCase().startsWith(featured.highlightWord.toLowerCase())
                        ? <span key={i} style={{ color: '#fff', fontWeight: 700 }}>{part}</span>
                        : part
                    )
                  : featured.description}
              </p>
              <p style={{ ...styles.featuredDetailsText, ...(featured.detailsTextColor ? { color: featured.detailsTextColor } : {}) }}>
                {featured.imposter_word_rounds || featured.total_rounds} rounds
                <br />
                vs {Object.entries(featured.ai_models || {}).map(([name, count]) =>
                  count > 1 ? `${count}x ${name}` : name
                ).join(', ')}
              </p>
            </div>
            <div style={styles.featuredDetailsSection}>
              <div style={{ ...styles.featuredDetailsSectionTitle, color: 'rgba(255,255,255,0.85)' }}>
                Leaderboard
                <span style={styles.featuredDetailsSectionMeta}>
                  {totalGames || 0} game{totalGames !== 1 ? 's' : ''} &middot; {leaderboard?.length || 0} player{(leaderboard?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>
              {leaderboard && leaderboard.length > 0 ? (
                <div style={styles.leaderboardListFull}>
                  {/* Header row */}
                  <div style={styles.leaderboardHeaderRow}>
                    <span style={{ ...styles.leaderboardRankFull, ...styles.leaderboardHeaderCell }}>#</span>
                    <span style={{ ...styles.leaderboardNameFull, ...styles.leaderboardHeaderCell }}>Player</span>
                    <span style={{ ...styles.leaderboardScoreFull, ...styles.leaderboardHeaderCell }}>Score</span>
                    <span style={{ ...styles.leaderboardMetaFull, ...styles.leaderboardHeaderCell }}>W / L</span>
                  </div>
                  {leaderboard.slice(0, 10).map((entry, i) => (
                    <div key={i} style={{
                      ...styles.leaderboardRowFull,
                      backgroundColor: i < 3
                        ? ['rgba(255,215,0,0.1)', 'rgba(192,192,192,0.08)', 'rgba(205,127,50,0.08)'][i]
                        : i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                    }}>
                      <span style={styles.leaderboardRankFull}>
                        {i === 0 ? '\u{1F947}' : i === 1 ? '\u{1F948}' : i === 2 ? '\u{1F949}' : `${i + 1}.`}
                      </span>
                      <span style={styles.leaderboardNameFull}>{entry.playerName}</span>
                      <span style={styles.leaderboardScoreFull}>{entry.score} pts</span>
                      <span style={styles.leaderboardMetaFull}>{entry.wins}W / {entry.gamesPlayed - entry.wins}L</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// YouTube title cache (shared across cards, persists for session)
const ytTitleCache = {};

function useYouTubeTitle(url) {
  const [title, setTitle] = useState(null);
  useEffect(() => {
    if (!url) return;
    if (ytTitleCache[url]) { setTitle(ytTitleCache[url]); return; }
    let cancelled = false;
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.title && !cancelled) {
          ytTitleCache[url] = data.title;
          setTitle(data.title);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);
  return title;
}

// Ghost Chat: loops through messages one at a time with random 1-3s delay
const GHOST_SENDER_COLORS = [
  { bg: '#e3f2fd', border: '#90caf9', name: '#1565c0' },
  { bg: '#f3e5f5', border: '#ce93d8', name: '#7b1fa2' },
  { bg: '#e8f5e9', border: '#a5d6a7', name: '#2e7d32' },
  { bg: '#fff3e0', border: '#ffcc80', name: '#e65100' },
  { bg: '#fce4ec', border: '#f48fb1', name: '#c62828' },
  { bg: '#e0f7fa', border: '#80deea', name: '#00838f' },
  { bg: '#f9fbe7', border: '#dce775', name: '#827717' },
  { bg: '#ede7f6', border: '#b39ddb', name: '#4527a0' },
];

const getGhostSenderColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GHOST_SENDER_COLORS[Math.abs(hash) % GHOST_SENDER_COLORS.length];
};

const GhostChat = ({ messages, accentColor, playerAvatars }) => {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const indexRef = useRef(0);
  const seqRef = useRef(0);
  const containerRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!messages || messages.length === 0) return;

    // Seed with first message if we haven't started yet
    if (visibleMessages.length === 0) {
      setVisibleMessages([{ ...messages[0], _seq: 0 }]);
      indexRef.current = 1;
      seqRef.current = 1;
    }

    const schedule = () => {
      const delay = 1000 + Math.random() * 2000;
      timeoutRef.current = setTimeout(() => {
        const msgs = messagesRef.current;
        if (!msgs || msgs.length === 0) return;
        const idx = indexRef.current % msgs.length;
        indexRef.current++;
        const seq = seqRef.current++;
        setVisibleMessages(prev => {
          const next = [...prev, { ...msgs[idx], _seq: seq }];
          return next.length > 20 ? next.slice(-20) : next;
        });
        schedule();
      }, delay);
    };
    schedule();

    return () => clearTimeout(timeoutRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]); // Restart only when message count changes

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [visibleMessages]);

  if (!messages || messages.length === 0) return null;

  // Build ordered list of unique senders for alternating alignment
  const senderSides = {};
  let sideCounter = 0;
  messages.forEach(m => {
    if (m.sender && !(m.sender in senderSides)) {
      senderSides[m.sender] = sideCounter % 2 === 0 ? 'left' : 'right';
      sideCounter++;
    }
  });

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
        padding: '2px 0',
      }}
    >
      {visibleMessages.map((msg) => {
        const color = getGhostSenderColor(msg.sender);
        const isRight = senderSides[msg.sender] === 'right';
        return (
          <div key={msg._seq} style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '5px',
            justifyContent: isRight ? 'flex-end' : 'flex-start',
            flexDirection: isRight ? 'row-reverse' : 'row',
            animation: 'ghostChatFadeIn 0.35s ease-out',
          }}>
            <div style={{
              maxWidth: '78%',
              padding: '4px 9px',
              borderRadius: isRight ? '10px 10px 3px 10px' : '10px 10px 10px 3px',
              backgroundColor: color.bg,
              border: `1px solid ${color.border}40`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <div style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: color.name,
                marginBottom: '1px',
                letterSpacing: '0.02em',
              }}>
                {msg.sender}
              </div>
              <div style={{
                fontSize: '0.73rem',
                lineHeight: 1.35,
                color: '#444',
              }}>
                {(msg.message || '').slice(0, 80)}{msg.message?.length > 80 ? '...' : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Twitch-style compact viewer count formatting (1.2K, 13.4K, 1.1M, etc.)
const formatViewerCount = (n) => {
  if (n < 1000) return String(n);
  if (n < 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (n < 1000000) return Math.round(n / 1000) + 'K';
  return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
};

// Live Now Rail — Twitch-style horizontal rail with live + recommendation fill
const LIVE_RAIL_TARGET_COUNT = 5; // bot mode only — human rail is bookends + dynamic below
// Human rail dynamic slots between the pinned bookends (Nina 08-04): rail =
// Explorer · The Claude House · <these> · Lost & Found · featured game · Anxious Bean.
// (08-14: the Lost & Found pin was added without spending a dynamic slot.)
// (09-01, Nina: Anxious Bean replaced The AI Dorm in the far-bookend slot.)
const LIVE_RAIL_DYNAMIC_COUNT = 0; // curated rail (Nina 08-29): featured slots are name-keyed pins, no auto-trending picks. Set back to 2 to restore dynamic slots.
const LIVE_RAIL_TRANSITION_MS = 380;
const LIVE_RAIL_PHONE_BREAK = 700;
const LIVE_RAIL_TABLET_BREAK = 1100;
const LIVE_RAIL_SWIPE_THRESHOLD = 50;

// Returns viewport-driven layout values. Card stays cinematic 16:6 above phone,
// switches to 16:9 on phone so the live preview strip has enough vertical room.
// centerStripAspect/centerStripHeight are used only by the focused center card so
// the active card reads taller than its dimmed neighbors.
const getRailLayout = (viewportWidth) => {
  if (viewportWidth < LIVE_RAIL_PHONE_BREAK) {
    // -40 = the page container's 20px side padding ×2. The old -16 ignored it,
    // so the card overflowed the padded content box and the stage's
    // overflow:clip cut both edges (avatar/badges) on small phones
    // (Nina's 13-mini screenshot, 07-19).
    const cardW = Math.min(380, Math.max(280, viewportWidth - 40));
    return {
      mode: 'phone',
      cardWidth: cardW,
      stripAspect: 16 / 9,
      stripHeight: Math.round(cardW * 9 / 16),
      centerStripAspect: 16 / 9,
      centerStripHeight: Math.round(cardW * 9 / 16),
      // 34px on phones. This was 0, which did NOT collapse the arrows — the
      // render reads `layout.arrowSize || 32`, so 0 silently fell through to
      // 32. Stating the size here makes the phone value intentional instead of
      // an accident of a falsy fallback (change that 32 and phones would have
      // moved with it). Deliberately under the 40px desktop disc: on a mini it
      // sits over a ~335px card and must hint, not cover.
      arrowSize: 34,
      gap: 8,
    };
  }
  if (viewportWidth < LIVE_RAIL_TABLET_BREAK) {
    // cardW bounded so side cards (scale 0.75 at offset cardW+gap) actually fit
    // within the viewport: outer edge = 1.375*cardW + gap ≤ viewportWidth/2.
    const cardW = Math.min(420, Math.max(260, Math.round((viewportWidth - 32) / 2.75)));
    return {
      mode: 'tablet',
      cardWidth: cardW,
      stripAspect: 16 / 6,
      stripHeight: Math.round(cardW * 6 / 16),
      centerStripAspect: 16 / 8,
      centerStripHeight: Math.round(cardW * 8 / 16),
      arrowSize: 36,
      gap: 8,
    };
  }
  // Desktop clip box is the 1200px page column (padding 20×2), NOT the viewport —
  // side cards live at 1.275*cardW + gap from stage center (scale 0.75, 0.1 inward
  // nudge), so unbounded cardW overshoots the stage at fullscreen and overflow:clip
  // amputates ~90px per side. Same fit rule as the tablet branch, container-capped.
  const stageW = Math.min(viewportWidth, 1200) - 40;
  const cardW = Math.min(520, Math.max(380, Math.round(Math.min(
    (viewportWidth - 32) / 2.75,
    (stageW / 2 - 8) / 1.275
  ))));
  return {
    mode: 'desktop',
    cardWidth: cardW,
    stripAspect: 16 / 6,
    stripHeight: Math.round(cardW * 6 / 16),
    centerStripAspect: 16 / 8,
    centerStripHeight: Math.round(cardW * 8 / 16),
    arrowSize: 40,
    gap: 8,
  };
};

// ─── Featured game rail card — the weekly competition, pinned after the
//     explorer tile with a corner ribbon. Clicking the (center) tile spins up
//     a fresh competition via the page's handleJoinCompetition. ──────────────
const FeaturedGameRailCard = ({ featured, stripHeight, isCenter, joining, isPhone = false }) => {
  const accent = '#a970ff';
  const hype = Array.isArray(featured?.hype_messages) ? featured.hype_messages : [];
  // Rotating quote ticker — real spicy lines from this week's games
  const [hypeIdx, setHypeIdx] = useState(0);
  useEffect(() => {
    if (hype.length < 2 || !isCenter) return;
    const t = setInterval(() => setHypeIdx(i => (i + 1) % hype.length), 3800);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hype.length, isCenter]);
  if (!featured) return null;
  const quote = hype.length ? hype[hypeIdx % hype.length] : null;
  // Whodunnit falls back to the pre-generated noir crime scene — the card's
  // existing art plumbing (dim gradient, no-invert, saturate) does the rest.
  const art = featured.card_image_url || (isWhodunnitCard(featured.name) ? NOIR_RAIL_ART : null);
  return (
    // data-no-invert: this card is self-branded (dark purple + gold + dark art)
    // and must not be flipped by the page's dark-mode invert filter. The art
    // <img>'s inline saturate() filter is load-bearing here — it overrides the
    // global `.explore-dark-mode img` counter-invert, which inside this
    // already-counter-inverted card would flip the art to a white negative.
    <div data-no-invert="" style={{
      width: '100%', borderRadius: 14, overflow: 'hidden', position: 'relative',
      background: 'linear-gradient(160deg, #2a1b4d 0%, #140a24 100%)',
      border: `1px solid ${accent}55`,
      // Phone: no outer shadow — the stage clips it into a hard-edged
      // rectangle (see LiveRailCard's isPhone note)
      boxShadow: isPhone ? 'none' : isCenter ? `0 6px 24px ${accent}30` : '0 4px 14px rgba(0,0,0,0.35)',
    }}>
      {/* Corner ribbon */}
      <div style={{
        position: 'absolute', top: 16, left: -34, transform: 'rotate(-45deg)',
        background: '#ffd54f', color: '#1a1a1a', padding: '3px 40px',
        fontWeight: 800, fontSize: '0.6rem', letterSpacing: '1.5px', zIndex: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      }}>
        ⭐ FEATURED
      </div>
      <div style={{
        height: stripHeight, position: 'relative', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 18px',
        textAlign: 'center', overflow: 'hidden',
      }}>
        {/* Real banner art from a recent featured game, dimmed for legibility */}
        {art && (
          <>
            <img src={art} alt="" aria-hidden="true" style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', filter: 'saturate(1.05)',
            }} />
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(10,5,20,0.35) 0%, rgba(10,5,20,0.72) 78%, rgba(10,5,20,0.9) 100%)',
            }} />
          </>
        )}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          {!art && <div style={{ fontSize: '2.4em', lineHeight: 1 }}>{featured.emoji || '🎮'}</div>}
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem', letterSpacing: '0.4px', textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
            {featured.name}
          </div>
          {quote ? (
            /* key swap re-runs the fade for each rotation */
            <div key={hypeIdx} style={{
              color: 'rgba(255,255,255,0.85)', fontSize: '0.74rem', lineHeight: 1.4, maxWidth: 350,
              fontStyle: 'italic', textShadow: '0 1px 5px rgba(0,0,0,0.8)',
              animation: 'featuredHypeIn 0.45s ease',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              "{quote.message}"{quote.sender ? <span style={{ opacity: 0.7, fontStyle: 'normal' }}> · {quote.sender}</span> : null}
            </div>
          ) : isWhodunnitCard(featured.name) ? (
            <div style={{
              ...NOIR_HEADLINE_STYLE,
              color: '#f3e9d2', fontSize: '0.92rem', maxWidth: 340,
              textShadow: '0 2px 8px rgba(0,0,0,0.85)',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {whodunnitHeadline()}
            </div>
          ) : featured.objective ? (
            <div style={{
              color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', lineHeight: 1.4, maxWidth: 340,
              textShadow: art ? '0 1px 5px rgba(0,0,0,0.8)' : 'none',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {featured.objective}
            </div>
          ) : null}
        </div>
        <style>{`@keyframes featuredHypeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
      </div>
      <div style={{
        padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderTop: `1px solid ${accent}30`, background: 'rgba(0,0,0,0.25)',
      }}>
        <span style={{
          padding: '7px 22px', borderRadius: 999, background: joining ? `${accent}55` : accent,
          color: '#fff', fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.4px',
          boxShadow: joining ? 'none' : `0 3px 14px ${accent}55`,
        }}>
          {joining ? 'Setting up…' : 'Play'}
        </span>
      </div>
    </div>
  );
};

const LiveNowRail = ({ spaces, viewerCounts, liveGames, liveStreams = {}, watchParties = {}, onJoin, explorerStatus, explorerUnreachable = false, directTileNav, featuredGame = null, featuredCompetition = null, onJoinFeatured = null, joiningFeatured = false }) => {
  // Effective human-stream list per space: live WS updates win, the HTTP
  // response's livestream field seeds the page-load state.
  const streamersFor = (space) => liveStreams[space.id] ?? space.livestream?.streamers ?? [];
  // Same for parties: a WS entry (including an explicit null = "party ended")
  // beats the load-time space.watch_party seed.
  const partyFor = (space) => (space.id in watchParties) ? watchParties[space.id] : (space.watch_party || null);
  // Sort all spaces: live first, then by viewer count, then by likes.
  // Live spaces always show; the rest fill up to TARGET so the rail feels populated.
  // Claude the Explorer is always pinned to position 0 for human visitors;
  // the sort below ranks the rest, then the bookend assembly at the bottom
  // picks the dynamic slots (see the 08-04 editorial-bookends block).
  // When directTileNav is true (Claude the Explorer's own ?bot=1 session),
  // the explorer tile is hidden entirely — otherwise his iframe would show
  // his desktop, which itself shows the page with his iframe… infinite recursion.
  const railSpaces = useMemo(() => {
    const explorerEntry = {
      id: EXPLORER_SENTINEL_ID,
      _isExplorer: true,
      name: 'Claude the Explorer',
    };
    // Featured game of the week — its own pinned card right after the explorer
    // (user call 07-10). Hidden in bot mode: the Explorer must not spin up
    // competitions from a tile click.
    // LIVE TAKEOVER: when someone is actually live in a featured-game space,
    // the slot becomes that space's REAL rail card (video, viewers, join-on-
    // click) with a FEATURED badge — most streamers, then most viewers, wins.
    const liveFeatured = (featuredCompetition && !directTileNav && spaces?.length)
      ? [...spaces]
          .filter(s => s.is_featured_competition && streamersFor(s).length > 0)
          .sort((a, b) =>
            (streamersFor(b).length - streamersFor(a).length)
            || ((viewerCounts[b.id] || 0) - (viewerCounts[a.id] || 0)))[0] || null
      : null;
    const featuredEntry = (featuredCompetition && !directTileNav)
      ? (liveFeatured
        ? { ...liveFeatured, _isFeaturedLive: true }
        : { id: '__featured-game__', _isFeaturedGame: true, name: featuredCompetition.name })
      : null;
    if (!spaces || spaces.length === 0) {
      return directTileNav ? [] : [explorerEntry, ...(featuredEntry ? [featuredEntry] : [])];
    }
    const sorted = [...spaces].sort((a, b) => {
      // Human webcam/screen streams outrank plain "someone is playing" live
      const aStream = streamersFor(a).length > 0 ? 1 : 0;
      const bStream = streamersFor(b).length > 0 ? 1 : 0;
      if (aStream !== bStream) return bStream - aStream;
      // 🍿 Watch parties are events — above plain live, below human streams
      const aParty = partyFor(a) ? 1 : 0;
      const bParty = partyFor(b) ? 1 : 0;
      if (aParty !== bParty) return bParty - aParty;
      const aLive = liveGames[a.id] ? 1 : 0;
      const bLive = liveGames[b.id] ? 1 : 0;
      if (aLive !== bLive) return bLive - aLive;
      const aViewers = viewerCounts[a.id] || 0;
      const bViewers = viewerCounts[b.id] || 0;
      if (aViewers !== bViewers) return bViewers - aViewers;
      // Quiet-state fallback = NEWEST first (Nina 07-31): with zero live
      // signal the old likes-first fallback let two ancient 1-like spaces own
      // the hero rail over the whole curated batch. Recency = editorial
      // control (curation is creation order); likes only break exact ties.
      const aT = new Date(a.shared_at || a.created_at || 0).getTime();
      const bT = new Date(b.shared_at || b.created_at || 0).getTime();
      if (aT !== bT) return bT - aT;
      return (b.likes_count || 0) - (a.likes_count || 0);
    });
    if (directTileNav) {
      // Bot mode — no explorer tile
      return sorted.slice(0, LIVE_RAIL_TARGET_COUNT);
    }
    // 📌 Editorial bookends v2 (Nina 08-06, supersedes the 08-04 order):
    // the wrap carousel reads <far bookend> ← EXPLORER → Claude House — the
    // house sits immediately RIGHT of the explorer tile (v2 house launch
    // day placement), the far bookend is the last card = one swipe left of
    // position 0 via the wrap clone. Between them: the dynamic slots
    // (live/trending sort above), then the featured game. Name-keyed on
    // purpose — survives regeneration; a missing/unshared name just drops
    // out of the rail.
    // 🫘 09-01 (Nina): Anxious Bean REPLACED The AI Dorm in the far-bookend
    // slot (name verified against listSharedSpaces on dev — exact-name
    // pinning, the 08-14 ampersand lesson).
    const farBookend = sorted.find(s => s.name === 'Anxious Bean');
    const housePick = sorted.find(s => s.name === 'The Claude House');
    // 🌀 Interdimensional Lost & Found (Nina 08-14) — added as a pin, nothing
    // replaced, so the rail grows to 6 and the dynamic count stays at 2.
    // ⚠️ The name is AMPERSAND, verified against listSharedSpaces on dev
    // (id 5df0faff). Spelling it "and" compiles clean and silently pins
    // nothing — that is the whole failure mode of name-keyed pinning.
    // Slotted BETWEEN the dynamic run and the featured game on purpose: the
    // explorer/house/far-bookend positions encode the 08-06 wrap-carousel
    // decision (house immediately right of explorer, the far bookend one swipe
    // left of position 0 — the dorm then, Anxious Bean since 09-01), and this
    // is the only insertion point that leaves all three where that decision
    // put them.
    const lostAndFound = sorted.find(s => s.name === 'The Interdimensional Lost & Found');
    // 🎡 Curated editorial pins (Nina 08-29): NYAN NYAN NYAN + ROTTEN MANGO
    // hold the two featured slots, replacing the dynamic auto-picks (which were
    // surfacing "What am I?" / "The Void"). Name-keyed to the EXACT shared-space
    // titles ("nyan cat" / "rotten mango" are the informal names) — a rename or
    // unshare silently drops the pin, the whole failure mode of name-keying.
    // (The Prompt Pals pin was removed here: that space was unshared 08-29.)
    const nyan = sorted.find(s => s.name === 'NYAN NYAN NYAN');
    const mango = sorted.find(s => s.name === 'ROTTEN MANGO');
    // Pinned cards (and a live-takeover featured space) must not ALSO fill
    // a dynamic slot
    const pinnedIds = new Set([farBookend?.id, housePick?.id, lostAndFound?.id, nyan?.id, mango?.id, featuredEntry?.id].filter(Boolean));
    // 🪟 Ported 1:1 companion spaces never fill a LIVE rail slot (Nina 08-28):
    // a private "just you and your resident" room is not a public watch-live
    // showcase. `porch` rides the listing ONLY for ported rows (home_agent_id),
    // so it is the ported discriminator here. They still appear in Browse with
    // the porch postcard back — the softer "someone lives here, knock first".
    const dynamic = sorted.filter(s => !pinnedIds.has(s.id) && !s.porch).slice(0, LIVE_RAIL_DYNAMIC_COUNT);
    return [
      explorerEntry,
      ...(housePick ? [housePick] : []),
      ...(nyan ? [nyan] : []),
      ...(mango ? [mango] : []),
      ...dynamic,
      ...(lostAndFound ? [lostAndFound] : []),
      ...(featuredEntry ? [featuredEntry] : []),
      ...(farBookend ? [farBookend] : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, viewerCounts, liveGames, liveStreams, watchParties, directTileNav, featuredCompetition]);

  // Spaces whose cards get a REAL video connection (bubble facecam) — every
  // live card up to LIVE_RAIL_MAX_VIDEO regardless of carousel position, in
  // rail order (live cards sort to the front). Bot mode gets none.
  const liveVideoIds = useMemo(() => {
    const ids = new Set();
    // Low-resource phones get the avatar bubble on every live card — three
    // concurrent WebRTC video decoders is a real chunk of an iPhone's per-tab
    // memory budget (07-19 crash fix).
    if (directTileNav || isLowResourceDevice) return ids;
    for (const s of railSpaces) {
      if (s._isExplorer || streamersFor(s).length === 0) continue;
      ids.add(s.id);
      if (ids.size >= LIVE_RAIL_MAX_VIDEO) break;
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railSpaces, liveStreams, directTileNav]);

  const liveCount = useMemo(
    () => railSpaces.filter(s => liveGames[s.id]).length,
    [railSpaces, liveGames]
  );
  const totalViewers = useMemo(
    () => railSpaces.reduce((sum, s) => sum + (viewerCounts[s.id] || 0), 0),
    [railSpaces, viewerCounts]
  );

  // Viewport-driven layout: card width, strip aspect, arrow size all flow from this.
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const layout = useMemo(() => getRailLayout(viewport), [viewport]);
  const isPhone = layout.mode === 'phone';
  // 'phone' is a width breakpoint, not a device check — a narrow desktop window
  // lands in it too, and without touch there was NO way to flip cards. Keep the
  // arrows whenever the pointer is a mouse/trackpad, regardless of width.
  const hasFinePointer = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
    : true;
  // ⭐ ARROWS ON PHONES TOO (Nina 08-14, on an iPhone mini: "I can't even see
  // that there's a way to move to another live rail card").
  //
  // They used to be desktop-only on the reasoning that touch users swipe. The
  // flaw: swipe is INVISIBLE. Nothing on a phone said the rail was a carousel,
  // so the first card read as the whole feature and everything past the
  // explorer tile was undiscoverable. An affordance a user has to guess at is
  // not an affordance. Touch swipe still works exactly as before; the arrows
  // are now the visible hint that it is there at all.
  const showArrows = true;

  // Sliding carousel: render all cards in an absolutely-positioned track that
  // translates horizontally. activeIndex can briefly hold a "virtual" value
  // (-1 or total) during a wrap, then snaps back without animation.
  const total = railSpaces.length;
  const wrap = total >= 3; // need at least 3 to make a loop visually meaningful
  const [activeIndex, setActiveIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const isSnappingRef = useRef(false);
  const swipeStartRef = useRef(null);

  // Reset if the rail shrinks below the current index
  useEffect(() => {
    if (total > 0 && activeIndex > total - 1) {
      setTransitionEnabled(false);
      setActiveIndex(0);
      requestAnimationFrame(() => requestAnimationFrame(() => setTransitionEnabled(true)));
    }
  }, [total, activeIndex]);

  // Snap-back: when activeIndex is at a virtual position, animate first,
  // then jump (no transition) to the equivalent real index.
  useEffect(() => {
    if (!wrap) return;
    if (activeIndex !== -1 && activeIndex !== total) return;
    isSnappingRef.current = true;
    const handle = setTimeout(() => {
      setTransitionEnabled(false);
      setActiveIndex(activeIndex === -1 ? total - 1 : 0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
          isSnappingRef.current = false;
        });
      });
    }, LIVE_RAIL_TRANSITION_MS + 20);
    return () => {
      clearTimeout(handle);
      isSnappingRef.current = false;
    };
  }, [activeIndex, total, wrap]);

  const goPrev = useCallback(() => {
    if (isSnappingRef.current) return;
    if (wrap) setActiveIndex(i => i - 1);
    else setActiveIndex(i => Math.max(0, i - 1));
  }, [wrap]);
  const goNext = useCallback(() => {
    if (isSnappingRef.current) return;
    if (wrap) setActiveIndex(i => i + 1);
    else setActiveIndex(i => Math.min(total - 1, i + 1));
  }, [wrap, total]);

  const canPrev = wrap || activeIndex > 0;
  const canNext = wrap || activeIndex < total - 1;

  // Desktop trackpad / shift+wheel horizontal scroll → carousel nav.
  // React's onWheel is passive by default, so we attach a native listener on
  // a ref to allow preventDefault (otherwise macOS hijacks horizontal swipe
  // for back-navigation). Accumulate deltaX past a threshold to fire one
  // step per gesture, then throttle for the slide duration.
  // Declared above the early return so hook order stays stable.
  const stageRef = useRef(null);
  const wheelAccumRef = useRef(0);
  const wheelLockRef = useRef(false);
  // Measured height of the CENTERED slot (ResizeObserver, container-width
  // mandate 07-12). The explorer card renders a 16:9 strip + pet-screen
  // chrome — taller than the 16:8 centerStripHeight math the stage minHeight
  // trusts. The old overflow pair computed back to visible so the excess
  // just hung out the bottom; the 07-13 `overflow: clip` fix made the clip
  // REAL and cut the card off (user report). Measure the actual centered
  // card and let the stage grow to fit it instead of stacking more
  // per-card-type height constants.
  const centerSlotRef = useRef(null);
  const [centerCardH, setCenterCardH] = useState(0);
  useEffect(() => {
    const node = centerSlotRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const h = Math.ceil(entries[0]?.contentRect?.height || 0);
      // 1px hysteresis — subpixel churn from the scale transition shouldn't
      // re-render the stage every frame.
      setCenterCardH((prev) => (Math.abs(prev - h) > 1 ? h : prev));
    });
    ro.observe(node);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, railSpaces]);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || isPhone) return;
    const THRESHOLD = 40;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical scroll → let it pass
      e.preventDefault();
      if (wheelLockRef.current || isSnappingRef.current) return;
      wheelAccumRef.current += e.deltaX;
      if (Math.abs(wheelAccumRef.current) < THRESHOLD) return;
      if (wheelAccumRef.current > 0) goNext(); else goPrev();
      wheelAccumRef.current = 0;
      wheelLockRef.current = true;
      setTimeout(() => { wheelLockRef.current = false; }, LIVE_RAIL_TRANSITION_MS);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isPhone, goNext, goPrev]);

  // Wrap flipping ON (spaces arriving post-mount) prepends a clone slot,
  // which shifts the track transform by one full STEP in the same commit —
  // with the 0.38s transition live, that painted as the whole rail sliding
  // in from the right on page load (Nina 07-20, probe-verified: transform
  // -167.5 → -510.5 animated while slots went 2 → 7). The active card
  // didn't logically move, only its coordinates did — snap, don't slide.
  const prevWrapRef = useRef(wrap);
  // useLayoutEffect, not useEffect: a plain effect runs AFTER paint, so one
  // animated frame escaped (~34px blip, probe-verified). Layout effects run
  // pre-paint — the snap is invisible.
  useLayoutEffect(() => {
    if (prevWrapRef.current === wrap) return;
    prevWrapRef.current = wrap;
    setTransitionEnabled(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setTransitionEnabled(true)));
  }, [wrap]);

  // Open the carousel on a live human stream at page load, so the real video
  // preview (center card only) is front and center. One-shot on the first
  // render with real spaces — never fights the user's navigation afterwards,
  // and later go-lives only pulse the badge/glow instead of yanking the rail.
  const autoFocusDoneRef = useRef(false);
  useEffect(() => {
    if (autoFocusDoneRef.current || !spaces || spaces.length === 0) return;
    autoFocusDoneRef.current = true;
    const liveIdx = railSpaces.findIndex(s => !s._isExplorer && streamersFor(s).length > 0);
    if (liveIdx > 0) {
      setTransitionEnabled(false);
      setActiveIndex(liveIdx);
      requestAnimationFrame(() => requestAnimationFrame(() => setTransitionEnabled(true)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, railSpaces]);

  if (total === 0) return null;

  const title = 'live & trending';
  const subtitle = liveCount > 0
    ? `${liveCount} live${totalViewers > 0 ? ` · ${formatViewerCount(totalViewers)} watching` : ''}`
    : null;

  // Build the track: when wrap is on, prepend a clone of the last and append a clone of the first
  // so the user always has a card sliding into the visible area on the wrap side.
  const trackCards = wrap
    ? [
        { space: railSpaces[total - 1], logical: -1, key: 'wrap-start' },
        ...railSpaces.map((s, i) => ({ space: s, logical: i, key: `o-${s.id}` })),
        { space: railSpaces[0], logical: total, key: 'wrap-end' },
      ]
    : railSpaces.map((s, i) => ({ space: s, logical: i, key: `o-${s.id}` }));

  // Each card's layout slot is CARD_W wide; gap between is LIVE_RAIL_GAP.
  // To center the active card, translate the track so the active slot's center
  // aligns with the stage's horizontal center (left:50% + translateX of half-width).
  const CARD_W = layout.cardWidth;
  const GAP = layout.gap;
  const STEP = CARD_W + GAP;
  // logicalToOffsetIndex: 0-based DOM index for given logical position
  const offsetIndex = wrap ? activeIndex + 1 : activeIndex;
  const trackTransform = `translate(calc(-${CARD_W / 2}px - ${offsetIndex * STEP}px), -50%)`;

  // Touch swipe: simple threshold-based pointer-down/up. Real-time drag would feel
  // smoother but adds complexity; threshold is enough for "feels right" on phone.
  const onTouchStart = (e) => {
    swipeStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < LIVE_RAIL_SWIPE_THRESHOLD) return;
    if (Math.abs(dy) > Math.abs(dx)) return; // vertical scroll wins
    if (dx < 0) goNext(); else goPrev();
  };

  return (
    <div
      className="live-rail-wrap"
      style={{
        ...styles.liveRailContainer,
        // Phone: the desktop 90px section gap reads as a dead band between
        // the dots row and BROWSE (Nina mini screenshot 07-20; probe showed
        // the stage/RO measurement is NOT the source — the margin is).
        ...(isPhone ? { marginBottom: '28px' } : null),
      }}
    >
      <div style={styles.liveRailHeader}>
        <div style={styles.liveRailHeaderLeft}>
          <span style={styles.liveRailHeaderChip} aria-label="Live">
            <span style={styles.liveRailHeaderChipDot} aria-hidden />
          </span>
          <h2 style={styles.liveRailTitle}>{title}</h2>
          {subtitle && <span style={styles.liveRailSubtitle}>{subtitle}</span>}
        </div>
      </div>

      <div
        ref={stageRef}
        className="live-rail-stage"
        style={{
          ...styles.liveRailStage,
          // Grow to the measured centered card when known (see centerSlotRef
          // note above) — the +60 strip-math fallback covers first paint.
          minHeight: `${Math.max(layout.centerStripHeight + 60, centerCardH ? centerCardH + 28 : 0)}px`,
          // On phone, claim horizontal touch gestures so the browser doesn't pan
          // the page in parallel (was causing tab crashes on iOS Safari).
          // Desktop/tablet clipping lives in liveRailStage base styles
          // (overflow: clip — see the note there re: the clip/visible pair).
          ...(isPhone ? { overflow: 'hidden', overflowX: 'hidden', overflowY: 'hidden', touchAction: 'pan-y' } : null),
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {showArrows && (
          <button
            aria-label="Previous"
            onClick={goPrev}
            className="live-rail-arrow"
            style={{
              ...styles.liveRailArrow,
              ...styles.liveRailArrowLeft,
              width: `${layout.arrowSize || 32}px`,
              height: `${layout.arrowSize || 32}px`,
              opacity: canPrev ? 1 : 0.25,
              pointerEvents: canPrev ? 'auto' : 'none',
            }}
          >
            <span style={styles.liveRailArrowGlyph}>‹</span>
          </button>
        )}

        <div
          style={{
            ...styles.liveRailTrack,
            gap: `${layout.gap}px`,
            transform: trackTransform,
            transition: transitionEnabled
              ? `transform ${LIVE_RAIL_TRANSITION_MS}ms cubic-bezier(0.2,0.7,0.3,1)`
              : 'none',
          }}
        >
          {trackCards.map(({ space, logical, key }) => {
            const dist = Math.abs(logical - activeIndex);
            const isCenter = dist === 0;
            // On phone, neighbors are fully hidden — only the active card reads.
            const isVisible = isPhone ? dist === 0 : dist <= 1;
            return (
              <div
                key={key}
                ref={isCenter ? centerSlotRef : null}
                style={{
                  ...styles.liveRailSlot,
                  width: `${CARD_W}px`,
                  transform: isCenter
                    ? 'scale(1)'
                    : `translateX(${(logical < activeIndex ? 1 : -1) * Math.round(CARD_W * 0.1)}px) scale(0.75)`,
                  opacity: isVisible ? (isCenter ? 1 : 0.5) : 0,
                  filter: isCenter ? 'none' : 'saturate(0.7)',
                  pointerEvents: isVisible ? 'auto' : 'none',
                  cursor: 'pointer',
                  transition: transitionEnabled
                    ? `transform ${LIVE_RAIL_TRANSITION_MS}ms cubic-bezier(0.2,0.7,0.3,1), opacity ${LIVE_RAIL_TRANSITION_MS}ms ease, filter ${LIVE_RAIL_TRANSITION_MS}ms ease`
                    : 'none',
                  zIndex: isCenter ? 5 : 1,
                }}
                onClick={() => {
                  // Explorer tile click: side clicks rotate the carousel like
                  // every other tile (07-22: the off-center chibi card was the
                  // only rail card that ignored a side tap). Center click: if
                  // live, open the livestream viewer; otherwise a soft no-op
                  // (countdown on the tile already communicates "not live
                  // yet"). Bot mode keeps the old semantics — rotation is
                  // exactly what directTileNav exists to bypass, and the
                  // Explorer must not open its own viewer (recursive trap).
                  if (space._isExplorer) {
                    if (!isCenter && !directTileNav) {
                      if (logical < activeIndex) goPrev(); else goNext();
                      return;
                    }
                    if (explorerStatus?.is_live && explorerStatus?.agent_url) {
                      // Pass ?thoughts= so the viewer page can overlay
                      // caption-style prose under the live HLS stream. Viewer
                      // gates the caption layer entirely on this param.
                      const thoughtsParam = encodeURIComponent(`${EXPLORER_API_BASE_URL}/explorer/thoughts`);
                      // Viewer-side chat params — only attached when the live
                      // status response has agent_id. Older deploys without
                      // agent_id in /explorer/status simply degrade to no chat
                      // (the viewer.html chat IIFE is gated on both params).
                      const chatParams = explorerStatus?.agent_id
                        ? `&chat=${encodeURIComponent(EXPLORER_CHAT_BASE_URL)}&agent_id=${encodeURIComponent(explorerStatus.agent_id)}`
                        : '';
                      const viewerUrl = `${explorerStatus.agent_url.replace(/\/$/, '')}/viewer?thoughts=${thoughtsParam}${chatParams}`;
                      window.open(viewerUrl, '_blank');
                    }
                    return;
                  }
                  // Featured game tile: center click starts a fresh competition
                  // (same flow as the old banner's Play button); side click rotates.
                  if (space._isFeaturedGame) {
                    if (isCenter) {
                      if (!joiningFeatured && onJoinFeatured) onJoinFeatured();
                    } else if (logical < activeIndex) goPrev();
                    else goNext();
                    return;
                  }
                  // Direct-tile-nav mode (used by Claude the Explorer with ?bot=1):
                  // any tile click navigates directly. Bypasses the human-friendly
                  // carousel rotation behavior so automated agents don't get stuck
                  // rotating instead of joining.
                  if (directTileNav) {
                    onJoin(space);
                    return;
                  }
                  if (isCenter) {
                    onJoin(space);
                  } else if (logical < activeIndex) goPrev();
                  else goNext();
                }}
              >
                {space._isFeaturedGame ? (
                  <FeaturedGameRailCard
                    featured={featuredCompetition}
                    stripHeight={isCenter ? layout.centerStripHeight : layout.stripHeight}
                    isCenter={isCenter}
                    joining={joiningFeatured}
                    isPhone={isPhone}
                  />
                ) : space._isExplorer ? (
                  <ClaudeExplorerRailCard
                    explorerStatus={explorerStatus}
                    statusUnreachable={explorerUnreachable}
                    cardWidth={CARD_W}
                    // Explorer center card gets a taller 16:9 video preview
                    // (other centered tiles stay 16:8). Side-explorer falls
                    // back to the carousel default so neighboring tiles align.
                    stripAspect={isCenter ? 16 / 9 : layout.stripAspect}
                    stripHeight={isCenter ? Math.round(CARD_W * 9 / 16) : layout.stripHeight}
                    isCenter={isCenter}
                    isPhone={isPhone}
                  />
                ) : (
                  // Cycle 6: anchor wrap for enumeration only. preventDefault
                  // means the anchor never navigates via click — the slot div's
                  // onClick above keeps doing all the work (carousel rotation
                  // for side tiles, onJoin for center / directTileNav). The
                  // anchor's href + data-space-name are read by the
                  // navigate_to_space MCP tool (CDP Page.navigate bypasses
                  // click events). tabIndex=-1 keeps keyboard tab order
                  // unchanged. Skipped entirely for the self-explorer tile
                  // (different click semantics — opens viewer in new tab).
                  <a
                    href={`/game-step?gameId=${encodeURIComponent(space.id)}`}
                    data-space-name={space.name}
                    onClick={(e) => e.preventDefault()}
                    style={{ display: 'block', textDecoration: 'none', color: 'inherit', position: 'relative' }}
                    tabIndex={-1}
                  >
                    {/* Live takeover: this real live space IS this week's featured
                        game — badge it (top-right; LIVE pill owns top-left) */}
                    {space._isFeaturedLive && (
                      <div style={{
                        position: 'absolute', top: 8, right: 8, zIndex: 3, pointerEvents: 'none',
                        background: '#ffd54f', color: '#1a1a1a', padding: '3px 10px', borderRadius: 999,
                        fontWeight: 800, fontSize: '0.6rem', letterSpacing: '1px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                      }}>
                        ⭐ FEATURED
                      </div>
                    )}
                    {/* 🏠🫙 House till jar (Claude House only — house_till rides
                        listSharedSpaces for autonomous spaces, null elsewhere).
                        08-14: sprite skin approved off the TillJar contact
                        sheet (Nina: 60px) — the minted 5-state jar hangs off
                        the card's top-right the way the mock shipped it;
                        the numbers moved to the hover tooltip. 🪙 skin = house
                        FICTION coins; the ⚡ real-till skin arrives at Phase 2
                        on every card. Motes animate on the center card only. */}
                    {space.house_till && (() => {
                      const till = Math.max(0, Number(space.house_till.till) || 0);
                      const goal = space.house_till.goal || 40;
                      const frac = Math.min(1, till / goal);
                      return (
                        <div
                          title={`House till: ${till}/${goal} 🪙 · the locals are saving up for their next build`}
                          style={{
                            position: 'absolute', top: 0, right: 0, zIndex: 4,
                            transform: 'translate(26%,-32%) rotate(7deg)',
                            filter: 'drop-shadow(0 5px 7px rgba(0,0,0,0.45))',
                            pointerEvents: 'auto',
                          }}
                        >
                          <TillJar sprite fill={frac} glyph="🪙" animate={isCenter} size={60} />
                          {/* Touch has no hover: the tooltip numbers are
                              unreachable on phones, so give them a tiny chip
                              (dark-pill idiom) under the jar (Nina 08-14). */}
                          {isPhone && (
                            <div style={{
                              position: 'absolute', left: '50%', bottom: -6,
                              transform: 'translateX(-50%) rotate(-7deg)',
                              background: 'rgba(20,16,8,0.82)', color: '#ffd76a',
                              fontSize: 10, fontWeight: 700, lineHeight: 1,
                              padding: '3px 7px', borderRadius: 999,
                              whiteSpace: 'nowrap', pointerEvents: 'none',
                            }}>
                              {till}/{goal} 🪙
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <LiveRailCard
                      space={space}
                      viewerCount={viewerCounts[space.id] || 0}
                      isLive={streamersFor(space).length > 0 || !!liveGames[space.id] || (viewerCounts[space.id] || 0) > 0}
                      liveStreamers={streamersFor(space)}
                      watchParty={partyFor(space)}
                      isCenter={isCenter}
                      animate={isCenter}
                      cardWidth={CARD_W}
                      isPhone={isPhone}
                      stripAspect={isCenter ? layout.centerStripAspect : layout.stripAspect}
                      stripHeight={isCenter ? layout.centerStripHeight : layout.stripHeight}
                      onJoin={() => onJoin(space)}
                      // Real video on every live card (capped set), but ONLY
                      // on the real copy — the wrap-around clones would join
                      // the same room with the same identity and LiveKit
                      // evicts duplicates. Never in bot mode (the Explorer
                      // must not join LiveKit — recursive self-view trap).
                      // Low-resource phones: ONE decoder, center card only (Nina
                      // 07-29 walk-back of the 07-19 all-off crash fix — retest
                      // on a mini before widening past the single center video)
                      showVideoPreview={(liveVideoIds.has(space.id)
                        || (isLowResourceDevice && !directTileNav && isCenter
                            && !space._isExplorer && streamersFor(space).length > 0))
                        && logical >= 0 && logical < total}
                      featuredGame={featuredGame}
                    />
                    <span style={{
                      position: 'absolute',
                      width: 1, height: 1, padding: 0, margin: -1,
                      overflow: 'hidden', clip: 'rect(0,0,0,0)',
                      whiteSpace: 'nowrap', border: 0,
                    }}>{` ${space.name}`}</span>
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {showArrows && (
          <button
            aria-label="Next"
            onClick={goNext}
            className="live-rail-arrow"
            style={{
              ...styles.liveRailArrow,
              ...styles.liveRailArrowRight,
              width: `${layout.arrowSize || 32}px`,
              height: `${layout.arrowSize || 32}px`,
              opacity: canNext ? 1 : 0.25,
              pointerEvents: canNext ? 'auto' : 'none',
            }}
          >
            <span style={styles.liveRailArrowGlyph}>›</span>
          </button>
        )}
      </div>

      {isPhone && total > 1 && (
        <div style={styles.liveRailDots} role="tablist" aria-label="Channels">
          {railSpaces.map((s, i) => {
            const active = (((activeIndex % total) + total) % total) === i;
            return (
              <button
                key={s.id}
                role="tab"
                aria-selected={active}
                aria-label={`Go to ${s.name}`}
                onClick={() => {
                  setTransitionEnabled(true);
                  setActiveIndex(i);
                }}
                style={{
                  ...styles.liveRailDot,
                  ...(active ? styles.liveRailDotActive : null),
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Individual Twitch-style card inside the Live Now rail
// Strip dimensions are coupled to liveRailCard width and liveRailThumbWrap aspectRatio.
// Card width 520, aspect 16:6 → strip height 195. Avatar 44 with center on the boundary
// → top offset = 195 - 22 = 173.
const LIVE_RAIL_STRIP_H = 195;
const LIVE_RAIL_AVATAR = 44;

// LivePreviewStrip (ghost chat bubbles + rising reactions) moved to
// components/LivePreviewStrip.js on 08-15 so the /explore swipe deck can mount
// the same simulation instead of growing a second one. Its keyframes and styles
// travelled with it — the liveRailBubbleFall / liveRailReactionRise keyframes
// and the liveRailPreview* style keys that used to live in this file are gone
// with it, not orphaned.

// Distinct LiveKit identity for the rail preview. Suffixed so entering the
// space afterwards (which joins as plain lk_viewer_id) doesn't collide —
// LiveKit evicts duplicate identities.
const railPeekIdentity = () => {
  try {
    let id = localStorage.getItem('lk_viewer_id');
    if (!id) {
      id = 'viewer-' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('lk_viewer_id', id);
    }
    return id + '-rail';
  } catch (e) {
    return 'viewer-' + Math.random().toString(36).slice(2, 10) + '-rail';
  }
};

// How many rail cards may hold a real LiveKit video connection at once.
// Cards past the cap (and the carousel's wrap-around clones) still render the
// live bubble — just with the host avatar instead of video, at zero cost.
const LIVE_RAIL_MAX_VIDEO = 3;

// 🛡️ Blur consent gate on rail live previews — OFF for now (Nina 07-18);
// flip to true to restore the tap-to-peek blur. The in-space stage gate
// (LivestreamPanel) is separate and still on.
const LIVE_RAIL_BLUR_GATE = false;

// Real live video inside a rail card's bubble. Deliberately cheap: at most
// LIVE_RAIL_MAX_VIDEO mount at once (live cards only, never bot mode), each
// asks for a subscribe-only token, subscribes to VIDEO tracks only (the
// preview is muted, so audio packets would be pure waste), and adaptiveStream
// matches the feed to the element — the small bubble pulls the lowest
// simulcast layer, not full-res. Disconnects entirely while the tab is
// hidden, so a backgrounded /explore costs the LiveKit server nothing.
const LiveStreamRailPreview = ({ spaceId }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [docVisible, setDocVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  // 🛡️ Blur consent (MODERATION P1.1): live HUMAN video on the rail starts
  // blurred behind a tap chip, once per space per session — same policy as
  // the in-space stage (LivestreamPanel).
  const [revealed, setRevealed] = useState(() => {
    if (!LIVE_RAIL_BLUR_GATE) return true;
    try { return sessionStorage.getItem(`railReveal:${spaceId}`) === '1'; } catch (e) { return false; }
  });
  const reveal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setRevealed(true);
    try { sessionStorage.setItem(`railReveal:${spaceId}`, '1'); } catch (e2) { /* private mode */ }
  };

  useEffect(() => {
    const onVis = () => setDocVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!docVisible) return;
    let cancelled = false;
    let room = null;
    (async () => {
      try {
        const { Room, RoomEvent } = await import('livekit-client');
        const tokenUrl = `${API_BASE_URL}/api/takeGameStep?getLiveKitToken=true&roomName=${encodeURIComponent(spaceId)}&participantName=${encodeURIComponent('rail preview')}&participantIdentity=${encodeURIComponent(railPeekIdentity())}&canPublish=false`;
        const response = await fetch(tokenUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        let data = await response.json();
        // Lambda proxy response wrapper (body is a JSON string)
        if (data.body && typeof data.body === 'string') data = JSON.parse(data.body);
        if (cancelled || !data.token) return;

        room = new Room({ adaptiveStream: true });
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === 'video' && videoRef.current) {
            track.attach(videoRef.current);
            setPlaying(true);
          }
        });
        room.on(RoomEvent.TrackUnsubscribed, (track) => {
          if (track.kind === 'video') {
            track.detach();
            setPlaying(false);
          }
        });
        room.on(RoomEvent.TrackPublished, (pub) => {
          if (pub.kind === 'video') pub.setSubscribed(true);
        });
        await room.connect(data.url, data.token, { autoSubscribe: false });
        if (cancelled) { room.disconnect(); return; }
        room.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((pub) => {
            if (pub.kind === 'video') pub.setSubscribed(true);
          });
        });
      } catch (err) {
        // Banner + badge stay — the preview is a progressive enhancement
        console.warn('[LiveRail] video preview unavailable:', err.message);
      }
    })();
    return () => {
      cancelled = true;
      setPlaying(false);
      if (room) room.disconnect();
    };
  }, [spaceId, docVisible]);

  return (
    <>
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: playing ? 1 : 0,
          filter: revealed ? 'none' : 'blur(16px)',
          transition: 'opacity 0.4s ease, filter 0.25s ease',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {playing && !revealed && (
        <button
          onClick={reveal}
          style={{
            position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)',
            zIndex: 2, border: 'none', borderRadius: 999, padding: '3px 10px',
            background: 'rgba(0,0,0,0.7)', color: '#fff',
            fontSize: '0.62em', fontWeight: 800, whiteSpace: 'nowrap', cursor: 'pointer',
          }}
        >
          🔴 Real person · tap to peek
        </button>
      )}
    </>
  );
};

// Speech bubble hanging off the host circle — the host "says" one of their
// playful statuses ("counting calories"). Rotates with a fade every ~8s, but
// only while `rotate` (center card) so the rail doesn't blink in unison;
// side cards hold one status. Random start index staggers cards further.
const HostStatusBubble = ({ messages, rotate = false }) => {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * messages.length));
  const [shown, setShown] = useState(true);

  useEffect(() => {
    if (!rotate || messages.length < 2) return;
    const interval = setInterval(() => {
      setShown(false);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % messages.length);
        setShown(true);
      }, 280);
    }, 8000);
    return () => clearInterval(interval);
  }, [rotate, messages.length]);

  return (
    <div style={{ position: 'relative', opacity: shown ? 1 : 0, transition: 'opacity 0.28s ease' }} aria-hidden>
      {/* tail — small rotated square poking toward the host circle */}
      <div style={{
        position: 'absolute',
        left: -4,
        bottom: 10,
        width: 10,
        height: 10,
        transform: 'rotate(45deg)',
        backgroundColor: 'rgba(14,14,16,0.88)',
        borderLeft: '1px solid rgba(255,255,255,0.14)',
        borderBottom: '1px solid rgba(255,255,255,0.14)',
      }} />
      <div style={{
        backgroundColor: 'rgba(14,14,16,0.88)',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: '12px',
        borderBottomLeftRadius: '4px',
        padding: '5px 10px',
        color: '#dedee3',
        fontSize: '0.74rem',
        fontStyle: 'italic',
        lineHeight: 1.35,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
      }}>
        {messages[idx % messages.length]}
      </div>
    </div>
  );
};

const LiveRailCard = ({
  space,
  viewerCount,
  isLive,
  liveStreamers = [],
  // Effective party (WS overlay wins over the space.watch_party seed) —
  // resolved by LiveNowRail's partyFor so the pill flips live
  watchParty = null,
  onJoin,
  isCenter = true,
  animate = false,
  cardWidth,
  stripAspect,
  stripHeight,
  showVideoPreview = false,
  featuredGame = null,
  isPhone = false,
}) => {
  // Whodunnit rides the pre-generated noir art when the space has no banner of
  // its own — the preview strip and every banner-conditional style pick it up
  // (children receive the shadowed space, so LivePreviewStrip inherits too).
  if (space.competitive_mode && isWhodunnitCard(space.name) && !space.banner_image_url) {
    space = { ...space, banner_image_url: NOIR_RAIL_ART };
  }
  const accentColor = space.imposter_mode ? '#ffb74d' : space.competitive_mode ? '#a970ff' : '#bf94ff';
  const emoji = (space.background_emojis || space.emojis || [])[0] || '✨';
  const isLiveStream = liveStreamers.length > 0;

  // Host circle — ONE element at two sizes, so going live literally GROWS the
  // host into a facecam (CSS transition on size/top/ring). Idle ≈2x the old
  // 44px avatar for host presence; live ≈3x with video inside, so live still
  // visibly outranks idle at a glance. Bottom edge always hangs 22px below
  // the strip/meta boundary (the card clips overflow, so a center-straddle
  // would get cut off); both sizes scale down with narrow cards.
  const circleSize = isLiveStream
    ? Math.max(88, Math.min(LIVE_RAIL_AVATAR * 3, Math.round((cardWidth || 520) * 0.26)))
    : Math.max(60, Math.min(LIVE_RAIL_AVATAR * 2, Math.round((cardWidth || 520) * 0.17)));
  const circleTop = (stripHeight ?? LIVE_RAIL_STRIP_H) + LIVE_RAIL_AVATAR / 2 - circleSize;

  // Pick a host: moderator first (mods are humans running a space), else first AI player
  const host = useMemo(() => {
    if (space.moderator?.profile_image_url || space.moderator?.name) {
      return {
        name: space.moderator.name,
        avatarUrl: space.moderator.profile_image_url,
        statusMessages: Array.isArray(space.moderator.status_messages) ? space.moderator.status_messages : [],
      };
    }
    const ai = (space.players || []).find(p => !p.is_human && p.profile_image_url);
    return ai
      ? { name: ai.name, avatarUrl: ai.profile_image_url, statusMessages: [] }
      : { name: null, avatarUrl: null, statusMessages: [] };
  }, [space.moderator, space.players]);

  // Friendly chip label. Mode flags win, then we map the first interest tag
  // to a short label; "Conversation" stays as the catch-all fallback.
  const interestLabelMap = {
    'Storytelling/D&D': 'Storytelling',
    'Fantasy/Fiction': 'Fantasy',
    'Self Improvement': 'Self',
    'Relationships': 'Relationships',
    'Language': 'Language',
    'Science/Technology': 'Science',
    'News/Politics': 'News',
    'Holiday/Culture': 'Culture',
    'Art': 'Art',
    'Entertainment/Media': 'Entertainment',
    'Digital Content': 'Digital Content',
    'Film': 'Film',
    'TV Shows': 'TV Shows',
    'Gaming': 'Gaming',
    'Music': 'Music',
    'Conversational Nook': 'Conversation',
    'ISSAVIBE': 'Vibes',
    'Silly/Random': 'Random',
    'qt3.14s': 'qt3.14s',
  };
  const categoryLabel = space.imposter_mode
    ? 'Imposter'
    : space.competitive_mode
      ? 'Competition'
      : (space.interests || [])
          .map(i => interestLabelMap[i])
          .filter(Boolean)[0] || 'Conversation';

  return (
    <div
      className={`live-rail-card${isCenter ? ' is-center' : ' is-side'}`}
      style={{
        ...styles.liveRailCard,
        ...(cardWidth ? { width: `${cardWidth}px` } : null),
        // No drop shadow on phone: the stage clips overflow at ~14px past the
        // card (and flush at the sides on a mini), so the 60-80px haze gets
        // amputated into a hard gray rectangle framing the rail — glaring on
        // the light page (Nina 07-31). The shadow only exists to pop the
        // center card against its neighbors, which phone hides anyway.
        ...(isCenter && !isPhone ? {
          boxShadow: '0 4px 10px rgba(0,0,0,0.3), 0 32px 60px rgba(0,0,0,0.45), 0 24px 80px rgba(123,31,162,0.32)',
        } : null),
        // Human stream live → pulsing red ring on EVERY copy of the card, so
        // side/dimmed cards still read "someone is live here" without joining
        // the room (the animation's box-shadow overrides the static one above).
        // Phone gets the ring-only variant — the glow layers clip square too.
        ...(liveStreamers.length > 0 ? {
          animation: `${isPhone ? 'liveRailCardGlowPhone' : 'liveRailCardGlow'} 2.4s ease-in-out infinite`,
        } : null),
      }}
    >
      {/* Live preview strip — banner + animated ghost messages + reactions */}
      <div
        style={{
          ...styles.liveRailThumbWrap,
          ...(stripAspect ? { aspectRatio: `${stripAspect}` } : null),
        }}
      >
        {space.banner_image_url ? (
          <img
            src={space.banner_image_url}
            alt=""
            className="live-rail-card-thumb-img"
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={styles.liveRailThumbImg}
          />
        ) : (
          <div
            className="live-rail-card-thumb-img"
            style={{
              ...styles.liveRailThumbImg,
              background: `radial-gradient(circle at 30% 30%, ${accentColor}55, #0e0e10 75%)`,
              fontSize: '3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {emoji}
          </div>
        )}

        <LivePreviewStrip space={space} accentColor={accentColor} animate={animate} />

        {/* LIVE badge — top-left. Human webcam/screen streams get the named
            treatment; plain "someone is playing" keeps the classic badge */}
        {liveStreamers.length > 0 ? (
          <div style={{ ...styles.liveRailLiveBadge, display: 'flex', alignItems: 'center', gap: 4, maxWidth: '85%' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fff', flexShrink: 0, display: 'inline-block' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {liveStreamers[0].name} LIVE{liveStreamers.length > 1 ? ` +${liveStreamers.length - 1}` : ''}
            </span>
            {/* Host attribution lives up here now (user call 07-10) — the meta
                strip below belongs entirely to the space title */}
            {host.name && (
              <>
                <span style={{ opacity: 0.85, flexShrink: 0 }}>with</span>
                {host.avatarUrl && (
                  <img src={host.avatarUrl} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>@{host.name}</span>
              </>
            )}
          </div>
        ) : isLive && (
          <div style={styles.liveRailLiveBadge}>LIVE</div>
        )}

        {/* 🍿 Watch party badge — the space is watching a video together right now.
            Sits below the LIVE/streamer badge when one is showing, else takes the
            top-left slot itself. */}
        {watchParty && (
          <div style={{
            ...styles.liveRailLiveBadge,
            top: (liveStreamers.length > 0 || isLive) ? '32px' : '8px',
            backgroundColor: '#7b1fa2',
            textTransform: 'none',
            display: 'flex', alignItems: 'center', gap: 4,
            // The top row is shared with the genre pill (top-right, higher
            // zIndex): a long title slid under it and got visually chopped.
            // Reserve the genre corner when on that row; the lower row is clear.
            maxWidth: (liveStreamers.length > 0 || isLive) ? '85%' : 'calc(100% - 118px)',
          }}>
            <span style={{ flexShrink: 0 }}>🍿</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Watching: {watchParty.title}
            </span>
          </div>
        )}

        {/* Genre — top-right pill */}
        <div style={{ ...styles.liveRailGenreBadge, color: accentColor }}>{categoryLabel}</div>

        {/* 📤 Share — bottom-right anchor (Nina 08-06): the live card is the
            most shareable thing on /explore ("X is LIVE, come watch") and had
            zero share affordance — grid cards and in-space both do. Icon-only
            compact pill matching the badge chrome; the modal portals to
            <body> so the card's stacking context can't trap it. stopPropagation
            inside ShareButton keeps a share tap from joining the space. */}
        <div style={{ position: 'absolute', right: 8, bottom: 8, zIndex: 2 }}>
          <ShareButton gameId={space.id || space.name} gameName={space.name} compact />
        </div>

        {/* Viewer count — bottom-right ladder, one rung above share */}
        {viewerCount > 0 && (
          <div style={{ ...styles.liveRailViewBadge, bottom: 34 }}>
            <span style={styles.liveRailViewDot} />
            {formatViewerCount(viewerCount)}
            <span style={styles.liveRailViewLabel}>viewers</span>
          </div>
        )}

        {/* Featured game — next rung of the bottom-right ladder (share pill
            took bottom 8). Dark pill + white text (contrast rule 07-10). */}
        {featuredGame && (
          <div style={{
            position: 'absolute', right: 8, bottom: viewerCount > 0 ? 60 : 34, zIndex: 2,
            padding: '3px 9px', borderRadius: 999, pointerEvents: 'none',
            background: 'rgba(0,0,0,0.62)', border: '1px solid rgba(255,255,255,0.22)',
            color: '#fff', fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.3,
            maxWidth: '62%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            🎮 {featuredGame}
          </div>
        )}
      </div>

      {/* Metadata strip below thumbnail. The host circle owns the left side on
          every card, so the "with @host" attribution lives in a right-aligned
          cluster with its own mini avatar — uniform layout live or idle. */}
      <div className="live-rail-card-meta" style={{
        ...styles.liveRailMeta,
        // Mirror the circle's left inset on the right so the centered title sits
        // at the CARD's center, not the leftover-space center (user call 07-10).
        // Live attribution moved into the top-left LIVE badge — the whole strip
        // belongs to the title now, live or idle.
        // 📱 The mirror is a LUXURY, not a requirement: only the LEFT inset has
        // to clear the circle — nothing sits on the right. On a phone the
        // mirrored 112px reserved a quarter of the card for empty space and the
        // nowrap+ellipsis title collapsed to ~5-11 characters (07-26 audit,
        // worst thing on /explore). Narrow cards keep the left clearance, drop
        // the right one, and left-align so the result reads deliberate rather
        // than off-centre. Wide cards are unchanged.
        // 08-02 (Nina, device): nameplate + title + tagline crowded each other
        // bottom-left — the nameplate box (left 4, width circle+16) actually
        // CROSSED 4px into where the text column starts. Narrow cards now
        // reserve circle+24 so there's a real gap, plus 2px more bottom pad.
        padding: (cardWidth || 520) < 420
          ? `8px 12px 10px ${circleSize + 24}px`
          : `8px ${circleSize + 24}px`,
        transition: 'padding 0.35s ease',
        // Idle cards caption the host's name UNDER the circle — the meta strip
        // needs a touch more height so the nameplate clears the clipped card edge
        ...(!isLiveStream && host.name ? { minHeight: '52px' } : null),
      }}>
        <div style={{ ...styles.liveRailMetaText, flex: 1, textAlign: (cardWidth || 520) < 420 ? 'left' : 'center' }}>
          <div style={styles.liveRailMetaTitle} title={space.name}>{space.name}</div>
          {space.tagline && (
            <div style={{ ...styles.liveRailMetaHost, fontStyle: 'italic' }} title={space.tagline}>
              {space.tagline}
            </div>
          )}
        </div>
      </div>

      {/* Host circle — idle: 2x avatar with the quiet ring; live: grows into
          the 3x red-ringed facecam (same element, transitioned, so going live
          is a visible MOMENT). Host avatar is the face until (and unless)
          real video arrives; only showVideoPreview copies hold an actual
          LiveKit connection. */}
      <div
        style={{
          position: 'absolute',
          top: `${circleTop}px`,
          left: '12px',
          width: `${circleSize}px`,
          height: `${circleSize}px`,
          borderRadius: '50%',
          overflow: 'hidden',
          border: isLiveStream ? '3px solid #eb0400' : '3px solid #1f1f23',
          boxShadow: isLiveStream
            ? '0 0 18px rgba(235,4,0,0.5), 0 6px 18px rgba(0,0,0,0.5)'
            : '0 4px 12px rgba(0,0,0,0.4)',
          backgroundColor: '#0e0e10',
          zIndex: 3,
          transition: 'top 0.35s ease, width 0.35s ease, height 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease',
        }}
        aria-hidden
      >
        {host.avatarUrl ? (
          <img src={host.avatarUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(135deg, ${accentColor}, #5c16c5)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: isLiveStream ? '2.6rem' : '1.8rem',
          }}>
            {emoji}
          </div>
        )}
        {showVideoPreview && <LiveStreamRailPreview spaceId={space.id} />}
      </div>

      {/* Host nameplate — idle cards caption the big circle directly (the
          little right-side avatar was a redundant second copy of the same
          face). Occupies the otherwise-empty indent column under the circle,
          so the title never has to move. */}
      {!isLiveStream && host.name && (
        <div
          style={{
            position: 'absolute',
            // 08-02: narrowed +16→+8 (left 4→8 keeps it centered on the
            // circle) so its right edge stays clear of the title column —
            // the old box overlapped it and read as crowding on phones.
            left: '8px',
            top: `${(stripHeight ?? LIVE_RAIL_STRIP_H) + 24}px`,
            width: `${circleSize + 8}px`,
            textAlign: 'center',
            fontSize: '0.72rem',
            color: '#adadb8',
            fontWeight: 600,
            zIndex: 3,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          {/* Inner block, not the centered div, owns the clipping: a centered
              overflowing name clips BOTH sides (first letter eaten, audit #10) —
              this way long names start at the left and ellipsize right only */}
          <span style={{
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'top',
          }}>
            @{host.name}
          </span>
        </div>
      )}

      {/* Host status speech bubble — idle cards only (live cards already have
          the facecam + badge; "counting calories" next to a live human would
          be noise). Sits beside the circle's upper half, tail pointing at it. */}
      {!isLiveStream && host.statusMessages.length > 0 && (
        <div style={{
          position: 'absolute',
          left: `${circleSize + 22}px`,
          top: `${circleTop + 6}px`,
          maxWidth: '52%',
          zIndex: 3,
          pointerEvents: 'none',
        }}>
          <HostStatusBubble messages={host.statusMessages} rotate={animate} />
        </div>
      )}
    </div>
  );
};

// Skeleton placeholder shown while spaces are loading. Matches SpaceCard
// dimensions/structure roughly so the swap to real cards has no layout shift.
const SkeletonCard = () => (
  <div style={styles.skeletonCard} aria-hidden="true">
    <div style={styles.skeletonHeader}>
      <div className="explore-skeleton-shimmer" style={styles.skeletonAvatar} />
      <div className="explore-skeleton-shimmer" style={styles.skeletonTitle} />
    </div>
    <div className="explore-skeleton-shimmer" style={{ ...styles.skeletonLine, width: '92%' }} />
    <div className="explore-skeleton-shimmer" style={{ ...styles.skeletonLine, width: '78%' }} />
    <div className="explore-skeleton-shimmer" style={{ ...styles.skeletonLine, width: '55%' }} />
    <div style={styles.skeletonFooter}>
      <div className="explore-skeleton-shimmer" style={styles.skeletonChip} />
      <div className="explore-skeleton-shimmer" style={styles.skeletonChip} />
    </div>
  </div>
);

// Space Card Component
const SpaceCard = ({ space, viewerCount, totalViews, isLive, onJoin, flipDelay }) => {
  // Whodunnit rides the pre-generated noir art when the space has no banner of
  // its own (same fallback as LiveRailCard — banner header + all its
  // conditional styling light up for free).
  if (space.competitive_mode && isWhodunnitCard(space.name) && !space.banner_image_url) {
    space = { ...space, banner_image_url: NOIR_RAIL_ART };
  }
  // 🏡🚪 W3 the porch postcard (PORTED_HOME_HANDOFF §9; behind the place
  // switch): a ported space whose resident chose a home shows that home's
  // painted OUTSIDE up front, framed on its door (strangers never see inside),
  // and the back names the house in one printed line. Before a home: a light
  // room glow (a place port skips the generated banner, D8).
  const porch = space.porch && placeOn() ? porchFor(space.porch.home_key) : null;
  if (porch) space = { ...space, banner_image_url: porch.url, porch: { ...space.porch, line: porch.line }, _porchFocus: porch.focus, _porchFallback: space.banner_image_url || null };
  const porchGlow = !!space.porch && !porch && !space.banner_image_url && placeOn();
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reported, setReported] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [castExpanded, setCastExpanded] = useState(false); // cast row: 2 big tiles ↔ full roster
  const [tagsExpanded, setTagsExpanded] = useState(false); // chip rows: one collapsed line ↔ all chips (Nina 08-02)
  const flipTimerRef = useRef(null);

  const accentColor = space.imposter_mode ? '#b8860b' : space.competitive_mode ? '#7b1fa2' : '#1565c0';
  const shareUrl = `${window.location.origin}/game-step?gameId=${encodeURIComponent(space.id || space.name)}`;
  const [isRemixing, setIsRemixing] = useState(false);

  // 🚫 Remix DISABLED (2026-08-29, Nina): the "Remix this space" button is off
  // pending a consent-flow rework — remixing silently cloned a public space
  // (including the imported agents seated in it) into a private copy with no
  // confirmation of what "remix" does. Flip to true to restore the button; the
  // backend /chat-game/clone endpoint is gated too (env REMIX_ENABLED), so a
  // direct API call can't clone either.
  const REMIX_ENABLED = false;

  // Remix — clone this space (fresh state, same cast/theme) and jump in.
  // Lineage is recorded backend-side as data.forked_from.
  const handleRemix = async (e) => {
    e.stopPropagation();
    if (isRemixing) return;
    setIsRemixing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/chat-game/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceGameId: space.id }),
      });
      const result = await res.json();
      if (result.game_id) {
        window.open(`/game-step?gameId=${encodeURIComponent(result.game_id)}`, '_blank');
      } else {
        alert(result.error || 'Remix failed. Try again in a bit.');
      }
    } catch (err) {
      alert('Remix failed. Try again in a bit.');
    } finally {
      setIsRemixing(false);
    }
  };
  const musicTitle = useYouTubeTitle(space.music_url);

  // Build name→profile_image_url lookup for ghost chat avatars and "Who's here"
  const playerAvatars = useMemo(() => {
    const map = {};
    (space.players || []).forEach(p => {
      if (p.name && p.profile_image_url) map[p.name] = p.profile_image_url;
    });
    return map;
  }, [space.players]);

  const aiPlayersWithImages = useMemo(() => {
    return (space.players || []).filter(p => !p.is_human && p.profile_image_url);
  }, [space.players]);

  // Auto-flip on a staggered interval, pause on hover. Low-resource phones
  // skip the auto-flip entirely — dozens of cards cycling 3D transforms keeps
  // every card's front+back promoted to GPU layers (07-19 crash fix). Tap
  // still flips.
  const initialDelayDone = useRef(false);
  useEffect(() => {
    if (hovered || isLowResourceDevice) {
      clearInterval(flipTimerRef.current);
      return;
    }
    if (!initialDelayDone.current) {
      const timeout = setTimeout(() => {
        initialDelayDone.current = true;
        setIsFlipped(prev => !prev);
        flipTimerRef.current = setInterval(() => setIsFlipped(prev => !prev), 12000);
      }, flipDelay || 3000);
      return () => clearTimeout(timeout);
    }
    flipTimerRef.current = setInterval(() => setIsFlipped(prev => !prev), 12000);
    return () => clearInterval(flipTimerRef.current);
  }, [hovered, flipDelay]);

  // Build player names and AI model names
  const playerNames = useMemo(() => {
    if (!space.players || space.players.length === 0) return [];
    return space.players.map(p => p.name).filter(Boolean);
  }, [space.players]);

  const aiModelNames = useMemo(() => {
    if (!space.players || space.players.length === 0) return space.ai_roles || [];
    return space.players.filter(p => !p.is_human).map(p => p.model_name || p.name).filter(Boolean);
  }, [space.players, space.ai_roles]);

  const roundInfo = space.current_round_number && (space.competitive_mode || space.imposter_mode)
    ? space.imposter_mode
      ? `Word round ${space.current_round_number} of ${space.imposter_word_rounds || 3}`
      : space.total_rounds
        ? `Round ${space.current_round_number} of ${space.total_rounds}`
        : null
    : null;

  const backEmojis = space.background_emojis || space.emojis || [];

  const isNew = (() => {
    const created = new Date(space.shared_at || space.created_at);
    return (Date.now() - created.getTime()) < 2 * 60 * 60 * 1000;
  })();

  return (
    <div
      className="space-card-outer"
      style={{ ...styles.cardOuter, perspective: '1000px', cursor: 'pointer' }}
      onClick={() => { setIsFlipped(prev => !prev); setShowInfoDialog(false); }}
      onMouseEnter={(e) => {
        // Skip hover lift on touch devices — adding a transform to the perspective
        // parent flattens the 3D context on iOS Safari and causes a black flash.
        if (typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches) return;
        setHovered(true);
        const inner = e.currentTarget;
        inner.style.transform = 'translateY(-4px)';
        inner.style.boxShadow = `0 8px 24px rgba(0,0,0,0.12), 0 0 16px ${accentColor}20`;
      }}
      onMouseLeave={(e) => {
        if (typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches) return;
        setHovered(false);
        const inner = e.currentTarget;
        inner.style.transform = 'translateY(0)';
        inner.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
    >
      <div className={`space-card-inner${isFlipped ? ' flipped' : ''}`}>
        {/* === FRONT FACE === */}
        <div className="space-card-front" style={{ ...styles.card, position: 'relative' }}>
          {/* 🏮 a ported space before its home: the light room's glow (no art to show yet) */}
          {porchGlow && (
            <div data-porch="light" aria-hidden="true" style={{
              margin: '-24px -24px 0 -24px', height: '100px', borderRadius: '12px 12px 0 0',
              backgroundColor: '#120e28',
              backgroundImage: 'radial-gradient(circle at 22% 40%, rgba(255,196,107,.55), rgba(255,196,107,0) 18%), radial-gradient(circle at 62% 55%, rgba(111,231,242,.6), rgba(111,231,242,0) 16%), radial-gradient(circle at 80% 30%, rgba(186,166,255,.45), rgba(186,166,255,0) 14%), linear-gradient(160deg, #1d1440, #07071a)',
            }} />
          )}
          {/* Banner image header */}
          {space.banner_image_url && (
            <div style={{
              margin: '-24px -24px 0 -24px',
              height: '100px',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '12px 12px 0 0',
            }}>
              <img
                src={space.banner_image_url}
                alt=""
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  // 🏮 a porch outside that isn't hosted yet falls back to the room's own banner, once
                  const img = e.currentTarget;
                  if (space._porchFallback && img.getAttribute('data-porch') === 'outside' && !img.dataset.fellBack) {
                    img.dataset.fellBack = '1'; img.style.objectPosition = ''; img.src = space._porchFallback; return;
                  }
                  img.style.display = 'none';
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  // 🚪 the porch: framed on the front door, the lit window above it
                  ...(space._porchFocus ? { objectPosition: `${space._porchFocus.x}% ${Math.max(0, space._porchFocus.y - 18)}%` } : {}),
                }}
                data-porch={space._porchFocus ? 'outside' : undefined}
              />
              {/* Gradient fade to white at bottom */}
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '40px',
                background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.85))',
              }} />
            </div>
          )}

          {/* Top row: likes + game/new tags (left) + menu (right) */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            ...(space.banner_image_url ? {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              padding: '10px 14px',
              zIndex: 3,
              marginBottom: 0,
            } : {}),
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{
                fontSize: '0.75rem',
                color: space.banner_image_url ? '#fff' : '#999',
                fontWeight: 500,
                textShadow: space.banner_image_url ? '0 1px 3px rgba(0,0,0,0.5)' : 'none',
              }}>
                ❤️ {space.likes_count || 0}
              </span>
              {(space.competitive_mode || space.imposter_mode) && (
                <span style={{
                  ...styles.gameTag,
                  ...(space.banner_image_url ? {
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  } : {}),
                }}>Game</span>
              )}
              {isNew && (
                <span style={{
                  padding: '2px 8px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  backgroundColor: space.banner_image_url ? 'rgba(255,235,238,0.95)' : '#ffebee',
                  color: '#c62828',
                  borderRadius: '10px',
                  border: '1px solid #ef9a9a',
                  ...(space.banner_image_url ? { boxShadow: '0 1px 4px rgba(0,0,0,0.15)' } : {}),
                }}>New</span>
              )}
              {space.is_space_of_week && (
                <span
                  title="Space of the Week"
                  style={{
                    padding: '2px 8px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    backgroundColor: space.banner_image_url ? 'rgba(255,248,225,0.95)' : '#fff8e1',
                    color: '#b8860b',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,193,7,0.5)',
                    ...(space.banner_image_url ? { boxShadow: '0 1px 4px rgba(0,0,0,0.15)' } : {}),
                  }}
                >🏆 This Week</span>
              )}
              {space.forked_from && (
                <span
                  title={`Forked from ${space.forked_from.name}`}
                  style={{
                    padding: '2px 8px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: space.banner_image_url ? 'rgba(232,245,233,0.95)' : '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '10px',
                    border: '1px solid rgba(76,175,80,0.4)',
                    ...(space.banner_image_url ? { boxShadow: '0 1px 4px rgba(0,0,0,0.15)' } : {}),
                  }}
                >⑂ Remix</span>
              )}
              {space.space_mood && MOOD_BY_KEY[space.space_mood] && (
                <span
                  title={`Current vibe: ${MOOD_BY_KEY[space.space_mood].label}`}
                  style={{
                    padding: '2px 8px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: space.banner_image_url ? 'rgba(243,238,248,0.95)' : '#f3eef8',
                    color: '#5e1583',
                    borderRadius: '10px',
                    border: '1px solid rgba(123,31,162,0.25)',
                    ...(space.banner_image_url ? { boxShadow: '0 1px 4px rgba(0,0,0,0.15)' } : {}),
                  }}
                >
                  {MOOD_BY_KEY[space.space_mood].emoji} {MOOD_BY_KEY[space.space_mood].label}
                </span>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowInfoDialog(!showInfoDialog); }}
                style={{
                  ...styles.moreButton,
                  ...(space.banner_image_url ? {
                    color: '#fff',
                    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  } : {}),
                }}
              >
                ···
              </button>
              {showInfoDialog && !isFlipped && (
                <>
                  <div
                    style={styles.dialogOverlay}
                    onClick={(e) => { e.stopPropagation(); setShowInfoDialog(false); }}
                  />
                  <div style={styles.infoDialog} onClick={(e) => e.stopPropagation()}>
                    {space.author && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Author</span>
                        <span style={styles.infoValue}>{space.author}</span>
                      </div>
                    )}
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Watching</span>
                      <span style={styles.infoValue}>{viewerCount}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Visits</span>
                      <span style={styles.infoValue}>{totalViews}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Likes / Dislikes</span>
                      <span style={styles.infoValue}>{space.likes_count || 0} / {space.dislikes_count || 0}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Time Left</span>
                      <span style={styles.infoValue}>
                        {(() => {
                          const TTL_HOURS = 24;
                          const created = new Date(space.shared_at || space.created_at);
                          const expires = new Date(created.getTime() + TTL_HOURS * 60 * 60 * 1000);
                          const remaining = expires - Date.now();
                          if (remaining <= 0) return 'Expired';
                          const hours = Math.floor(remaining / (1000 * 60 * 60));
                          const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                          if (hours > 0) return `${hours}h ${minutes}m`;
                          return `${minutes}m`;
                        })()}
                      </span>
                    </div>
                    {space.forked_from && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Forked from</span>
                        <span style={styles.infoValue}>{space.forked_from.name}</span>
                      </div>
                    )}
                    {REMIX_ENABLED && (
                    <button
                      onClick={handleRemix}
                      disabled={isRemixing}
                      style={{
                        marginTop: '8px',
                        width: '100%',
                        padding: '7px 10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(123,31,162,0.35)',
                        backgroundColor: '#f3eef8',
                        color: '#5e1583',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: isRemixing ? 'wait' : 'pointer',
                        fontFamily: 'inherit',
                        opacity: isRemixing ? 0.6 : 1,
                      }}
                    >
                      {isRemixing ? 'Remixing…' : '⑂ Remix this space'}
                    </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(shareUrl).then(() => {
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        });
                      }}
                      style={{
                        marginTop: '8px',
                        width: '100%',
                        padding: '6px 12px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: copied ? '#2e7d32' : '#1976d2',
                        backgroundColor: '#fff',
                        border: `1px solid ${copied ? '#a5d6a7' : '#90caf9'}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      {copied ? 'Link Copied!' : 'Share Link'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setReported(true);
                        setTimeout(() => setReported(false), 2000);
                      }}
                      style={{ ...styles.reportButton, marginTop: '4px' }}
                    >
                      {reported ? 'Reported. Thanks!' : 'Report'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{
            ...styles.cardHeader,
            ...(space.banner_image_url ? { marginTop: '10px' } : {}),
            justifyContent: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              {isLive && <span style={styles.liveDot} />}
              <h3 style={{ ...styles.cardTitle, textAlign: 'center', fontSize: '1.35rem', flex: 'unset' }}>{space.name}</h3>
            </div>
          </div>

          {/* Tagline — the space's own one-liner, when the creator set one */}
          {space.tagline && (
            <p style={{
              margin: '-6px 0 10px',
              textAlign: 'center',
              fontSize: '0.85rem',
              fontStyle: 'italic',
              color: accentColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              “{space.tagline}”
            </p>
          )}

          <p className={descExpanded ? undefined : 'card-desc-clamp'} style={{
            ...styles.cardDescription,
            ...(space.competitive_mode && isWhodunnitCard(space.name)
              ? { ...NOIR_HEADLINE_STYLE, fontSize: '1.02rem', color: accentColor }
              : {}),
            ...(!descExpanded ? {
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            } : {}),
          }}>{space.imposter_mode
            ? 'A word game where one player is The Golden Glitch and doesn\'t know the secret topic. Say one word per turn!'
            : space.competitive_mode && isWhodunnitCard(space.name)
            ? whodunnitHeadline()
            : space.competitive_mode && space.objective
            ? space.objective.replace(/\s*Call out each AI by their model name and roast their known technical limitations\s*[—–-]\s*hallucinations,\s*reasoning gaps,\s*context window size,\s*and more!\s*/i, '').trim()
            : space.description}</p>
          {(() => {
            const desc = space.imposter_mode
              ? 'A word game where one player is The Golden Glitch and doesn\'t know the secret topic. Say one word per turn!'
              : space.competitive_mode && isWhodunnitCard(space.name)
              ? whodunnitHeadline()
              : space.competitive_mode && space.objective
              ? space.objective.replace(/\s*Call out each AI by their model name and roast their known technical limitations\s*[—–-]\s*hallucinations,\s*reasoning gaps,\s*context window size,\s*and more!\s*/i, '').trim()
              : space.description;
            return desc && desc.length > 80 ? (
              <span
                onClick={(e) => { e.stopPropagation(); setDescExpanded(!descExpanded); }}
                style={{ fontSize: '0.75rem', color: '#1976d2', cursor: 'pointer', fontWeight: 500, marginTop: '-10px', marginBottom: '8px', display: 'block' }}
              >
                {descExpanded ? 'Show less' : 'Show more'}
              </span>
            ) : null;
          })()}

          {(space.moderator?.profile_image_url || aiPlayersWithImages.length > 0) && (() => {
            // Cast row: big tiles beat a complete roster (Nina 07-31 — two
            // large images hook people). Host sits MID-ROW and slightly
            // larger (Nina 08-01), and the row carries the Tea-tab scrapbook
            // tilt (recipe from e7f283697). +N chip expands in place to the
            // whole cast as a wrapped grid.
            const visibleCast = castExpanded ? aiPlayersWithImages : aiPlayersWithImages.slice(0, 2);
            const hiddenCount = aiPlayersWithImages.length - 2;
            const castTile = castExpanded
              ? { flex: '0 0 auto', width: 84 }
              : { flex: '1 1 0', minWidth: 0, maxWidth: 120 };
            const modTile = castExpanded
              ? { flex: '0 0 auto', width: 100 }
              : { flex: '1.2 1 0', minWidth: 0, maxWidth: 140 };
            // Interleave: host in the middle, AI cast around them
            const tiles = visibleCast.map((p) => ({ mod: false, p }));
            if (space.moderator?.profile_image_url) {
              tiles.splice(Math.floor(tiles.length / 2), 0, { mod: true, p: space.moderator });
            }
            // Tea-tab tilt cycle, scatter scaled down for these short tiles
            const tileTilt = (i) => `rotate(${[-2, 1.6, -1.2, 2, -0.8][i % 5]}deg) translateY(${[0, 5, 2, 6, 3][i % 5]}px)`;
            return (
            // Centered + 16px bleed into the card's side padding: buys the
            // tiles ~11px each on a mini (~100px), and centering matches the
            // card's centered title/tagline so the bleed reads intentional.
            // Vertical padding absorbs the tilt scatter.
            <div style={{ display: 'flex', gap: '10px', margin: '0 -16px 12px', justifyContent: 'center', alignItems: 'flex-end', flexWrap: castExpanded ? 'wrap' : 'nowrap', paddingTop: 6, paddingBottom: 8 }}>
              {tiles.map((t, i) => (
                <div key={i} style={{
                  ...(t.mod ? modTile : castTile),
                  // Badge host: the +N/collapse chip overlays this tile's
                  // corner — as a row sibling it cost ~66px and shrank the
                  // "two very large tiles" right back down to ~64px
                  ...(hiddenCount > 0 && i === tiles.length - 1 ? { position: 'relative' } : {}),
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                  transform: tileTilt(i),
                }}>
                  <img
                    src={t.p.profile_image_url}
                    alt={t.p.name}
                    title={t.p.name}
                    style={{
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '1 / 1',
                      borderRadius: '10px',
                      objectFit: 'cover',
                      border: t.mod ? '2px solid #ddd' : '2px solid #eee',
                      boxSizing: 'border-box',
                      boxShadow: t.mod ? '0 2px 8px rgba(0,0,0,0.15)' : '0 2px 6px rgba(0,0,0,0.15)',
                    }}
                  />
                  <span style={{
                    fontSize: '0.65rem',
                    color: t.mod ? '#444' : '#555',
                    fontWeight: t.mod ? 700 : 600,
                    maxWidth: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {t.mod ? <>{t.p.avatar_emoji} {t.p.name}</> : t.p.name}
                  </span>
                  {hiddenCount > 0 && i === tiles.length - 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setCastExpanded(prev => !prev); }}
                      aria-label={castExpanded ? 'Show fewer characters' : `Show ${hiddenCount} more characters`}
                      style={{
                        // stopPropagation is load-bearing: the card's
                        // outer onClick flips it — expanding must not flip
                        position: 'absolute', top: -8, right: -8, zIndex: 2, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: '2px',
                        padding: '6px 9px', borderRadius: 999,
                        backgroundColor: '#fff', border: '2px solid #e0e0e0',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                        fontSize: '0.72rem', fontWeight: 700, color: '#777',
                        fontFamily: 'inherit', lineHeight: 1,
                      }}
                    >
                      {castExpanded ? '‹' : <>+{hiddenCount} <span aria-hidden>›</span></>}
                    </button>
                  )}
                </div>
              ))}
            </div>
            );
          })()}

          {space.ai_roles && space.ai_roles.length > 0 && (
            <div style={styles.tagsSection}>
              <span style={styles.tagsLabel}>AI Roles:</span>
              <div className={tagsExpanded ? undefined : 'card-chips-collapsed'} style={styles.tagsContainer}>
                {(tagsExpanded ? space.ai_roles : space.ai_roles.slice(0, 3)).map((role, idx) => (
                  <span key={idx} style={styles.roleTag}>{role}</span>
                ))}
                {space.ai_roles.length > 3 && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setTagsExpanded(!tagsExpanded); }}
                    title={space.ai_roles.join(' · ')}
                    style={{ fontSize: '0.7rem', color: '#888', alignSelf: 'center', cursor: 'pointer', fontWeight: 600, padding: '2px 6px' }}
                  >{tagsExpanded ? 'less' : `+${space.ai_roles.length - 3}`}</span>
                )}
              </div>
            </div>
          )}

          {/* Chip row collapses to ONE line (Nina 08-02): slice(0,2) + a
              tappable +N that expands in place; title = full list for
              desktop hover. Mobile CSS adds nowrap so wrap can't sneak a
              second line in even when 2 long chips overflow. */}
          {space.interests && space.interests.length > 0 && (
            <div className={tagsExpanded ? undefined : 'card-chips-collapsed'} style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '12px', marginTop: 'auto' }}>
              {(tagsExpanded ? space.interests : space.interests.slice(0, 2)).map((interest, idx) => (
                <span key={idx} style={styles.interestTag}>{interest}</span>
              ))}
              {space.interests.length > 2 && (
                <span
                  onClick={(e) => { e.stopPropagation(); setTagsExpanded(!tagsExpanded); }}
                  title={space.interests.join(' · ')}
                  style={{ fontSize: '0.7rem', color: '#888', alignSelf: 'center', cursor: 'pointer', fontWeight: 600, padding: '2px 6px', flexShrink: 0 }}
                >{tagsExpanded ? 'less' : `+${space.interests.length - 2}`}</span>
              )}
            </div>
          )}

          {/* Real <a> anchor so automated explorers can enumerate the
              space's /game-step URL directly without needing to flip the
              card first. e.stopPropagation prevents the click from also
              triggering the outer space-card-outer onClick (which flips
              the card). Cycle 2: anchor-wrap. */}
          <a
            href={`/game-step?gameId=${encodeURIComponent(space.id)}`}
            data-space-name={space.name}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onJoin(); }}
            style={{
              ...styles.joinButton,
              position: 'relative',
              display: 'block',
              textAlign: 'center',
              textDecoration: 'none',
              boxSizing: 'border-box',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1976d2'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#1976d2'; }}
          >
            Drop in
            <span style={{
              position: 'absolute',
              width: 1, height: 1, padding: 0, margin: -1,
              overflow: 'hidden', clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap', border: 0,
            }}>{` ${space.name}`}</span>
          </a>
          <ShareButton
            gameId={space.id || space.name}
            gameName={space.name}
            variant="light"
            fullWidth
          />
        </div>

        {/* === BACK FACE === */}
        <div className="space-card-back" style={{
          backgroundColor: '#fff',
          border: '1px solid #eee',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: '#222',
          boxSizing: 'border-box',
        }}>
          {/* Floating emojis on back */}
          {backEmojis.length > 0 && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none', opacity: 0.12 }}>
              {backEmojis.map((emoji, i) => (
                <span key={i} style={{
                  position: 'absolute',
                  fontSize: `${20 + (i * 12)}px`,
                  top: `${10 + (i * 25) % 70}%`,
                  left: `${5 + (i * 30) % 80}%`,
                  transform: `rotate(${i * 15 - 20}deg)`,
                }}>{emoji}</span>
              ))}
            </div>
          )}

          {/* Card back header — game tag + name + ... on same line */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
              {(space.competitive_mode || space.imposter_mode) && (
                <span style={styles.gameTag}>Game</span>
              )}
              {isLive && <span style={styles.liveDot} />}
              <span style={{ fontSize: '0.8rem', color: '#888' }}>{space.name}</span>
              {isLive && roundInfo && (
                <span style={{ fontSize: '0.8rem', color: '#888' }}>
                  <span style={{ margin: '0 4px' }}>·</span>
                  <span style={{ color: accentColor, fontWeight: 600 }}>{roundInfo}</span>
                </span>
              )}
              {viewerCount > 0 && (
                <span style={{ fontSize: '0.8rem', color: '#888' }}>
                  <span style={{ margin: '0 4px' }}>·</span>
                  👁 {viewerCount}
                </span>
              )}
              <div style={{ flex: 1 }} />
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowInfoDialog(!showInfoDialog); }}
                  style={{ ...styles.moreButton }}
                >
                  ...
                </button>
                {showInfoDialog && isFlipped && (
                  <>
                    <div
                      style={styles.dialogOverlay}
                      onClick={(e) => { e.stopPropagation(); setShowInfoDialog(false); }}
                    />
                    <div style={styles.infoDialog} onClick={(e) => e.stopPropagation()}>
                      {space.author && (
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>Author</span>
                          <span style={styles.infoValue}>{space.author}</span>
                        </div>
                      )}
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Watching</span>
                        <span style={styles.infoValue}>{viewerCount}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Visits</span>
                        <span style={styles.infoValue}>{totalViews}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Likes / Dislikes</span>
                        <span style={styles.infoValue}>{space.likes_count || 0} / {space.dislikes_count || 0}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Time Left</span>
                        <span style={styles.infoValue}>
                          {(() => {
                            const TTL_HOURS = 24;
                            const created = new Date(space.shared_at || space.created_at);
                            const expires = new Date(created.getTime() + TTL_HOURS * 60 * 60 * 1000);
                            const remaining = expires - Date.now();
                            if (remaining <= 0) return 'Expired';
                            const hours = Math.floor(remaining / (1000 * 60 * 60));
                            const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                            if (hours > 0) return `${hours}h ${minutes}m`;
                            return `${minutes}m`;
                          })()}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(shareUrl).then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          });
                        }}
                        style={{
                          marginTop: '8px',
                          width: '100%',
                          padding: '6px 12px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: copied ? '#2e7d32' : '#1976d2',
                          backgroundColor: '#fff',
                          border: `1px solid ${copied ? '#a5d6a7' : '#90caf9'}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {copied ? 'Link Copied!' : 'Share Link'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setReported(true);
                          setTimeout(() => setReported(false), 2000);
                        }}
                        style={{ ...styles.reportButton, marginTop: '4px' }}
                      >
                        {reported ? 'Reported. Thanks!' : 'Report'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Music/video — right under name line, before heading */}
            {space.music_url && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '6px',
                fontSize: '0.8rem',
                color: '#888',
              }}>
                <span style={{ flexShrink: 0 }}>🎵</span>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{musicTitle || 'Music playing'}</span>
              </div>
            )}
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.25rem', fontWeight: '600', color: '#333' }}>Inside this space</h3>
          </div>

          {/* AI Models / Roles */}
          {aiModelNames.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px' }}>
              {aiModelNames.slice(0, 4).map((name, i) => (
                <span key={i} style={{
                  padding: '2px 8px',
                  fontSize: '0.7rem',
                  backgroundColor: `${accentColor}10`,
                  color: accentColor,
                  borderRadius: '10px',
                  border: `1px solid ${accentColor}25`,
                  whiteSpace: 'nowrap',
                }}>{name}</span>
              ))}
              {aiModelNames.length > 4 && (
                <span style={{ fontSize: '0.7rem', color: '#888', alignSelf: 'center' }}>+{aiModelNames.length - 4}</span>
              )}
            </div>
          )}

          {/* Ghost Chat — centerpiece, fills available space. Spaces with no
              recent messages left a giant void between the header and the
              buttons (space-between back + nothing in the middle — mobile
              audit P1, 07-16); the quiet-state filler keeps the card alive. */}
          {space.porch ? (
            /* 🏮 the porch (D10, ruled + built 08-28): a ported 1:1 space is a
               private room — the card back is a postcard (resident, day plate,
               knock-first line), never the room's own chat. */
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 7,
              textAlign: 'center', padding: '12px 0',
            }}>
              {space.porch.resident_image ? (
                <img
                  src={space.porch.resident_image}
                  alt={space.porch.resident_name || 'resident'}
                  style={{
                    width: 64, height: 64, borderRadius: '50%', objectFit: 'cover',
                    border: '2px solid rgba(232,188,114,0.8)',
                    boxShadow: '0 0 20px rgba(240,172,85,0.45)',
                  }}
                />
              ) : (
                <div style={{ fontSize: '2.1rem' }}>🏮</div>
              )}
              {space.porch.resident_name && (
                <div style={{
                  fontSize: '0.95rem', fontWeight: 700, color: '#5a4832', fontStyle: 'italic',
                  fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                }}>
                  {space.porch.resident_name} lives here
                </div>
              )}
              {space.porch.line && (
                <div data-porch-line="" style={{ fontSize: '0.8rem', color: '#8a7454', fontStyle: 'italic', fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif' }}>
                  {space.porch.line}
                </div>
              )}
              {/* 🏮 ruling 31 (E4): strangers read how long he has lived here,
                  "12 days alive" from the port day; "day N together" is the owner's */}
              {porchDaysAlive(space.porch.day_n) != null && (
                <span data-porch-days="" style={{
                  padding: '4px 12px', borderRadius: 7, fontSize: '0.7rem', fontWeight: 700,
                  letterSpacing: '0.04em', color: '#3A2606',
                  background: 'linear-gradient(178deg, #E8BC72, #B9843B 78%)',
                  border: '1px solid rgba(64,40,8,0.5)',
                }}>
                  {porchDaysAlive(space.porch.day_n)}
                </span>
              )}
              <div style={{ fontSize: '0.78rem', color: '#8a7454' }}>A private room. Knock first.</div>
            </div>
          ) : space.recent_messages && space.recent_messages.length > 0 ? (
            <GhostChat messages={space.recent_messages} accentColor={accentColor} playerAvatars={playerAvatars} />
          ) : (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 6,
              textAlign: 'center', padding: '12px 0',
            }}>
              <div style={{ fontSize: '2.1rem' }}>{backEmojis[0] || '👻'}</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#777' }}>All quiet in here...</div>
              <div style={{ fontSize: '0.8rem', color: '#9a9a9a' }}>Jump in and wake the locals!</div>
            </div>
          )}

          {/* Join button — full width, same as front */}
          {/* Real <a> anchor so automated explorers can enumerate the
              space's /game-step URL directly without needing to flip the
              card first. e.stopPropagation prevents the click from also
              triggering the outer space-card-outer onClick (which flips
              the card). Cycle 2: anchor-wrap. */}
          <a
            href={`/game-step?gameId=${encodeURIComponent(space.id)}`}
            data-space-name={space.name}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onJoin(); }}
            style={{
              ...styles.joinButton,
              position: 'relative',
              display: 'block',
              textAlign: 'center',
              textDecoration: 'none',
              boxSizing: 'border-box',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1976d2'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#1976d2'; }}
          >
            Drop in
            <span style={{
              position: 'absolute',
              width: 1, height: 1, padding: 0, margin: -1,
              overflow: 'hidden', clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap', border: 0,
            }}>{` ${space.name}`}</span>
          </a>
          <ShareButton
            gameId={space.id || space.name}
            gameName={space.name}
            variant="light"
            fullWidth
          />
        </div>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    // Real-iOS-Safari page-level horizontal scroll reported on iPhone mini
    // (Nina, 07-20) that headless Chrome never reproduced — clip at the page
    // edge so no child can widen the page; intentional carousels scroll
    // inside their own containers and are unaffected. Plain 'clip', never
    // 'clip visible' (computes back to visible — 07-13 lesson).
    overflowX: 'clip',
  },
  // ──────────────── Live Now Rail (coverflow carousel) ────────────────
  liveRailContainer: {
    position: 'relative',
    padding: 0,
    marginTop: '56px',
    marginBottom: '90px',
  },
  liveRailHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: '8px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  liveRailHeaderLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    flexWrap: 'wrap',
  },
  liveRailHeaderChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignSelf: 'center',
    padding: '4px',
  },
  liveRailHeaderChipDot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    backgroundColor: '#eb0400',
    animation: 'liveRecDotPulse 1.6s ease-in-out infinite',
    flexShrink: 0,
  },
  liveRailTitle: {
    color: '#1a1a1f',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    margin: 0,
  },
  liveRailSubtitle: {
    color: '#5a5a66',
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  liveRailStage: {
    position: 'relative',
    minHeight: '240px',
    // BOTH axes clip. The old `overflowX: clip` + `overflowY: visible` pair
    // serialized to `overflow: clip visible`, which Chrome computes back to
    // VISIBLE — so the track (all cards side by side, ~3700px) widened the
    // whole page into a real horizontal scrollbar at ≥1280px (screen-size
    // audit 07-13). `clip` creates no scroll container; the minHeight's
    // +60px headroom keeps hover shadows inside the clip.
    overflow: 'clip',
  },
  liveRailTrack: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    willChange: 'transform',
  },
  liveRailSlot: {
    flex: '0 0 auto',
    transformOrigin: 'center center',
  },
  liveRailCard: {
    position: 'relative',
    width: '520px',
    borderRadius: '10px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: '#1f1f23',
    display: 'flex',
    flexDirection: 'column',
  },
  liveRailArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 30,
    width: '40px',
    height: '40px',
    border: 'none',
    borderRadius: '50%',
    // ⭐ 8% white read as absent over bright card art (Nina 08-14) — the same
    // failure the swipe deck's ✕ and this page's rail counter both had. A
    // control that sits ON artwork cannot be a tint of it: this is a dark
    // scrim disc with a light glyph, which holds against both a blown-out
    // banner photo and a dark one.
    backgroundColor: 'rgba(10,10,14,0.62)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.18s ease, background-color 0.18s ease, transform 0.18s ease',
    boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
  },
  liveRailArrowLeft: {
    left: '8px',
  },
  liveRailArrowRight: {
    right: '8px',
  },
  liveRailArrowGlyph: {
    fontSize: '1.7rem',
    fontWeight: 300,
    lineHeight: 1,
    marginTop: '-3px',
  },
  liveRailDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '14px',
    padding: '4px 0',
  },
  liveRailDot: {
    // 40px touch target, 8px visual dot — padding + content-box clip keeps
    // the dot small while the tap area meets the 44px-ish mobile floor
    // (was 24px; mobile audit #5 leftover, 07-19. Visual dot unchanged.)
    width: '40px',
    height: '40px',
    boxSizing: 'border-box',
    padding: '16px',
    backgroundClip: 'content-box',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(26,26,31,0.25)',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, transform 0.2s ease',
  },
  liveRailDotActive: {
    backgroundColor: '#1a1a1f',
    transform: 'scale(1.25)',
  },
  liveRailThumbWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 6',
    backgroundColor: '#0e0e10',
    overflow: 'hidden',
  },
  liveRailThumbImg: {
    position: 'absolute',
    top: 0, left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  liveRailLiveBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: '#eb0400',
    color: '#fff',
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    padding: '2px 6px',
    borderRadius: '2px',
    textTransform: 'uppercase',
    lineHeight: 1.4,
    zIndex: 2,
  },
  liveRailViewBadge: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: '#efeff1',
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '3px 7px',
    borderRadius: '2px',
    lineHeight: 1.3,
    zIndex: 2,
  },
  liveRailGenreBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(0,0,0,0.65)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    padding: '3px 8px',
    borderRadius: '999px',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    zIndex: 5,
    transform: 'translateZ(0)',
    willChange: 'transform',
  },
  liveRailViewDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: '#eb0400',
    animation: 'liveRedDot 1.6s ease-in-out infinite',
    flexShrink: 0,
  },
  liveRailViewLabel: {
    color: '#adadb8',
    fontWeight: 400,
    marginLeft: '2px',
  },
  liveRailMeta: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px 8px 68px',
    backgroundColor: '#1f1f23',
    transition: 'background-color 0.18s ease',
    minHeight: '36px',
  },
  liveRailMetaText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  liveRailMetaTitle: {
    // Display treatment for the space name — it's the card's headline, not a
    // caption (user call 07-10: the old 0.9rem/600 read as filler text)
    color: '#f7f5ff',
    fontSize: '1.02rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textShadow: '0 1px 6px rgba(0,0,0,0.45)',
  },
  liveRailMetaHost: {
    color: '#adadb8',
    fontSize: '0.82rem',
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // ────────── End Live Now Rail ──────────
  header: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
  },
  headerTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  marketplaceLink: {
    position: 'fixed',
    top: 84, // 20 (LofiRadio top) + 48 (LofiRadio height) + 16 (gap)
    right: 24,
    zIndex: 100009,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    color: '#333',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  // Mobile-only inline AI Marketplace card — visibility controlled by the
  // .marketplace-link-mobile media query (display: none on desktop).
  marketplaceMobileIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
    flexShrink: 0,
  },
  marketplaceMobileText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  marketplaceMobileTitle: {
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'inherit',
    lineHeight: 1.2,
  },
  marketplaceMobileSubtitle: {
    fontSize: '0.7rem',
    fontWeight: 400,
    color: '#777',
    fontStyle: 'italic',
    marginTop: 2,
    lineHeight: 1.3,
  },
  buildSpacePinned: {
    position: 'fixed',
    top: 108, // 84 (marketplace top) + ~24 (NEW chip) — padding below re-centers the text
    right: 18,
    zIndex: 100009,
    // Was a bare 13px-tall text link — pad it up to a real tap target
    // (screen-size audit 07-15) without visually moving the label much
    padding: '8px 6px',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    color: '#333',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  betaInline: {
    fontSize: '0.7rem',
    fontWeight: 400,
    letterSpacing: 'normal',
    color: '#999',
    fontStyle: 'italic',
    textTransform: 'lowercase',
  },
  newTag: {
    display: 'inline-block',
    padding: '2px 7px',
    background: '#43a047',
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
    borderRadius: '4px',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: '300',
    color: '#111',
    marginBottom: '10px',
  },
  titleAi: {
    color: '#43a047',
    fontWeight: '600',
    display: 'inline-block',
    animation: 'aiWiggle 8s ease-in-out infinite',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#333',
    fontWeight: '600',
    letterSpacing: '1.5px',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  browseHeader: {
    marginBottom: '12px',
  },
  categorySection: {
    marginBottom: '48px',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '18px',
    flexWrap: 'wrap',
  },
  categoryTitle: {
    color: '#1a1a1f',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    margin: 0,
  },
  categoryShowAll: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#7b1fa2',
    fontSize: '0.85rem',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: '6px',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
    lineHeight: 1.4,
    minHeight: '28px', // touch floor (screen-size audit 07-15)
    transition: 'background-color 120ms ease, color 120ms ease, transform 120ms ease',
  },
  categoryShowAllBelowWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
  },
  categoryShowAllArrow: {
    fontSize: '0.95em',
    transform: 'translateY(0.5px)',
  },
  controls: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    marginBottom: '40px',
    flexWrap: 'wrap',
  },
  // Chip row that appears under search on focus / while typing.
  // Negative top + matching bottom margins keep the no-chips layout untouched
  // (controls already has marginBottom: 40px) while giving a natural gap when
  // chips render: search→chips ~12px, chips→grid ~28px.
  searchChipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    marginTop: -28,
    marginBottom: 28,
  },
  searchChipsLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#888',
    marginRight: 2,
  },
  searchChip: {
    // Neutral gray/black pills (Nina 08-01: back to the original neutral —
    // the purple-tinted pills read badly through the dark-mode invert).
    // Applies to Try/Trending/Vibe rows alike so they stay ONE system;
    // active/selected state stays purple.
    background: 'rgba(0,0,0,0.04)',
    color: 'inherit',
    border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: '0.82rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    lineHeight: 1.2,
    transition: 'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
  },
  searchContainer: {
    flex: '1',
    minWidth: '250px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 12px 12px 40px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  sortContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  sortLabel: {
    fontSize: '0.75rem',
    color: '#666',
  },
  sortSelect: {
    padding: '12px 30px 12px 12px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  randomButton: {
    padding: '12px 20px',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#1976d2',
    backgroundColor: '#fff',
    border: '2px solid #1976d2',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#d32f2f',
  },
  retryButton: {
    marginTop: '10px',
    padding: '8px 16px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 24px',
    maxWidth: '520px',
    margin: '0 auto',
    color: '#5a5a66',
  },
  emptyStateTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1f',
    margin: '0 0 8px 0',
  },
  emptyStateBody: {
    fontSize: '0.95rem',
    margin: '0 0 24px 0',
    lineHeight: 1.5,
  },
  emptyStateActions: {
    display: 'inline-flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  emptyStatePrimaryBtn: {
    background: '#7b1fa2',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 120ms ease',
  },
  emptyStateSecondaryBtn: {
    background: 'transparent',
    color: '#7b1fa2',
    border: '1.5px solid rgba(123,31,162,0.4)',
    borderRadius: '8px',
    padding: '8.5px 20px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 120ms ease, border-color 120ms ease',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '24px',
  },
  skeletonCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #eee',
    minHeight: '260px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    boxSizing: 'border-box',
  },
  skeletonHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '6px',
  },
  skeletonAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  skeletonTitle: {
    height: '18px',
    width: '60%',
    borderRadius: '4px',
  },
  skeletonLine: {
    height: '12px',
    borderRadius: '4px',
  },
  skeletonFooter: {
    display: 'flex',
    gap: '8px',
    marginTop: 'auto',
    paddingTop: '12px',
  },
  skeletonChip: {
    height: '22px',
    width: '70px',
    borderRadius: '11px',
  },
  cardOuter: {
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'default',
    minHeight: '260px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
  },
  cardTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  humanDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    boxShadow: '0 0 6px rgba(76, 175, 80, 0.6)',
    flexShrink: 0,
  },
  moreButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    color: '#999',
    cursor: 'pointer',
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: '700',
    letterSpacing: '1px',
    lineHeight: 1,
    // 28px touch floor (screen-size audit 07-15: 25 of these per page at 23px)
    minHeight: '28px',
    minWidth: '32px',
  },
  dialogOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99,
  },
  infoDialog: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    padding: '12px 16px',
    zIndex: 100,
    minWidth: '180px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  infoLabel: {
    fontSize: '0.85rem',
    color: '#666',
  },
  infoValue: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#333',
  },
  reportButton: {
    marginTop: '8px',
    width: '100%',
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#d32f2f',
    backgroundColor: '#fff',
    border: '1px solid #ef9a9a',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    margin: 0,
    flex: 1,
  },
  liveDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    boxShadow: '0 0 6px rgba(76, 175, 80, 0.6)',
    flexShrink: 0,
    animation: 'livePulse 2s ease-in-out infinite',
  },
  watchingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.8rem',
    color: '#e53935',
    fontWeight: '600',
    padding: '2px 8px',
    backgroundColor: '#ffebee',
    borderRadius: '12px',
  },
  viewsBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.8rem',
    color: '#666',
    padding: '2px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '12px',
  },
  cardDescription: {
    fontSize: '0.95rem',
    color: '#555',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  tagsSection: {
    marginBottom: '12px',
  },
  tagsLabel: {
    fontSize: '0.8rem',
    color: '#888',
    display: 'block',
    marginBottom: '6px',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  interestTag: {
    padding: '4px 12px',
    fontSize: '0.85rem',
    backgroundColor: '#f0f0f0',
    color: '#555',
    borderRadius: '16px',
    border: '1px solid #ddd',
  },
  roleTag: {
    padding: '4px 12px',
    fontSize: '0.85rem',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    borderRadius: '16px',
    border: '1px solid #90caf9',
  },
  gameTag: {
    padding: '2px 8px',
    fontSize: '0.7rem',
    fontWeight: '600',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '4px',
    border: '1px solid #a5d6a7',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  glitchTag: {
    padding: '2px 8px',
    fontSize: '0.7rem',
    fontWeight: '600',
    backgroundColor: '#fce4ec',
    color: '#c62828',
    borderRadius: '4px',
    border: '1px solid #ef9a9a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  joinButton: {
    marginTop: 'auto',
    padding: '12px',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#1976d2',
    backgroundColor: '#fff',
    border: '1px solid #1976d2',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  // Featured Competition Banner styles
  featuredBanner: {
    position: 'relative',
    borderRadius: '12px',
    padding: '16px 24px',
    marginBottom: '28px',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    color: '#fff',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2), 0 0 25px rgba(100,150,255,0.22), 0 0 50px rgba(100,150,255,0.12), 0 0 80px rgba(100,150,255,0.06)',
  },
  featuredEmojiBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  featuredContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  featuredLeft: {
    flex: '0 1 auto',
  },
  featuredCenter: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
  },
  featuredRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  featuredLabel: {
    fontSize: '0.65rem',
    fontWeight: '700',
    letterSpacing: '1.5px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '2px',
    textTransform: 'uppercase',
  },
  featuredTitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    flexWrap: 'wrap',
  },
  featuredTitle: {
    fontSize: '1.4rem',
    fontWeight: '800',
    margin: 0,
  },
  featuredTagline: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.75)',
    fontStyle: 'italic',
  },
  countdownRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  countdownLabel: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.95)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  countdownDigits: {
    display: 'flex',
    gap: '4px',
  },
  countdownUnit: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontVariantNumeric: 'tabular-nums',
  },
  joinCompetitionButton: {
    padding: '12px 24px',
    fontSize: '0.85rem',
    fontWeight: '700',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
    backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)',
  },
  featuredStatsInline: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  featuredStatInline: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.55)',
  },
  featuredStatInlineSep: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.3)',
  },
  featuredGamesPlayed: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.4)',
  },
  leaderboardEmpty: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.4)',
    whiteSpace: 'nowrap',
  },
  leaderboardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    marginRight: '48px',
  },
  leaderboardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  leaderboardRank: {
    fontSize: '0.85rem',
  },
  leaderboardName: {
    fontSize: '0.85rem',
    fontWeight: '600',
    maxWidth: '110px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  leaderboardScore: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: '#ffd54f',
  },
  featuredMoreButton: {
    position: 'absolute',
    top: '4px',
    right: '10px',
    zIndex: 2,
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.9)',
    padding: 0,
    fontSize: '1.2rem',
    fontWeight: '700',
    cursor: 'pointer',
    lineHeight: 1,
  },
  featuredDetails: {
    position: 'relative',
    zIndex: 1,
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.15)',
  },
  featuredDetailsCornerStats: {
    position: 'absolute',
    top: '16px',
    right: '0',
    display: 'flex',
    gap: '5px',
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.45)',
  },
  featuredDetailsGrid: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  featuredDetailsSection: {
    flex: '1 1 200px',
    minWidth: '180px',
  },
  featuredDetailsSectionTitle: {
    fontSize: '0.75rem',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  },
  featuredDetailsSectionMeta: {
    fontSize: '0.7rem',
    fontWeight: '400',
    letterSpacing: '0',
    textTransform: 'none',
    color: 'rgba(255,255,255,0.6)',
  },
  featuredDetailsText: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.95)',
    fontWeight: '600',
    lineHeight: '1.6',
    margin: '0 0 8px 0',
    whiteSpace: 'pre-line',
  },
  featuredDetailsObjective: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.8)',
    lineHeight: '1.6',
    margin: '0 0 8px 0',
  },
  featuredDetailsStats: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
  },
  featuredDetailsStat: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.75)',
    backgroundColor: 'rgba(255,255,255,0.12)',
    padding: '4px 12px',
    borderRadius: '4px',
    fontWeight: '600',
  },
  leaderboardListFull: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  leaderboardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  leaderboardHeaderCell: {
    fontSize: '0.7rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'rgba(255,255,255,0.5)',
  },
  leaderboardRowFull: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '7px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    transition: 'background-color 0.15s',
  },
  leaderboardRankFull: {
    width: '26px',
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  leaderboardNameFull: {
    flex: 1,
    fontSize: '0.9rem',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  leaderboardScoreFull: {
    fontSize: '0.9rem',
    fontWeight: '700',
    color: '#ffd54f',
  },
  leaderboardMetaFull: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.45)',
    width: '60px',
    textAlign: 'right',
  },
};

export default ExploreSpacesPage;
